import React, { useState, useEffect } from 'react';
import { companiesService, workersService, communicationsService, documentsService, storageService, emailService, auditLogService } from './supabaseService';
import { supabase } from './supabaseClient';
import { 
  Building2, Users, FileText, Settings, Plus, Search, Filter, Edit2, Trash2, 
  Eye, Send, Clock, CheckCircle, AlertCircle, X, ChevronRight, ChevronDown,
  Mail, Phone, MapPin, Calendar, Briefcase, Shield, FileCheck, Download,
  Upload, Save, Menu, Bell, LogOut, Home, MessageSquare, Target, File, Info,
  MousePointerClick, History, Lock, UserCircle, FileSignature, ClipboardCheck,
  ImageIcon, UserMinus, Archive
} from 'lucide-react';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// 🔧 COMPONENTE SEPARATO - Storico Eventi Modal (esterno per prevenire re-render)
const StoricoEventiModal = React.memo(({ 
  show,
  onClose,
  worker,
  currentUser,
  workers,
  setWorkers,
  onUpdateWorker,
  calcolaGiorniTotaliLavorati,
  contaRiassunzioni,
  isLavoratoreAttivo,
  formatDateOnly,
  formatDate,
  formatTipoEvento
}) => {
  const [formData, setFormData] = useState({
    data: '',
    tipo: 'NOTA',
    mansione: '',
    ruoli: [],
    note: ''
  });
  
  // 🆕 State per editing evento
  const [editingEvento, setEditingEvento] = useState(null);

  if (!show || !worker) return null;

  const eventi = worker.storicoEventi || [];
  const giorniTotali = calcolaGiorniTotaliLavorati(worker);
  const numeroRiassunzioni = contaRiassunzioni(worker);

  // 🆕 Funzione per iniziare modifica evento
  const handleEditEvento = (evento) => {
    setEditingEvento(evento);
    setFormData({
      data: evento.data,
      tipo: evento.tipo,
      mansione: evento.mansione || '',
      ruoli: evento.ruoli || [],
      note: evento.note
    });
    // Scroll to form
    setTimeout(() => {
      document.querySelector('.bg-gray-50.p-4')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  // 🆕 Funzione per annullare modifica
  const handleCancelEdit = () => {
    setEditingEvento(null);
    setFormData({
      data: '',
      tipo: 'NOTA',
      mansione: '',
      ruoli: [],
      note: ''
    });
  };

  // 🆕 Funzione per eliminare evento
  const handleDeleteEvento = (eventoId) => {
    if (!window.confirm('⚠️ Sei sicuro di voler eliminare questo evento?\n\nQuesta azione NON può essere annullata.')) {
      return;
    }

    const updatedEventi = eventi.filter(e => e.id !== eventoId);
    const updatedWorker = {
      ...worker,
      storicoEventi: updatedEventi
    };

    setWorkers(workers.map(w => w.id === worker.id ? updatedWorker : w));
    onUpdateWorker(updatedWorker);

    alert('✅ Evento eliminato dallo storico!');
  };

  // 🆕 Funzione per salvare (ADD o UPDATE)
  const handleSaveEvento = () => {
    if (!formData.data || !formData.note) {
      alert('Compila data e note evento!');
      return;
    }

    if (editingEvento) {
      // UPDATE evento esistente
      const updatedEventi = eventi.map(e => 
        e.id === editingEvento.id 
          ? {
              ...e,
              data: formData.data,
              tipo: formData.tipo,
              mansione: formData.mansione || worker.mansione,
              ruoli: formData.ruoli.length > 0 ? formData.ruoli : worker.ruoli,
              note: formData.note,
              modificatoDa: `${currentUser.nome} ${currentUser.cognome}`,
              dataModifica: new Date().toISOString()
            }
          : e
      );

      const updatedWorker = {
        ...worker,
        storicoEventi: updatedEventi
      };

      setWorkers(workers.map(w => w.id === worker.id ? updatedWorker : w));
      onUpdateWorker(updatedWorker);

      setEditingEvento(null);
      setFormData({
        data: '',
        tipo: 'NOTA',
        mansione: '',
        ruoli: [],
        note: ''
      });

      alert('✅ Evento modificato con successo!');
    } else {
      // ADD nuovo evento
      const evento = {
        id: eventi.length > 0 ? Math.max(...eventi.map(e => e.id)) + 1 : 1,
        data: formData.data,
        tipo: formData.tipo,
        mansione: formData.mansione || worker.mansione,
        ruoli: formData.ruoli.length > 0 ? formData.ruoli : worker.ruoli,
        note: formData.note,
        inseritoDa: `${currentUser.nome} ${currentUser.cognome}`,
        dataInserimento: new Date().toISOString()
      };

      const updatedWorker = {
        ...worker,
        storicoEventi: [...eventi, evento]
      };

      setWorkers(workers.map(w => w.id === worker.id ? updatedWorker : w));
      onUpdateWorker(updatedWorker);

      setFormData({
        data: '',
        tipo: 'NOTA',
        mansione: '',
        ruoli: [],
        note: ''
      });

      alert('✅ Evento aggiunto allo storico!');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="bg-purple-600 text-white p-6 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Storico Eventi</h2>
              <p className="text-sm text-purple-100 mt-1">
                {worker.nome} {worker.cognome} • CF: {worker.codiceFiscale}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-purple-700 rounded-lg">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Giorni Lavorati</span>
              </div>
              <p className="text-3xl font-bold text-blue-600">{giorniTotali}</p>
              <p className="text-xs text-blue-700 mt-1">
                {(giorniTotali / 365).toFixed(1)} anni circa
              </p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-xl border-2 border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-900">Stato Attuale</span>
              </div>
              <p className="text-lg font-bold text-green-600">
                {isLavoratoreAttivo(worker) ? '🟢 ATTIVO' : '🔴 DIMESSO'}
              </p>
              <p className="text-xs text-green-700 mt-1">
                dal {formatDateOnly(worker.dataAssunzione || worker.dataFine)}
              </p>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-xl border-2 border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-orange-900">Riassunzioni</span>
              </div>
              <p className="text-3xl font-bold text-orange-600">{numeroRiassunzioni}</p>
              <p className="text-xs text-orange-700 mt-1">
                {numeroRiassunzioni === 0 ? 'Mai riassunto' : 'Rapporti precedenti'}
              </p>
            </div>
          </div>

          <div className={`p-4 rounded-xl border-2 ${editingEvento ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}`}>
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              {editingEvento ? (
                <>
                  <Edit2 className="w-5 h-5 text-blue-600" />
                  <span>Modifica Evento #{editingEvento.id}</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>Aggiungi Nuovo Evento</span>
                </>
              )}
            </h3>
            
            {editingEvento && (
              <div className="mb-3 p-2 bg-blue-100 border border-blue-300 rounded-lg text-sm text-blue-800">
                <strong>ℹ️ Modalità Modifica:</strong> Stai modificando l'evento del {formatDateOnly(editingEvento.data)}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data Evento *</label>
                <input
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData(prev => ({...prev, data: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo Evento *</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData(prev => ({...prev, tipo: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="NOTA">📝 Nota</option>
                  <option value="ASSUNZIONE">🟢 Assunzione</option>
                  <option value="RIASSUNZIONE">🔵 Riassunzione</option>
                  <option value="DISMISSIONE">🔴 Cessazione</option>
                  <option value="CAMBIO_MANSIONE">📋 Cambio Mansione</option>
                  <option value="CAMBIO_RUOLO">🛡️ Cambio Ruolo</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Note *</label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData(prev => ({...prev, note: e.target.value}))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Descrivi l'evento (es. Fine contratto determinato, Promozione, etc.)"
                  autoComplete="off"
                />
              </div>
              
              <div className="md:col-span-2 flex justify-end gap-2">
                {editingEvento && (
                  <button
                    onClick={handleCancelEdit}
                    className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                  >
                    Annulla
                  </button>
                )}
                <button
                  onClick={handleSaveEvento}
                  className={`px-6 py-2 ${editingEvento ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'} text-white rounded-lg font-medium transition-colors flex items-center gap-2`}
                >
                  {editingEvento ? (
                    <>
                      <Save className="w-4 h-4" />
                      Salva Modifiche
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Aggiungi Evento
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Timeline Storico ({eventi.length} eventi)
            </h3>
            
            {eventi.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">Nessun evento storico registrato</p>
                <p className="text-sm text-gray-500">Utilizza il form sopra per aggiungere il primo evento</p>
              </div>
            ) : (
              <div className="space-y-4">
                {[...eventi].sort((a, b) => new Date(b.data) - new Date(a.data)).map((evento) => (
                  <div key={evento.id} className={`relative pl-8 pb-4 ${
                    evento.tipo === 'DISMISSIONE' ? 'border-l-4 border-red-300' :
                    evento.tipo === 'ASSUNZIONE' ? 'border-l-4 border-green-300' :
                    evento.tipo === 'RIASSUNZIONE' ? 'border-l-4 border-blue-300' :
                    'border-l-4 border-gray-300'
                  }`}>
                    <div className={`absolute left-0 top-0 w-4 h-4 rounded-full -ml-[9px] ${
                      evento.tipo === 'DISMISSIONE' ? 'bg-red-500' :
                      evento.tipo === 'ASSUNZIONE' ? 'bg-green-500' :
                      evento.tipo === 'RIASSUNZIONE' ? 'bg-blue-500' :
                      evento.tipo === 'CAMBIO_MANSIONE' ? 'bg-purple-500' :
                      evento.tipo === 'CAMBIO_RUOLO' ? 'bg-yellow-500' :
                      'bg-gray-500'
                    }`} />
                    
                    <div className="bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-purple-300 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">{formatTipoEvento(evento.tipo)}</p>
                          <p className="text-sm text-gray-600">{formatDateOnly(evento.data)}</p>
                        </div>
                        
                        <div className="flex items-start gap-1">
                          {(evento.tipo === 'CAMBIO_MANSIONE' || evento.tipo === 'CAMBIO_RUOLO') && (
                            <div className="text-right mr-2">
                              <p className="text-xs text-gray-500">Mansione</p>
                              <p className="text-sm font-semibold text-blue-600">{evento.mansione}</p>
                            </div>
                          )}
                          
                          {/* 🆕 Pulsanti Azioni */}
                          <button
                            onClick={() => handleEditEvento(evento)}
                            className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
                            title="Modifica evento"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEvento(evento.id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-red-600 transition-colors"
                            title="Elimina evento"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-2">{evento.note}</p>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                        <span>Inserito da: <strong>{evento.inseritoDa}</strong></span>
                        <span>•</span>
                        <span>{formatDate(evento.dataInserimento)}</span>
                        {evento.modificatoDa && (
                          <>
                            <span>•</span>
                            <span className="text-blue-600">
                              Modificato da: <strong>{evento.modificatoDa}</strong> il {formatDate(evento.dataModifica)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-600 text-white rounded-xl font-medium hover:bg-gray-700 transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
});

const SafeTrackerAdmin = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState({ action: '', entityType: '', search: '', companyId: '' });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySubMenu, setCompanySubMenu] = useState('anagrafica');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCompany, setSearchCompany] = useState(''); // 🆕 Ricerca aziende
  const [filterTipoContratto, setFilterTipoContratto] = useState('all'); // 🆕 Filtro tipo contratto
  const [filterStatoContratto, setFilterStatoContratto] = useState('all'); // 🆕 Filtro stato contratto
  const [showDisabledCompanies, setShowDisabledCompanies] = useState(false); // 🆕 Mostra aziende disattivate
  const [filterDocumentsExpired, setFilterDocumentsExpired] = useState('all'); // all, expiring, expired
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedCommunication, setSelectedCommunication] = useState(null);
  const [communicationTab, setCommunicationTab] = useState('recipients');
  const [selectedDocument, setSelectedDocument] = useState(null); // 🆕 Per dettaglio documento
  const [documentTab, setDocumentTab] = useState('recipients'); // 🆕 Tab documento
  const [editingWorker, setEditingWorker] = useState(null);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterExpire, setFilterExpire] = useState('all'); // 🆕 Filtro scadenze comunicazioni: all | expiring | expired | noexpiry
  const [selectedRoleForList, setSelectedRoleForList] = useState(null);
  const [selectedMansioneForList, setSelectedMansioneForList] = useState(null);
  const [selectedSpecialRole, setSelectedSpecialRole] = useState(null); // 🆕 Per Datore/RSPP/Medico
  const [selectedCompanyForDocuments, setSelectedCompanyForDocuments] = useState('all'); // 🆕 Per documenti ADMIN - DEFAULT: Tutte le aziende
  const [newMansione, setNewMansione] = useState('');
  const [newRuolo, setNewRuolo] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingCommunication, setEditingCommunication] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);

  // 🆕 SISTEMA REVISIONI DOCUMENTI
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionDocument, setRevisionDocument] = useState(null);
  const [newRevision, setNewRevision] = useState({
    descrizione: '',
    dataRevisione: '',
    dataScadenza: '',
    file: null,
    autoReshare: true
  });
  // 🆕 FILE OBJECTS per upload reale su Supabase Storage
  const [documentFileObject, setDocumentFileObject] = useState(null);
  const [revisionFileObject, setRevisionFileObject] = useState(null);

  const [showArchivedDocuments, setShowArchivedDocuments] = useState(false);
  const [selectedDocumentForHistory, setSelectedDocumentForHistory] = useState(null);

  // 🆕 SISTEMA STORICO EVENTI LAVORATORI
  const [showStoricoEventoModal, setShowStoricoEventoModal] = useState(false);
  const [selectedWorkerForStorico, setSelectedWorkerForStorico] = useState(null);

  // 🆕 SISTEMA PROFILI UTENTE
  const [currentUser, setCurrentUser] = useState({
    role: 'ADMIN', // ADMIN | AZIENDA | LAVORATORE
    companyId: null,
    workerId: null,
    nome: 'Marco',
    cognome: 'Rossi',
    email: 'admin@consulenza.it'
  });

  // 🔐 SUPABASE AUTH
  const [authUser, setAuthUser] = useState(null); // Supabase auth session user
  const [authLoading, setAuthLoading] = useState(true); // Loading iniziale auth check
  const [isRealAdmin, setIsRealAdmin] = useState(false); // true se l'utente loggato è ADMIN (anche se simula altri profili)
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false); // Loading durante login

  // 🔐 CONFERMA PASSWORD PER ELIMINAZIONE AZIENDA
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteConfirmPassword, setDeleteConfirmPassword] = useState('');
  const [deleteConfirmError, setDeleteConfirmError] = useState('');
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [pendingDeleteCompanyId, setPendingDeleteCompanyId] = useState(null);

  // 🆕 ORDINAMENTO LISTA LAVORATORI
  const [workerSortOrder, setWorkerSortOrder] = useState('asc'); // asc | desc
  
  // 🆕 RICERCA LAVORATORE IN CAMBIA PROFILO
  const [profileWorkerSearch, setProfileWorkerSearch] = useState('');

  // 🆕 LOGO CONSULENTE (ADMIN)
  const [consultantLogo, setConsultantLogo] = useState(null);
  const [consultantName, setConsultantName] = useState('Studio Sicurezza & Consulenza');

  // 🆕 SISTEMA EMAIL
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pendingEmailData, setPendingEmailData] = useState(null);

  // 🆕 MODAL CONFERMA DOCUMENTO
  const [showDocumentConfirmModal, setShowDocumentConfirmModal] = useState(false);
  const [pendingDocumentData, setPendingDocumentData] = useState(null);

  // 🆕 IMPORT EXCEL LAVORATORI
  const [showImportExcelModal, setShowImportExcelModal] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [excelPreview, setExcelPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [showImportPreview, setShowImportPreview] = useState(false);

  // 🆕 FILTRO DATE DASHBOARD
  const [dateFilter, setDateFilter] = useState('3months'); // 3months | 6months | 1year | custom
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  // 🆕 FILTRO STATO LAVORATORI
  const [workerStatusFilter, setWorkerStatusFilter] = useState('all'); // all | attivi | dimessi

  // 🆕 PROFILO LAVORATORE - FILTRI E STATI
  const [workerCommFilter, setWorkerCommFilter] = useState('all'); // all | unread | read
  const [workerCommDateFilter, setWorkerCommDateFilter] = useState('all'); // all | 3months | 6months | 1year | custom
  const [workerCommExpireFilter, setWorkerCommExpireFilter] = useState('all'); // all | expiring | expired | noexpiry
  const [workerCommTypeFilter, setWorkerCommTypeFilter] = useState('all'); // all | Messaggio | Azione | Documento
  const [workerCommDateFrom, setWorkerCommDateFrom] = useState('');
  const [workerCommDateTo, setWorkerCommDateTo] = useState('');
  const [workerDocFilter, setWorkerDocFilter] = useState('all'); // all | unread | read | approved | notapproved
  const [workerDocTypeFilter, setWorkerDocTypeFilter] = useState('all'); // 🆕 Filtro per tipo documento
  const [workerDocSearch, setWorkerDocSearch] = useState(''); // 🆕 Ricerca documento per nome
  const [selectedWorkerComm, setSelectedWorkerComm] = useState(null); // Comunicazione selezionata
  const [selectedWorkerDoc, setSelectedWorkerDoc] = useState(null); // Documento selezionato
  const [newCompany, setNewCompany] = useState({
    ragioneSociale: '',
    indirizzo: '',
    citta: '',
    pec: '',
    partitaIva: '',
    logo: null,
    // 🆕 Gestione Contratto
    tipoContratto: 'gestione_completa', // 'gestione_completa' | 'solo_piattaforma'
    attiva: true,
    dataInizioContratto: '',
    dataScadenzaContratto: '',
    importoContratto: '',
    noteContratto: '',
    datoreLavoro: { 
      nome: '', 
      cognome: '', 
      email: '',
      cellulare: '',
      cumulaRuoli: false,
      ruoliCumulati: []
    },
    medicoCompetente: { nome: '', cognome: '', email: '', pec: '', cellulare: '' },
    rspp: { nome: '', cognome: '', email: '', pec: '', cellulare: '' },
    codiciAteco: [{ codice: '', descrizione: '' }]
  });

  const [newWorker, setNewWorker] = useState({
    nome: '',
    cognome: '',
    luogoNascita: '',
    dataNascita: '',
    codiceFiscale: '',
    email: '',
    cellulare: '',
    mansione: '',
    ruoli: [],
    dataAssunzione: '',
    dataFine: ''
  });

  const [newCommunication, setNewCommunication] = useState({
    titolo: '',
    contenuto: '',
    tipologia: 'Messaggio',
    dataScadenza: '',
    stato: 'Bozza',
    aziendaId: null,
    destinatari: {
      mansioni: [],
      ruoli: [],
      ruoliOrganizzativi: [],
      tuttiLavoratori: false,
      personeSpecifiche: []
    },
    allegati: []
  });

  const [companies, setCompanies] = useState([]); 

  const [workers, setWorkers] = useState([]);

  const [communications, setCommunications] = useState([]);

  const [mansioniTemplate, setMansioniTemplate] = useState([
    'Operaio Produzione',
    'Addetto Magazzino',
    'Addetto Amministrativo',
    'Responsabile Magazzino',
    'Tecnico Manutenzione',
    'Operaio Edile',
    'Sviluppatore Software'
  ]);

  const [ruoliTemplate, setRuoliTemplate] = useState([
    'Preposto',
    'Addetto Antincendio',
    'Addetto Primo Soccorso',
    'RLS - Rappresentante Lavoratori Sicurezza'
  ]);

  // 🆕 RUOLI ORGANIZZATIVI (Figure Aziendali)
  const ruoliOrganizzativiTemplate = [
    'Datore di Lavoro',
    'RSPP - Responsabile Servizio Prevenzione e Protezione',
    'Medico Competente'
  ];

  // 🆕 DOCUMENTI AZIENDALI
  const [documents, setDocuments] = useState([]);

  // 🆕 TEMPLATE DOCUMENTI AZIENDALI (per profilo AZIENDA)
  const [documentiTemplate, setDocumentiTemplate] = useState([
    'DVR - Documento Valutazione Rischi',
    'DUVRI - Documento Unico Valutazione Rischi Interferenti',
    'POS - Piano Operativo di Sicurezza',
    'PSC - Piano di Sicurezza e Coordinamento'
  ]);

  const [newDocumento, setNewDocumento] = useState('');
  const [newDocument, setNewDocument] = useState({
    tipo: 'DVR',
    descrizione: '',
    dataRevisione: '',
    dataScadenza: '',
    file: null,
    stato: 'Privato',
    destinatari: {
      mansioni: [],
      ruoli: [], // Ruoli lavoratori
      ruoliOrganizzativi: [], // 🆕 Figure aziendali
      tuttiLavoratori: false,
      personeSpecifiche: []
    }
  });

  // Utility functions
  // ============================================================
  // CARICAMENTO DATI DA DATABASE SUPABASE
  // ============================================================
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        
        console.log('📡 Caricamento dati da Supabase...');
        
        const [dbCompanies, dbWorkers, dbCommunications, dbDocuments] = await Promise.all([
          companiesService.getAll(),
          workersService.getAll(),
          communicationsService.getAll(),
          documentsService.getAll(),
        ]);
        
        setCompanies(dbCompanies);
        setWorkers(dbWorkers);
        setCommunications(dbCommunications);
        setDocuments(dbDocuments);
        
        console.log('✅ Dati caricati!', {
          aziende: dbCompanies.length,
          lavoratori: dbWorkers.length,
          comunicazioni: dbCommunications.length,
          documenti: dbDocuments.length,
        });
        
      } catch (err) {
        console.error('❌ Errore caricamento dati:', err);
        setLoadError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  
  // ============================================================
  // FUNZIONE RICARICA DATI DA DATABASE
  // ============================================================
  const reloadData = async () => {
    try {
      const [dbCompanies, dbWorkers, dbCommunications, dbDocuments] = await Promise.all([
        companiesService.getAll(),
        workersService.getAll(),
        communicationsService.getAll(),
        documentsService.getAll(),
      ]);
      setCompanies(dbCompanies);
      setWorkers(dbWorkers);
      setCommunications(dbCommunications);
      setDocuments(dbDocuments);
      console.log('🔄 Dati ricaricati dal database');
    } catch (err) {
      console.error('Errore ricaricamento:', err);
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 🔐 FASE 6.1 - HASH SHA-256 PER TRACCIABILITÀ LEGALE
  // Genera un hash SHA-256 del contenuto per certificare l'integrità
  const generateSHA256Hash = async (content) => {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(content);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      return hashHex;
    } catch (err) {
      console.error('Errore generazione hash SHA-256:', err);
      return null;
    }
  };

  // 🔐 Genera stringa di contenuto per hash comunicazione
  const buildCommunicationHashContent = (comm) => {
    return JSON.stringify({
      titolo: comm.titolo,
      contenuto: comm.contenuto,
      tipologia: comm.tipologia,
      aziendaId: comm.aziendaId,
      dataInvio: comm.dataInvio,
      recipients: (comm.recipients || []).map(r => r.id).sort()
    });
  };

  // 🔐 Genera stringa di contenuto per hash documento
  const buildDocumentHashContent = (doc) => {
    return JSON.stringify({
      tipo: doc.tipo,
      descrizione: doc.descrizione,
      dataRevisione: doc.dataRevisione,
      companyId: doc.companyId,
      file: doc.file,
      stato: doc.stato,
      dataCreazione: doc.dataCreazione,
      recipients: (doc.recipients || []).map(r => r.id).sort()
    });
  };


  const getCompanyName = (id) => {
    const company = companies.find(c => c.id === id);
    return company ? company.ragioneSociale : 'N/A';
  };

  const getActiveCommunications = () => {
    // 🆕 Escludi aziende "Solo Piattaforma"
    const aziendeGestite = companies.filter(c => c.tipoContratto === 'gestione_completa').map(c => String(c.id));
    let comms = communications.filter(c => c.stato === 'Pubblicato' && aziendeGestite.includes(String(c.aziendaId)));
    if (currentUser.role === 'AZIENDA' && currentUser.companyId) {
      comms = comms.filter(c => String(c.aziendaId) === String(currentUser.companyId));
    }
    return comms.length;
  };

  const getAverageCompliance = () => {
    // 🆕 Escludi aziende "Solo Piattaforma"
    const aziendeGestite = companies.filter(c => c.tipoContratto === 'gestione_completa').map(c => c.id);
    let published = communications.filter(c => c.stato === 'Pubblicato' && aziendeGestite.includes(String(c.aziendaId)));
    if (currentUser.role === 'AZIENDA' && currentUser.companyId) {
      published = published.filter(c => String(c.aziendaId) === String(currentUser.companyId));
    }
    if (published.length === 0) return 0;
    
    const total = published.reduce((sum, comm) => {
      const stats = calculateCommunicationStats(comm);
      if (stats.destinatari === 0) return sum;
      return sum + (stats.letti / stats.destinatari * 100);
    }, 0);
    
    return Math.round(total / published.length);
  };

  const calculateCommunicationStats = (comm) => {
    const totalRecipients = comm.recipients ? comm.recipients.length : 0;
    const letti = comm.recipients ? comm.recipients.filter(r => r.letto).length : 0;
    const nonLetti = totalRecipients - letti;
    
    return {
      destinatari: totalRecipients,
      letti: letti,
      nonLetti: nonLetti
    };
  };

  // 🆕 UTILITY FUNCTIONS SISTEMA PROFILI
  const handleRoleChange = (newRole) => {
    // Se cambia a LAVORATORE, imposta anche companyId dal primo lavoratore
    let newWorkerId = null;
    let newCompanyId = null;
    let newNome = currentUser.nome;
    let newCognome = currentUser.cognome;
    let newEmail = currentUser.email;
    
    if (newRole === 'LAVORATORE') {
      const firstWorker = workers[0];
      if (firstWorker) {
        newWorkerId = firstWorker.id;
        newCompanyId = firstWorker.companyId;
        newNome = firstWorker.nome;
        newCognome = firstWorker.cognome;
        newEmail = firstWorker.email;
      }
    } else if (newRole === 'AZIENDA') {
      newCompanyId = 1;
      const company = companies.find(c => c.id === 1);
      if (company) {
        newNome = company.datoreLavoro.nome;
        newCognome = company.datoreLavoro.cognome;
        newEmail = company.datoreLavoro.email;
      }
    }
    
    setCurrentUser({
      ...currentUser,
      role: newRole,
      companyId: newCompanyId,
      workerId: newWorkerId,
      nome: newNome,
      cognome: newCognome,
      email: newEmail
    });
    setActiveSection('dashboard');
    setSelectedCompany(null);
    
    // Reset filtri LAVORATORE
    setWorkerCommFilter('all');
    setWorkerCommDateFilter('all');
    setWorkerCommExpireFilter('all');
    setWorkerCommTypeFilter('all');
    setWorkerDocFilter('all');
    setWorkerDocTypeFilter('all');
    setWorkerDocSearch('');
    setSelectedWorkerComm(null);
    setSelectedWorkerDoc(null);
  };

  const handleCompanyChange = (companyId) => {
    const numericId = isNaN(companyId) ? companyId : Number(companyId);
    const company = companies.find(c => c.id == companyId);
    setCurrentUser({
      ...currentUser,
      companyId: numericId,
      nome: company?.datoreLavoro.nome || '',
      cognome: company?.datoreLavoro.cognome || '',
      email: company?.datoreLavoro.email || ''
    });
  };

  const handleWorkerChange = (workerId) => {
    const numericId = isNaN(workerId) ? workerId : Number(workerId);
    const worker = workers.find(w => w.id == workerId);
    setCurrentUser({
      ...currentUser,
      workerId: numericId,
      companyId: worker?.companyId || null,
      nome: worker?.nome || '',
      cognome: worker?.cognome || '',
      email: worker?.email || ''
    });
  };

  // 🆕 UTILITY FUNCTIONS SISTEMA EMAIL
  const prepareEmailData = (recipients, communication) => {
    const company = companies.find(c => c.id === communication.aziendaId);
    const emailList = recipients.map(r => {
      // Per figure organizzative, email è già presente in r.email
      if (r.tipo === 'organizzativo' && r.email) {
        return {
          workerId: r.id,
          name: r.nome,
          email: r.email
        };
      }
      // Per workers, cerca l'email nel database workers
      const worker = workers.find(w => w.id === r.id);
      return {
        workerId: r.id,
        name: r.nome,
        email: worker ? worker.email : 'N/A'
      };
    }).filter(e => e.email !== 'N/A');

    return {
      communication,
      company,
      recipients: emailList,
      timestamp: new Date().toISOString()
    };
  };

  const confirmEmailSend = async () => {
    if (!pendingEmailData) return;

    const senderName = `${currentUser.nome} ${currentUser.cognome}`;
    
    try {
      // Prepara email per ogni destinatario
      const emails = pendingEmailData.recipients.map(recipient => {
        let template;
        
        if (pendingEmailData.communication) {
          template = emailService.buildCommunicationEmail({
            workerName: recipient.name,
            communicationTitle: pendingEmailData.communication.titolo,
            communicationContent: pendingEmailData.communication.contenuto,
            companyName: pendingEmailData.company.ragioneSociale,
            senderName
          });
        } else if (pendingEmailData.document) {
          template = emailService.buildDocumentEmail({
            workerName: recipient.name,
            documentDescription: pendingEmailData.document.descrizione,
            documentType: pendingEmailData.document.tipo,
            companyName: pendingEmailData.company.ragioneSociale,
            senderName
          });
        }

        return {
          to: recipient.email,
          ...template
        };
      });

      // Invio batch
      const results = await emailService.sendBatch(emails);
      
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);

      // Log per tracciabilità
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📧 EMAIL INVIATE CON RESEND');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`Successo: ${successful}/${results.length}`);
      if (failed.length > 0) {
        console.log('Fallite:', failed.map(f => `${f.to}: ${f.error}`));
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      if (failed.length > 0) {
        alert(`⚠️ Email inviate: ${successful}/${results.length}\n\nFallite:\n${failed.map(f => `• ${f.to}: ${f.error}`).join('\n')}`);
      }

    } catch (err) {
      console.error('Errore invio email:', err);
      alert('Errore nell\'invio delle email: ' + err.message);
    }

    // Chiudi modal
    setShowEmailModal(false);
    setPendingEmailData(null);
  };

  const cancelEmailSend = () => {
    setShowEmailModal(false);
    setPendingEmailData(null);
  };

  // 🆕 CANCEL DOCUMENT CONFIRM
  const cancelDocumentConfirm = () => {
    setShowDocumentConfirmModal(false);
    setPendingDocumentData(null);
  };

  // 🆕 UTILITY FILTRO DATE DASHBOARD
  const getFilteredCommunicationsByDate = () => {
    const now = new Date();
    // 🔧 FIX: Filtra per azienda se profilo AZIENDA
    let comms = communications.filter(c => c.stato === 'Pubblicato');
    
    if (currentUser.role === 'AZIENDA' && currentUser.companyId) {
      comms = comms.filter(c => String(c.aziendaId) === String(currentUser.companyId));
    }
    
    if (dateFilter === 'custom') {
      if (!customDateFrom || !customDateTo) return comms;
      const from = new Date(customDateFrom);
      const to = new Date(customDateTo);
      return comms.filter(c => {
        const commDate = new Date(c.dataInvio);
        return commDate >= from && commDate <= to;
      });
    }
    
    let monthsAgo;
    if (dateFilter === '3months') monthsAgo = 3;
    else if (dateFilter === '6months') monthsAgo = 6;
    else if (dateFilter === '1year') monthsAgo = 12;
    else return comms;
    
    const filterDate = new Date(now.setMonth(now.getMonth() - monthsAgo));
    return comms.filter(c => new Date(c.dataInvio) >= filterDate);
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    });
  };

  // 🔐 FASE 6.3: Formattazione timestamp UTC certificato
  // Mostra data/ora in formato italiano CON indicatore UTC per tracciabilità legale
  const formatDateUTC = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZone: 'UTC'
    }) + ' (UTC)';
  };

  // 🆕 UTILITY LAVORATORI ATTIVI/DIMESSI
  const isLavoratoreAttivo = (worker) => {
    if (!worker.dataFine) return true;
    const oggi = new Date().toISOString().split('T')[0];
    return worker.dataFine > oggi;
  };

  const getLavoratoriAttivi = (companyId) => {
    return workers.filter(w => 
      String(w.companyId) === String(companyId) && 
      isLavoratoreAttivo(w) &&
      !isDatoreDiLavoro(w, companyId) // 🔧 ESCLUDI DATORE dal conteggio
    );
  };

  const getLavoratoriDimessi = (companyId) => {
    return workers.filter(w => 
      String(w.companyId) === String(companyId) && 
      !isLavoratoreAttivo(w) &&
      !isDatoreDiLavoro(w, companyId) // 🔧 ESCLUDI DATORE dal conteggio
    );
  };

  // 🆕 GESTIONE CONTRATTI AZIENDE
  
  // Calcola lo stato del contratto basato sulla scadenza
  const getStatoContratto = (company) => {
    if (!company.attiva) return 'disattivata';
    if (!company.dataScadenzaContratto) return 'attivo';
    
    const oggi = new Date();
    const scadenza = new Date(company.dataScadenzaContratto);
    const giorniAllaScadenza = Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24));
    
    if (giorniAllaScadenza < 0) return 'scaduto';
    if (giorniAllaScadenza <= 30) return 'in_scadenza'; // 🟠
    if (giorniAllaScadenza <= 60) return 'attenzione'; // 🟡
    return 'attivo'; // 🟢
  };

  // Calcola i giorni alla scadenza
  const getGiorniAllaScadenza = (dataScadenza) => {
    if (!dataScadenza) return null;
    const oggi = new Date();
    const scadenza = new Date(dataScadenza);
    return Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24));
  };

  // Badge per stato contratto
  const getStatoContrattoBadge = (company) => {
    const stato = getStatoContratto(company);
    const badges = {
      'disattivata': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'DISATTIVATA' },
      'scaduto': { bg: 'bg-red-100', text: 'text-red-700', label: 'SCADUTO' },
      'in_scadenza': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'IN SCADENZA' },
      'attenzione': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'ATTENZIONE' },
      'attivo': { bg: 'bg-green-100', text: 'text-green-700', label: 'ATTIVO' }
    };
    return badges[stato] || badges['attivo'];
  };

  // Badge per tipo contratto
  const getTipoContrattoBadge = (tipoContratto) => {
    if (tipoContratto === 'solo_piattaforma') {
      return { bg: 'bg-purple-100', text: 'text-purple-700', label: 'SOLO PIATTAFORMA' };
    }
    return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'GESTIONE COMPLETA' };
  };

  // Ottieni aziende con contratti in scadenza (per dashboard)
  const getAziendeInScadenza = () => {
    return companies
      .filter(c => {
        const stato = getStatoContratto(c);
        return stato === 'scaduto' || stato === 'in_scadenza' || stato === 'attenzione';
      })
      .sort((a, b) => new Date(a.dataScadenzaContratto) - new Date(b.dataScadenzaContratto));
  };

  // Ottieni solo aziende gestite (per comunicazioni/documenti)
  const getAziendeGestite = () => {
    return companies.filter(c => c.tipoContratto === 'gestione_completa' && c.attiva);
  };

  // Verifica se azienda è accessibile (per profilo AZIENDA/LAVORATORE)
  const isAziendaAccessibile = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (!company) return false;
    if (!company.attiva) return false;
    
    // Verifica scadenza - se scaduto, disattiva automaticamente
    const stato = getStatoContratto(company);
    if (stato === 'scaduto') {
      // Disattiva automaticamente
      setCompanies(prev => prev.map(c => 
        c.id === companyId ? { ...c, attiva: false } : c
      ));
      return false;
    }
    return true;
  };

  // Invia email alert scadenza contratto
  const sendContractExpiryEmail = async (company, tipo) => {
    const datoreLavoro = company.datoreLavoro;
    const giorni = getGiorniAllaScadenza(company.dataScadenzaContratto);
    
    try {
      const template = emailService.buildContractExpiryEmail({
        datoreName: `${datoreLavoro.nome} ${datoreLavoro.cognome}`,
        companyName: company.ragioneSociale,
        expiryDate: formatDateOnly(company.dataScadenzaContratto),
        daysLeft: giorni,
        urgencyLevel: tipo
      });

      await emailService.send({
        to: datoreLavoro.email,
        ...template
      });

      console.log(`📧 Email scadenza contratto inviata a ${datoreLavoro.email} (${tipo})`);
      return { to: datoreLavoro.email, subject: template.subject, success: true };
    } catch (err) {
      console.error('Errore invio email scadenza:', err);
      return { to: datoreLavoro.email, subject: '', success: false, error: err.message };
    }
  };

  // 🆕 UTILITY PROFILO LAVORATORE
  
  // Ottiene comunicazioni destinate al lavoratore corrente
  const getWorkerCommunications = () => {
    if (!currentUser.workerId) return [];
    
    return communications.filter(comm => {
      // Solo comunicazioni pubblicate
      if (comm.stato !== 'Pubblicato') return false;
      // Solo comunicazioni della stessa azienda
      if (String(comm.aziendaId) !== String(currentUser.companyId)) return false;
      // Solo se il lavoratore è tra i recipients
      return comm.recipients?.some(r => r.id === currentUser.workerId);
    });
  };
  
  // Ottiene documenti condivisi con il lavoratore corrente
  const getWorkerDocuments = () => {
    if (!currentUser.workerId) return [];
    
    return documents.filter(doc => {
      // Solo documenti della stessa azienda
      if (String(doc.companyId) !== String(currentUser.companyId)) return false;
      // Solo documenti condivisi (stato === 'Condiviso')
      if (doc.stato !== 'Condiviso') return false;
      // Escludi documenti archiviati (versioni obsolete)
      if (doc.isArchived) return false;
      // Solo se il lavoratore è tra i recipients
      return doc.recipients?.some(r => r.id === currentUser.workerId);
    });
  };
  
  // Filtra comunicazioni per stato lettura
  const filterWorkerComms = (comms, filter, dateFilter, dateFrom, dateTo, expireFilter = 'all', typeFilter = 'all') => {
    let filtered = [...comms];
    
    // Filtro stato lettura
    if (filter === 'unread') {
      filtered = filtered.filter(c => {
        const recipient = c.recipients?.find(r => r.id === currentUser.workerId);
        return recipient && !recipient.letto;
      });
    } else if (filter === 'read') {
      filtered = filtered.filter(c => {
        const recipient = c.recipients?.find(r => r.id === currentUser.workerId);
        return recipient && recipient.letto;
      });
    }
    
    // Filtro tipologia
    if (typeFilter !== 'all') {
      filtered = filtered.filter(c => c.tipologia === typeFilter);
    }
    
    // Filtro scadenze
    if (expireFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(c => {
        if (expireFilter === 'noexpiry') {
          return !c.dataScadenza;
        }
        
        if (!c.dataScadenza) return false;
        
        const scadenza = new Date(c.dataScadenza);
        scadenza.setHours(0, 0, 0, 0);
        const daysToExpiry = Math.ceil((scadenza - today) / (1000 * 60 * 60 * 24));
        
        if (expireFilter === 'expired') {
          return daysToExpiry < 0;
        } else if (expireFilter === 'expiring') {
          return daysToExpiry >= 0 && daysToExpiry <= 30;
        }
        return true;
      });
    }
    
    // Filtro date
    if (dateFilter !== 'all') {
      const now = new Date();
      let startDate = null;
      
      if (dateFilter === '3months') {
        startDate = new Date(now.setMonth(now.getMonth() - 3));
      } else if (dateFilter === '6months') {
        startDate = new Date(now.setMonth(now.getMonth() - 6));
      } else if (dateFilter === '1year') {
        startDate = new Date(now.setFullYear(now.getFullYear() - 1));
      } else if (dateFilter === 'custom' && dateFrom) {
        startDate = new Date(dateFrom);
      }
      
      if (startDate) {
        filtered = filtered.filter(c => new Date(c.dataInvio) >= startDate);
      }
      
      if (dateFilter === 'custom' && dateTo) {
        const endDate = new Date(dateTo);
        endDate.setHours(23, 59, 59, 999);
        filtered = filtered.filter(c => new Date(c.dataInvio) <= endDate);
      }
    }
    
    return filtered.sort((a, b) => new Date(b.dataInvio) - new Date(a.dataInvio));
  };
  
  // Filtra documenti per stato visualizzazione
  const filterWorkerDocs = (docs, filter, searchTerm = '', typeFilter = 'all') => {
    let filtered = [...docs];
    
    // Filtro per ricerca nome documento
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(d => 
        d.descrizione?.toLowerCase().includes(search) ||
        d.tipo?.toLowerCase().includes(search)
      );
    }
    
    // Filtro per tipo documento
    if (typeFilter !== 'all') {
      filtered = filtered.filter(d => d.tipo === typeFilter);
    }
    
    // Filtro per stato
    if (filter !== 'all') {
      filtered = filtered.filter(d => {
        const recipient = d.recipients?.find(r => r.id === currentUser.workerId);
        if (filter === 'unread') {
          return recipient && !recipient.letto;
        } else if (filter === 'read') {
          return recipient && recipient.letto;
        } else if (filter === 'approved') {
          return recipient && recipient.approvato;
        } else if (filter === 'notapproved') {
          return recipient && !recipient.approvato;
        }
        return true;
      });
    }
    
    return filtered;
  };
  
  // Conferma lettura comunicazione (LAVORATORE)
  const confirmReadCommunication = async (commId) => {
    setCommunications(communications.map(comm => {
      if (comm.id === commId) {
        return {
          ...comm,
          recipients: comm.recipients.map(r => 
            r.id === currentUser.workerId
              ? { ...r, letto: true, dataLettura: new Date().toISOString() }
              : r
          )
        };
      }
      return comm;
    }));
    const commForRead = communications.find(c => c.id === commId);
    const commReadCompany = companies.find(c => c.id === commForRead?.aziendaId);
    await auditLogService.log('CONFIRM', 'communication_read', commId, {
      userEmail: currentUser?.email,
      newValues: { workerId: currentUser.workerId, azione: 'conferma lettura', companyId: commForRead?.aziendaId, companyName: commReadCompany?.ragioneSociale }
    });
  };
  
  // Approva documento (LAVORATORE)
  const approveDocument = async (docId) => {
    setDocuments(documents.map(doc => {
      if (doc.id === docId) {
        return {
          ...doc,
          recipients: doc.recipients.map(r => 
            r.id === currentUser.workerId
              ? { ...r, approvato: true, dataApprovazione: new Date().toISOString() }
              : r
          )
        };
      }
      return doc;
    }));
    
    // Log per tracciabilità
    // Audit log approvazione documento
    const docForApprove = documents.find(d => d.id === docId);
    const docApproveCompany = companies.find(c => c.id === docForApprove?.companyId);
    await auditLogService.log('CONFIRM', 'document_share', docId, {
      userEmail: currentUser?.email,
      newValues: { workerId: currentUser.workerId, azione: 'approvazione documento', descrizione: docForApprove?.descrizione, companyId: docForApprove?.companyId, companyName: docApproveCompany?.ragioneSociale }
    });
  };
  
  // Presa visione documento (LAVORATORE) - download
  const viewDocument = (docId) => {
    setDocuments(documents.map(doc => {
      if (doc.id === docId) {
        return {
          ...doc,
          recipients: doc.recipients.map(r => 
            r.id === currentUser.workerId
              ? { ...r, letto: true, dataLettura: new Date().toISOString() }
              : r
          )
        };
      }
      return doc;
    }));
    
    // Log per tracciabilità
    const doc = documents.find(d => d.id === docId);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('👁️ PRESA VISIONE DOCUMENTO');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('Lavoratore:', currentUser.nome, currentUser.cognome);
    console.log('Documento:', doc?.descrizione);
    console.log('File:', doc?.file);
    console.log('Timestamp:', new Date().toISOString());
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  };
  
  // Calcola giorni totali lavorati da tutti i periodi
  const calcolaGiorniTotaliLavorati = (worker) => {
    if (!worker.storicoEventi || worker.storicoEventi.length === 0) {
      // Se non c'è storico, calcola dal periodo corrente
      const dataInizio = new Date(worker.dataAssunzione);
      const dataFine = worker.dataFine ? new Date(worker.dataFine) : new Date();
      return Math.floor((dataFine - dataInizio) / (1000 * 60 * 60 * 24));
    }
    
    // Estrae i periodi di lavoro dallo storico
    let periodi = [];
    let dataInizio = null;
    
    worker.storicoEventi.forEach(evento => {
      if (evento.tipo === 'ASSUNZIONE' || evento.tipo === 'RIASSUNZIONE') {
        dataInizio = new Date(evento.data);
      } else if (evento.tipo === 'DISMISSIONE' && dataInizio) {
        periodi.push({
          inizio: dataInizio,
          fine: new Date(evento.data)
        });
        dataInizio = null;
      }
    });
    
    // Aggiungi periodo corrente se attivo
    if (isLavoratoreAttivo(worker) && worker.dataAssunzione) {
      periodi.push({
        inizio: new Date(worker.dataAssunzione),
        fine: new Date()
      });
    }
    
    // Calcola giorni totali
    let giorniTotali = 0;
    periodi.forEach(periodo => {
      giorniTotali += Math.floor((periodo.fine - periodo.inizio) / (1000 * 60 * 60 * 24));
    });
    
    return giorniTotali;
  };
  
  // Conta numero riassunzioni
  const contaRiassunzioni = (worker) => {
    if (!worker.storicoEventi || worker.storicoEventi.length === 0) return 0;
    return worker.storicoEventi.filter(e => e.tipo === 'RIASSUNZIONE').length;
  };
  
  // Ottieni ultimo evento storico
  const getUltimoEvento = (worker) => {
    if (!worker.storicoEventi || worker.storicoEventi.length === 0) return null;
    return worker.storicoEventi.sort((a, b) => new Date(b.data) - new Date(a.data))[0];
  };
  
  // Formatta tipo evento per visualizzazione
  const formatTipoEvento = (tipo) => {
    const tipi = {
      'ASSUNZIONE': '🟢 Assunzione',
      'RIASSUNZIONE': '🔵 Riassunzione',
      'DISMISSIONE': '🔴 Cessazione',
      'CAMBIO_MANSIONE': '📋 Cambio Mansione',
      'CAMBIO_RUOLO': '🛡️ Cambio Ruolo',
      'NOTA': '📝 Nota'
    };
    return tipi[tipo] || tipo;
  };

  // 🆕 Verifica se un lavoratore è il Datore di Lavoro (Art. 34 D.Lgs. 81/2008)
  const isDatoreDiLavoro = (worker, companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (!company || !company.datoreLavoro || !company.datoreLavoro.email) return false;
    return worker.email === company.datoreLavoro.email;
  };

  const getTotalWorkers = () => {
    if (currentUser.role === 'AZIENDA' && currentUser.companyId) {
      // 🔧 ESCLUDI DATORE DI LAVORO dal conteggio (Art. 34 D.Lgs. 81/2008)
      return getLavoratoriAttivi(currentUser.companyId).filter(w => !isDatoreDiLavoro(w, currentUser.companyId)).length;
    }
    // 🔧 ESCLUDI DATORI DI LAVORO dal conteggio totale
    // 🆕 ESCLUDI lavoratori di aziende "Solo Piattaforma"
    const aziendeGestite = companies.filter(c => c.tipoContratto === 'gestione_completa').map(c => c.id);
    return workers.filter(w => 
      isLavoratoreAttivo(w) && 
      !isDatoreDiLavoro(w, w.companyId) &&
      aziendeGestite.includes(String(w.companyId))
    ).length;
  };

  // 🆕 FILTRA LAVORATORI IN BASE AL FILTRO STATO
  const getFilteredWorkersByStatus = (companyWorkers) => {
    if (workerStatusFilter === 'attivi') {
      return companyWorkers.filter(w => isLavoratoreAttivo(w));
    } else if (workerStatusFilter === 'dimessi') {
      return companyWorkers.filter(w => !isLavoratoreAttivo(w));
    }
    return companyWorkers; // 'all'
  };

  const validateCompanyForm = () => {
    const required = [
      newCompany.ragioneSociale,
      newCompany.indirizzo,
      newCompany.citta,
      newCompany.pec,
      newCompany.partitaIva,
      newCompany.datoreLavoro.nome,
      newCompany.datoreLavoro.cognome,
      newCompany.datoreLavoro.email
    ];
    
    return required.every(field => field && field.trim() !== '');
  };

  const removeDestinationBadge = (type, value) => {
    if (type === 'mansione') {
      setNewCommunication({
        ...newCommunication,
        destinatari: {
          ...newCommunication.destinatari,
          mansioni: newCommunication.destinatari.mansioni.filter(m => m !== value)
        }
      });
    } else if (type === 'ruolo') {
      setNewCommunication({
        ...newCommunication,
        destinatari: {
          ...newCommunication.destinatari,
          ruoli: newCommunication.destinatari.ruoli.filter(r => r !== value)
        }
      });
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      alert(`File "${file.name}" caricato con successo!`);
    }
  };

  const openModal = (type, data = null) => {
    setModalType(type);
    setShowModal(true);
    
    if (type === 'editWorker' && data) {
      setEditingWorker(data);
      setNewWorker({...data});
    }
    
    if (type === 'editCompany' && data) {
      setEditingCompany(data);
      setNewCompany({...data});
    }
    
    if (type === 'editCommunication' && data) {
      setEditingCommunication(data);
      setNewCommunication({...data});
    }

    // 🆕 Pre-seleziona azienda se profilo AZIENDA
    if (type === 'newCommunication' && currentUser.role === 'AZIENDA') {
      setNewCommunication({
        ...newCommunication,
        aziendaId: currentUser.companyId
      });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setEditingWorker(null);
    setEditingCompany(null);
    setEditingCommunication(null);
    setEditingDocument(null);
    setNewMansione('');
    setNewRuolo('');
    setUploadedFile(null);
    setNewDocumento(''); // 🆕
    // Reset forms
    setNewCompany({
      ragioneSociale: '',
      indirizzo: '',
      citta: '',
      pec: '',
      partitaIva: '',
      datoreLavoro: { nome: '', cognome: '', email: '', cellulare: '' },
      medicoCompetente: { nome: '', cognome: '', email: '', pec: '' },
      rspp: { nome: '', cognome: '', email: '', pec: '' },
      codiciAteco: [{ codice: '', descrizione: '' }]
    });
    setNewWorker({
      nome: '',
      cognome: '',
      luogoNascita: '',
      dataNascita: '',
      codiceFiscale: '',
      email: '',
      cellulare: '',
      mansione: '',
      ruoli: [],
      dataAssunzione: '',
      dataFine: ''
    });
    setNewCommunication({
      titolo: '',
      contenuto: '',
      tipologia: 'Messaggio',
      dataScadenza: '',
      stato: 'Bozza',
      aziendaId: currentUser.role === 'AZIENDA' ? currentUser.companyId : null, // 🆕 Mantieni azienda se profilo AZIENDA
      destinatari: {
        mansioni: [],
        ruoli: [],
        ruoliOrganizzativi: [],
        tuttiLavoratori: false,
        personeSpecifiche: []
      },
      allegati: []
    });
    // 🆕 Reset form documento
    setNewDocument({
      tipo: 'DVR',
      descrizione: '',
      dataRevisione: '',
      dataScadenza: '',
      file: null,
      stato: 'Privato',
      destinatari: {
        mansioni: [],
        ruoli: [],
        ruoliOrganizzativi: [],
        tuttiLavoratori: false,
        personeSpecifiche: []
      }
    });
    setDocumentFileObject(null);
    setRevisionFileObject(null);
  };

  const handleSaveCompany = async () => {
    if (!validateCompanyForm()) {
      alert('Compilare tutti i campi obbligatori:\n- Ragione Sociale\n- Indirizzo\n- Città\n- PEC\n- Partita IVA\n- Datore di Lavoro (Nome, Cognome, Email)');
      return;
    }
    
    try {
      if (editingCompany) {
        await companiesService.update(editingCompany.id, newCompany);
        await auditLogService.log('UPDATE', 'company', editingCompany.id, {
          userEmail: currentUser?.email,
          newValues: { ragioneSociale: newCompany.ragioneSociale, companyId: editingCompany.id, companyName: newCompany.ragioneSociale }
        });
        alert('Azienda modificata con successo!');
      } else {
        const createdCompany = await companiesService.create(newCompany);
        await auditLogService.log('CREATE', 'company', createdCompany?.id, {
          userEmail: currentUser?.email,
          newValues: { ragioneSociale: newCompany.ragioneSociale, companyId: createdCompany?.id, companyName: newCompany.ragioneSociale }
        });
        alert('Azienda salvata con successo!');
      }
      
      await reloadData();
      closeModal();
      
    } catch (err) {
      console.error('Errore salvataggio azienda:', err);
      alert('Errore nel salvataggio: ' + err.message);
    }
  };

  // 🔐 ELIMINA AZIENDA - Apre modal conferma password
  const handleDeleteCompany = (companyId) => {
    setPendingDeleteCompanyId(companyId);
    setDeleteConfirmPassword('');
    setDeleteConfirmError('');
    setShowDeleteConfirmModal(true);
  };

  // 🔐 CONFERMA ELIMINAZIONE - Verifica password e procede
  const confirmDeleteCompany = async () => {
    if (!deleteConfirmPassword) {
      setDeleteConfirmError('Inserisci la password per confermare');
      return;
    }
    
    setDeleteConfirmLoading(true);
    setDeleteConfirmError('');
    
    try {
      // Verifica la password ri-autenticandosi con Supabase
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: currentUser.email,
        password: deleteConfirmPassword
      });
      
      if (authError) {
        setDeleteConfirmError('Password non corretta');
        setDeleteConfirmLoading(false);
        return;
      }
      
      // Password corretta - procedi con eliminazione
      await companiesService.deactivate(pendingDeleteCompanyId);
      const companyToDelete = companies.find(c => c.id === pendingDeleteCompanyId);
      await auditLogService.log('DELETE', 'company', pendingDeleteCompanyId, {
        userEmail: currentUser?.email,
        newValues: { azione: 'disattivazione', companyId: pendingDeleteCompanyId, companyName: companyToDelete?.ragioneSociale }
      });
      await reloadData();
      setSelectedCompany(null);
      
      // Chiudi modal e pulisci
      setShowDeleteConfirmModal(false);
      setDeleteConfirmPassword('');
      setPendingDeleteCompanyId(null);
      setDeleteConfirmLoading(false);
      alert('Azienda eliminata con successo!');
    } catch (err) {
      console.error('Errore eliminazione azienda:', err);
      setDeleteConfirmError('Errore: ' + err.message);
      setDeleteConfirmLoading(false);
    }
  };

  const handleSaveWorker = async () => {
    try {
      if (editingWorker) {
        await workersService.update(editingWorker.id, {...newWorker, companyId: editingWorker.companyId});
        const workerCompanyUpd = companies.find(c => c.id === editingWorker.companyId);
        await auditLogService.log('UPDATE', 'worker', editingWorker.id, {
          userEmail: currentUser?.email,
          newValues: { nome: newWorker.nome, cognome: newWorker.cognome, companyId: editingWorker.companyId, companyName: workerCompanyUpd?.ragioneSociale }
        });
        alert('Lavoratore modificato con successo!');
      } else {
        const createdWorker = await workersService.create({...newWorker, companyId: selectedCompany});
        const workerCompanyNew = companies.find(c => c.id === selectedCompany);
        await auditLogService.log('CREATE', 'worker', createdWorker?.id, {
          userEmail: currentUser?.email,
          newValues: { nome: newWorker.nome, cognome: newWorker.cognome, companyId: selectedCompany, companyName: workerCompanyNew?.ragioneSociale }
        });
        alert('Lavoratore aggiunto con successo!');
      }
      await reloadData();
      closeModal();
    } catch (err) {
      console.error('Errore salvataggio lavoratore:', err);
      alert('Errore: ' + err.message);
    }
  };

  // 🔒 FASE 6.4: Elimina intelligente - cancella solo se nessuna traccia legale
  const handleDeleteWorker = async (workerId) => {
    const worker = workers.find(w => w.id === workerId);

    // PROTEZIONE: Impedisci eliminazione lavoratore-datore
    if (worker && worker.isDatore) {
      alert('⚠️ Impossibile eliminare il Datore di Lavoro!\n\nPer rimuovere il Datore dall\'elenco lavoratori:\n1. Vai in "Anagrafica Azienda"\n2. Deseleziona "Il Datore cumula ruoli operativi"');
      return;
    }

    // Verifica se il lavoratore ha tracce legali (comunicazioni ricevute o documenti condivisi)
    const hasCommunications = communications.some(c => 
      c.recipients?.some(r => String(r.id) === String(workerId))
    );
    const hasDocuments = documents.some(d => 
      d.recipients?.some(r => String(r.id) === String(workerId))
    );
    const hasTracce = hasCommunications || hasDocuments;

    if (hasTracce) {
      // Il lavoratore ha tracce legali: NON si può cancellare
      alert('⚠️ Impossibile eliminare questo lavoratore!\n\nHa comunicazioni ricevute o documenti condivisi che devono essere conservati per tracciabilità legale (D.Lgs. 81/2008).\n\nUsa il pulsante "Dimetti" per spostarlo nello storico.');
      return;
    }

    // Il lavoratore è "pulito": si può cancellare fisicamente
    if (window.confirm(`Confermi l'eliminazione definitiva di ${worker.nome} ${worker.cognome}?\n\nIl lavoratore non ha comunicazioni o documenti associati, quindi può essere rimosso dal database.`)) {
      try {
        await workersService.delete(workerId);
        await auditLogService.log('DELETE', 'worker', workerId, {
          userEmail: currentUser?.email,
          newValues: { nome: worker.nome, cognome: worker.cognome, companyId: worker.companyId, companyName: companies.find(c => c.id === worker.companyId)?.ragioneSociale }
        });
        await reloadData();
        alert('✅ Lavoratore eliminato definitivamente.');
      } catch (err) {
        console.error('Errore eliminazione lavoratore:', err);
        alert('Errore: ' + err.message);
      }
    }
  };

  // 🆕 DISMISSIONE LAVORATORE
  const handleDismissioneLavoratore = async (workerId) => {
    const worker = workers.find(w => w.id === workerId);
    const oggi = new Date().toISOString().split('T')[0];

    if (window.confirm(`Confermi la dismissione di ${worker.nome} ${worker.cognome}?\n\nIl lavoratore verrà spostato nello storico e non comparirà più nell'elenco attivi.\n\nData fine rapporto: ${formatDateOnly(oggi)}\n\n⚠️ Per tracciabilità legale, i dati del lavoratore vengono conservati nel database.`)) {
      try {
        // Aggiungi evento automatico nella Timeline Storico
        const eventoTimeline = {
          data: oggi,
          tipo: 'CESSAZIONE',
          mansione: worker.mansione || '',
          ruoli: worker.ruoli || [],
          note: 'Dismissione registrata automaticamente dal sistema',
          inseritoDa: currentUser ? `${currentUser.nome} ${currentUser.cognome}` : 'Sistema',
          dataInserimento: new Date().toISOString()
        };
        const storicoAggiornato = [...(worker.storicoEventi || []), eventoTimeline];

        await workersService.update(workerId, { ...worker, dataFine: oggi, storicoEventi: storicoAggiornato });
        await auditLogService.log('UPDATE', 'worker', workerId, {
          userEmail: currentUser?.email,
          newValues: { azione: 'dismissione', nome: worker.nome, cognome: worker.cognome, dataFine: oggi, companyId: worker.companyId, companyName: companies.find(c => c.id === worker.companyId)?.ragioneSociale }
        });
        await reloadData();
        alert('✅ Lavoratore dimesso e spostato nello storico');
      } catch (err) {
        console.error('Errore dismissione:', err);
        alert('Errore: ' + err.message);
      }
    }
  };

  // 🆕 RIATTIVAZIONE LAVORATORE
  const handleRiattivazioneLavoratore = async (workerId) => {
    const worker = workers.find(w => w.id === workerId);

    if (window.confirm(`Confermi la riattivazione di ${worker.nome} ${worker.cognome}?\n\nIl lavoratore tornerà nell'elenco attivi.\nLa data fine rapporto verrà rimossa.`)) {
      try {
        // Aggiungi evento automatico nella Timeline Storico
        const oggi = new Date().toISOString().split('T')[0];
        const eventoTimeline = {
          data: oggi,
          tipo: 'RIASSUNZIONE',
          mansione: worker.mansione || '',
          ruoli: worker.ruoli || [],
          note: 'Riattivazione registrata automaticamente dal sistema',
          inseritoDa: currentUser ? `${currentUser.nome} ${currentUser.cognome}` : 'Sistema',
          dataInserimento: new Date().toISOString()
        };
        const storicoAggiornato = [...(worker.storicoEventi || []), eventoTimeline];

        await workersService.update(workerId, { ...worker, dataFine: '', storicoEventi: storicoAggiornato });
        await auditLogService.log('UPDATE', 'worker', workerId, {
          userEmail: currentUser?.email,
          newValues: { azione: 'riattivazione', nome: worker.nome, cognome: worker.cognome, companyId: worker.companyId, companyName: companies.find(c => c.id === worker.companyId)?.ragioneSociale }
        });
        await reloadData();
        alert('✅ Lavoratore riattivato');
      } catch (err) {
        console.error('Errore riattivazione:', err);
        alert('Errore: ' + err.message);
      }
    }
  };

  // 🆕 FUNZIONE SOLLECITA - Invia email di sollecito a destinatari e TRACCIA
  const handleSollecita = async (recipient, itemTitle, itemType = 'comunicazione', itemId) => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    const worker = workers.find(w => w.id === recipient.id);
    const email = recipient.email || worker?.email || 'N/A';
    
    if (email === 'N/A') {
      alert('⚠️ Impossibile inviare sollecito: email del destinatario non disponibile');
      return;
    }

    // Verifica numero solleciti già inviati
    const numSolleciti = recipient.solleciti?.length || 0;
    const warningMessage = numSolleciti >= 3 
      ? `\n\n⚠️ ATTENZIONE: Questo destinatario ha già ricevuto ${numSolleciti} solleciti!` 
      : '';

    const confirmMessage = `📧 Inviare email di sollecito a ${recipient.nome}?\n\nEmail: ${email}\n${itemType === 'comunicazione' ? 'Comunicazione' : 'Documento'}: ${itemTitle}${warningMessage}`;
    
    if (window.confirm(confirmMessage)) {
      try {
        const senderName = `${currentUser.nome} ${currentUser.cognome}`;
        
        // Salva sollecito nel database
        if (itemType === 'comunicazione') {
          await communicationsService.sendReminder(itemId, recipient.id, senderName);
        } else {
          await documentsService.sendReminder(itemId, recipient.id, senderName);
        }
        
        const sollecitoCompanyId = itemType === 'comunicazione' 
            ? communications.find(comm => comm.id === itemId)?.aziendaId 
            : documents.find(doc => doc.id === itemId)?.companyId;
        const sollecitoCompanyName = companies.find(c => c.id === sollecitoCompanyId)?.ragioneSociale;
        await auditLogService.log('UPDATE', itemType === 'comunicazione' ? 'communication' : 'document', itemId, {
          userEmail: currentUser?.email,
          newValues: { azione: 'sollecito', destinatario: recipient.nome, email: email, companyId: sollecitoCompanyId, companyName: sollecitoCompanyName }
        });
        await reloadData();
        
        // 🆕 Invio email sollecito reale
        try {
          const company = companies.find(c => c.id === (itemType === 'comunicazione' 
            ? communications.find(comm => comm.id === itemId)?.aziendaId 
            : documents.find(doc => doc.id === itemId)?.companyId));
          
          const template = emailService.buildReminderEmail({
            workerName: recipient.nome,
            itemTitle: itemTitle,
            itemType: itemType,
            companyName: company?.ragioneSociale || '',
            senderName: `${currentUser.nome} ${currentUser.cognome}`,
            reminderNumber: numSolleciti + 1
          });
          
          await emailService.send({
            to: email,
            ...template
          });
        } catch (emailErr) {
          console.error('Errore invio email sollecito:', emailErr);
          // Non bloccare il flusso, il sollecito è già salvato nel DB
        }

        alert(`✅ Sollecito inviato con successo a ${recipient.nome}!\n\n📧 ${email}\n⏰ ${formatDate(new Date().toISOString())}\n📊 Sollecito N° ${numSolleciti + 1}`);
      } catch (err) {
        console.error('Errore invio sollecito:', err);
        alert('Errore: ' + err.message);
      }
    }
  };

  const validateCommunicationForm = () => {
    if (!newCommunication.aziendaId) {
      alert('Seleziona un\'azienda');
      return false;
    }
    
    const hasDestinations = 
      newCommunication.destinatari.mansioni.length > 0 ||
      newCommunication.destinatari.ruoli.length > 0 ||
      newCommunication.destinatari.ruoliOrganizzativi.length > 0 ||
      newCommunication.destinatari.tuttiLavoratori ||
      newCommunication.destinatari.personeSpecifiche.length > 0;
    
    if (!hasDestinations) {
      alert('Seleziona almeno un destinatario');
      return false;
    }
    
    return true;
  };

  const handleSaveCommunication = async () => {
    if (!validateCommunicationForm()) {
      return;
    }

    try {
      // Calcola destinatari reali basati sulla selezione
      const companyWorkers = workers.filter(w => String(w.companyId) === String(newCommunication.aziendaId));
      let recipients = [];

      if (newCommunication.destinatari.tuttiLavoratori) {
        recipients = companyWorkers.map(w => ({
          id: w.id,
          nome: `${w.nome} ${w.cognome}`,
          letto: false,
          dataLettura: null
        }));
      } else {
        // Per mansioni
        if (newCommunication.destinatari.mansioni.length > 0) {
          const byMansione = companyWorkers.filter(w =>
            newCommunication.destinatari.mansioni.includes(w.mansione)
          );
          recipients = [...recipients, ...byMansione.map(w => ({
            id: w.id,
            nome: `${w.nome} ${w.cognome}`,
            letto: false,
            dataLettura: null
          }))];
        }

        // Per ruoli
        if (newCommunication.destinatari.ruoli.length > 0) {
          const byRuolo = companyWorkers.filter(w =>
            w.ruoli && w.ruoli.some(r => newCommunication.destinatari.ruoli.includes(r))
          );
          recipients = [...recipients, ...byRuolo.map(w => ({
            id: w.id,
            nome: `${w.nome} ${w.cognome}`,
            letto: false,
            dataLettura: null
          }))];
        }

        // Per persone specifiche
        if (newCommunication.destinatari.personeSpecifiche.length > 0) {
          const byPersone = companyWorkers.filter(w =>
            newCommunication.destinatari.personeSpecifiche.includes(w.id)
          );
          recipients = [...recipients, ...byPersone.map(w => ({
            id: w.id,
            nome: `${w.nome} ${w.cognome}`,
            letto: false,
            dataLettura: null
          }))];
        }

        // Rimuovi duplicati
        recipients = recipients.filter((r, index, self) =>
          index === self.findIndex(t => t.id === r.id)
        );
      }

      // Per ruoli organizzativi (Figure Aziendali)
      if (newCommunication.destinatari.ruoliOrganizzativi && newCommunication.destinatari.ruoliOrganizzativi.length > 0) {
        const company = companies.find(c => c.id === newCommunication.aziendaId);
        if (company) {
          newCommunication.destinatari.ruoliOrganizzativi.forEach(ruolo => {
            if (ruolo === 'Datore di Lavoro' && company.datoreLavoro && company.datoreLavoro.email) {
              recipients.push({
                id: `org-datore-${company.id}`,
                nome: `${company.datoreLavoro.nome} ${company.datoreLavoro.cognome} (Datore di Lavoro)`,
                email: company.datoreLavoro.email,
                letto: false,
                dataLettura: null,
                tipo: 'organizzativo'
              });
            } else if (ruolo === 'RSPP - Responsabile Servizio Prevenzione e Protezione' && company.rspp && company.rspp.email) {
              recipients.push({
                id: `org-rspp-${company.id}`,
                nome: `${company.rspp.nome} ${company.rspp.cognome} (RSPP)`,
                email: company.rspp.email,
                letto: false,
                dataLettura: null,
                tipo: 'organizzativo'
              });
            } else if (ruolo === 'Medico Competente' && company.medicoCompetente && company.medicoCompetente.email) {
              recipients.push({
                id: `org-medico-${company.id}`,
                nome: `${company.medicoCompetente.nome} ${company.medicoCompetente.cognome} (Medico Competente)`,
                email: company.medicoCompetente.email,
                letto: false,
                dataLettura: null,
                tipo: 'organizzativo'
              });
            }
          });
        }
      }

      if (recipients.length === 0) {
        alert('Nessun destinatario trovato con i criteri selezionati!');
        return;
      }

      // Se è una bozza, salva senza email
      if (newCommunication.stato === 'Bozza') {
        if (editingCommunication) {
          await communicationsService.update(editingCommunication.id, {
            ...newCommunication,
            recipients: recipients
          });
          const commCompanyBozza = companies.find(c => c.id === newCommunication.aziendaId);
          await auditLogService.log('UPDATE', 'communication', editingCommunication.id, {
            userEmail: currentUser?.email,
            newValues: { titolo: newCommunication.titolo, tipologia: newCommunication.tipologia, stato: 'Bozza', companyId: newCommunication.aziendaId, companyName: commCompanyBozza?.ragioneSociale }
          });
          alert('Bozza aggiornata con successo!');
        } else {
          const dataCreazione = new Date().toISOString();
          const commData = {
            ...newCommunication,
            dataInvio: dataCreazione,
            recipients: recipients
          };
          const createdComm = await communicationsService.create(commData);
          const commCompanyBozza = companies.find(c => c.id === newCommunication.aziendaId);
          await auditLogService.log('CREATE', 'communication', createdComm?.id, {
            userEmail: currentUser?.email,
            newValues: { titolo: newCommunication.titolo, tipologia: newCommunication.tipologia, stato: 'Bozza', companyId: newCommunication.aziendaId, companyName: commCompanyBozza?.ragioneSociale }
          });
          if (createdComm && recipients.length > 0) {
            const workerRecipientIds = recipients
              .filter(r => !r.tipo || r.tipo !== 'organizzativo')
              .map(r => r.id);
            if (workerRecipientIds.length > 0) {
              await communicationsService.addRecipients(createdComm.id, workerRecipientIds);
            }
          }
          alert('Bozza salvata con successo!');
        }
        await reloadData();
        closeModal();
        return;
      }

      // Mostra anteprima email
      const recipientEmails = recipients.map(r => r.email || workers.find(w => w.id === r.id)?.email || 'N/A');
      const emailPreview = `📧 Verranno inviate ${recipients.length} email a:\n\n${recipients.map((r, i) => `• ${r.nome} (${recipientEmails[i]})`).join('\n')}\n\n📌 Oggetto: ${newCommunication.titolo}\n\nConfermi l'invio?`;

      if (!window.confirm(emailPreview)) {
        return;
      }

      // Salva nel database
      if (editingCommunication) {
        // Controlla se si sta passando da Bozza a Pubblicata
        const wasBozza = editingCommunication.stato === 'Bozza';
        
        if (wasBozza && newCommunication.stato === 'Pubblicato') {
          // 🔐 Bozza → Pubblicato: genera hash SHA-256
          const dataInvio = new Date().toISOString();
          const commData = {
            ...newCommunication,
            dataInvio: dataInvio,
            recipients: recipients
          };
          const hashContent = buildCommunicationHashContent(commData);
          const sha256Hash = await generateSHA256Hash(hashContent);
          
          await communicationsService.update(editingCommunication.id, {
            ...commData,
            hashSha256: sha256Hash
          });
          
          // Recipients già inseriti quando la bozza è stata salvata - non reinserire
          
          const commCompanyUpd = companies.find(c => c.id === newCommunication.aziendaId);
          await auditLogService.log('UPDATE', 'communication', editingCommunication.id, {
            userEmail: currentUser?.email,
            newValues: { titolo: newCommunication.titolo, tipologia: newCommunication.tipologia, stato: 'Pubblicato (da Bozza)', destinatari: recipients.length, hashSha256: sha256Hash, companyId: newCommunication.aziendaId, companyName: commCompanyUpd?.ragioneSociale }
          });
          
          // 📧 Mostra modal conferma invio email
          if (recipients.length > 0) {
            const emailData = prepareEmailData(recipients, { ...commData, id: editingCommunication.id });
            setPendingEmailData(emailData);
            setShowEmailModal(true);
          }
          
          alert('Comunicazione pubblicata con successo!');
        } else {
          // Modifica semplice (era già Pubblicato)
          await communicationsService.update(editingCommunication.id, {
            ...newCommunication,
            recipients: recipients
          });
          const commCompanyUpd = companies.find(c => c.id === newCommunication.aziendaId);
          await auditLogService.log('UPDATE', 'communication', editingCommunication.id, {
            userEmail: currentUser?.email,
            newValues: { titolo: newCommunication.titolo, tipologia: newCommunication.tipologia, companyId: newCommunication.aziendaId, companyName: commCompanyUpd?.ragioneSociale }
          });
          alert('Comunicazione modificata con successo!');
        }
      } else {
        // 🔐 FASE 6.1: Genera hash SHA-256 alla creazione
        const dataInvio = new Date().toISOString();
        const commData = {
          ...newCommunication,
          dataInvio: dataInvio,
          recipients: recipients
        };
        const hashContent = buildCommunicationHashContent(commData);
        const sha256Hash = await generateSHA256Hash(hashContent);
        
        const createdComm = await communicationsService.create({
          ...commData,
          hashSha256: sha256Hash
        });
        
        // 🔧 Salva recipients nella tabella communication_reads
        if (createdComm && recipients.length > 0) {
          const workerRecipientIds = recipients
            .filter(r => !r.tipo || r.tipo !== 'organizzativo')
            .map(r => r.id);
          if (workerRecipientIds.length > 0) {
            await communicationsService.addRecipients(createdComm.id, workerRecipientIds);
          }
        }
        const commCompanyNew = companies.find(c => c.id === newCommunication.aziendaId);
        await auditLogService.log('CREATE', 'communication', createdComm?.id, {
          userEmail: currentUser?.email,
          newValues: { titolo: newCommunication.titolo, tipologia: newCommunication.tipologia, destinatari: recipients.length, hashSha256: sha256Hash, companyId: newCommunication.aziendaId, companyName: commCompanyNew?.ragioneSociale }
        });

        // 📧 Attiva modal conferma invio email ai destinatari
        if (recipients.length > 0) {
          const emailData = prepareEmailData(recipients, { ...commData, id: createdComm?.id });
          setPendingEmailData(emailData);
          setShowEmailModal(true);
        }

        alert('Comunicazione salvata con successo!');
      }

      await reloadData();
      closeModal();
    } catch (err) {
      console.error('Errore salvataggio comunicazione:', err);
      alert('Errore: ' + err.message);
    }
  };

  // 🗑️ Elimina comunicazione in bozza
  const handleDeleteCommunication = async (commId) => {
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    const comm = communications.find(c => c.id === commId);
    if (!comm || comm.stato !== 'Bozza') {
      alert('Solo le comunicazioni in Bozza possono essere eliminate.');
      return;
    }
    if (!window.confirm('Sei sicuro di voler eliminare questa bozza?')) return;
    try {
      await communicationsService.delete(commId);
      const commCompanyDel = companies.find(c => String(c.id) === String(comm.aziendaId));
      await auditLogService.log('DELETE', 'communication', commId, {
        userEmail: currentUser?.email,
        newValues: { titolo: comm.titolo, tipologia: comm.tipologia, stato: 'Bozza', companyId: comm.aziendaId, companyName: commCompanyDel?.ragioneSociale }
      });
      await reloadData();
      alert('Bozza eliminata con successo!');
    } catch (err) {
      console.error('Errore eliminazione comunicazione:', err);
      alert('Errore: ' + err.message);
    }
  };

  

  // 🆕 FUNZIONI IMPORT EXCEL LAVORATORI
  
    // Download template Excel (.xlsx) vuoto
  const handleDownloadTemplateExcel = () => {
    // Dati template con esempi
    const templateData = [
      {
        'Nome': 'Mario',
        'Cognome': 'Rossi',
        'Luogo Nascita': 'Milano',
        'Data Nascita (gg/mm/aaaa)': '15/03/1985',
        'Codice Fiscale': 'RSSMRA85C15F205Z',
        'Email': 'm.rossi@example.it',
        'Cellulare': '+39 333 1234567',
        'Mansione': 'Operaio Produzione',
        'Ruoli (separati da virgola)': 'Preposto,Addetto Antincendio',
        'Data Assunzione (gg/mm/aaaa)': '10/01/2020',
        'Data Fine (gg/mm/aaaa)': ''
      },
      {
        'Nome': 'Anna',
        'Cognome': 'Verdi',
        'Luogo Nascita': 'Roma',
        'Data Nascita (gg/mm/aaaa)': '22/07/1990',
        'Codice Fiscale': 'VRDNNA90L62H501W',
        'Email': 'a.verdi@example.it',
        'Cellulare': '+39 340 7654321',
        'Mansione': 'Addetto Amministrativo',
        'Ruoli (separati da virgola)': '',
        'Data Assunzione (gg/mm/aaaa)': '15/06/2021',
        'Data Fine (gg/mm/aaaa)': ''
      }
    ];

    // Crea workbook Excel
    const ws = XLSX.utils.json_to_sheet(templateData);
    
    // Imposta larghezza colonne
    ws['!cols'] = [
      { wch: 12 },  // Nome
      { wch: 14 },  // Cognome
      { wch: 15 },  // Luogo Nascita
      { wch: 24 },  // Data Nascita
      { wch: 18 },  // Codice Fiscale
      { wch: 25 },  // Email
      { wch: 18 },  // Cellulare
      { wch: 22 },  // Mansione
      { wch: 30 },  // Ruoli
      { wch: 26 },  // Data Assunzione
      { wch: 24 },  // Data Fine
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lavoratori');
    
    // Download file
    XLSX.writeFile(wb, `Template_Import_Lavoratori_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    alert('📥 Template Excel scaricato!\n\n✅ File .XLSX → Si apre direttamente con Excel\n✅ Compila i dati dei lavoratori (vedi esempi)\n✅ Formato date: gg/mm/aaaa\n✅ Ruoli separati da virgola\n\n💾 Salva il file dopo la compilazione\n📤 Carica il file salvato con "Importa Lavoratori"');
  };

    // Parse file Excel (.xlsx) o TSV caricato
  const handleExcelFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        let headers = [];
        let dataRows = [];

        if (isExcel) {
          // 🆕 Lettura file Excel con SheetJS
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
          
          if (jsonData.length < 2) {
            alert('❌ File Excel vuoto o formato non valido');
            return;
          }
          
          headers = jsonData[0].map(h => String(h).trim());
          dataRows = jsonData.slice(1);
        } else {
          // Lettura TSV/TXT/CSV (retrocompatibilità)
          const text = event.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            alert('❌ File vuoto o formato non valido');
            return;
          }
          
          headers = lines[0].split('\t').map(h => h.trim());
          dataRows = lines.slice(1).map(line => line.split('\t'));
        }

        // Verifica headers richiesti
        const requiredHeaders = [
          'Nome', 
          'Cognome', 
          'Codice Fiscale', 
          'Email', 
          'Mansione', 
          'Data Assunzione (gg/mm/aaaa)'
        ];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          alert(`❌ Colonne mancanti nel file:\n${missingHeaders.join(', ')}\n\nScarica il template corretto.`);
          return;
        }

        // Parse data rows
        const parsedData = [];
        const errors = [];
        
        for (let i = 0; i < dataRows.length; i++) {
          const values = dataRows[i];
          
          // Salta righe completamente vuote
          if (values.every(v => !v || !String(v).trim())) {
            continue;
          }
          
          const row = {};
          
          headers.forEach((header, index) => {
            row[header] = values[index] ? String(values[index]).trim() : '';
          });
          
          // Validazione base
          const rowErrors = [];
          
          if (!row['Nome']) rowErrors.push('Nome mancante');
          if (!row['Cognome']) rowErrors.push('Cognome mancante');
          if (!row['Codice Fiscale']) rowErrors.push('Codice Fiscale mancante');
          if (!row['Email']) rowErrors.push('Email mancante');
          if (!row['Mansione']) rowErrors.push('Mansione mancante');
          if (!row['Data Assunzione (gg/mm/aaaa)']) rowErrors.push('Data Assunzione mancante');
          
          // Validazione formato email
          if (row['Email'] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row['Email'])) {
            rowErrors.push('Email non valida');
          }
          
          // Validazione Codice Fiscale (16 caratteri)
          if (row['Codice Fiscale'] && row['Codice Fiscale'].length !== 16) {
            rowErrors.push('Codice Fiscale deve essere 16 caratteri');
          }
          
          parsedData.push({
            ...row,
            rowNumber: i + 2,
            errors: rowErrors,
            valid: rowErrors.length === 0
          });
          
          if (rowErrors.length > 0) {
            errors.push({
              row: i + 2,
              errors: rowErrors,
              data: row
            });
          }
        }
        
        setExcelFile(file);
        setExcelData(parsedData);
        setExcelPreview(parsedData.slice(0, 10)); // Preview primi 10
        setImportErrors(errors);
        setShowImportExcelModal(false); // Chiudi modal upload
        setShowImportPreview(true); // Apri modal preview automaticamente
        
      } catch (error) {
        alert('❌ Errore lettura file:\n' + error.message);
        console.error('Import error:', error);
      }
    };
    
    // Leggi in modo diverso in base al tipo di file
    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  // Conferma import massivo
  const handleConfirmImport = () => {
    const validData = excelData.filter(row => row.valid);
    
    if (validData.length === 0) {
      alert('❌ Nessun dato valido da importare!');
      return;
    }

    const companyId = currentUser.role === 'AZIENDA' ? currentUser.companyId : selectedCompany;
    
    if (!companyId) {
      alert('❌ Nessuna azienda selezionata');
      return;
    }

    // Converti date da formato gg/mm/aaaa a aaaa-mm-gg
    const convertDate = (dateStr) => {
      if (!dateStr) return '';
      const parts = dateStr.split('/');
      if (parts.length !== 3) return '';
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    };

    // Crea workers da dati Excel
    const newWorkers = validData.map((row, index) => {
      const ruoliArray = row['Ruoli (separati da virgola)'] 
        ? row['Ruoli (separati da virgola)'].split(',').map(r => r.trim()).filter(r => r)
        : [];
      
      return {
        id: workers.length + index + 1,
        nome: row['Nome'],
        cognome: row['Cognome'],
        luogoNascita: row['Luogo Nascita'] || '',
        dataNascita: convertDate(row['Data Nascita (gg/mm/aaaa)']),
        codiceFiscale: row['Codice Fiscale'].toUpperCase(),
        email: row['Email'],
        cellulare: row['Cellulare'] || '',
        mansione: row['Mansione'],
        ruoli: ruoliArray,
        dataAssunzione: convertDate(row['Data Assunzione (gg/mm/aaaa)']),
        dataFine: convertDate(row['Data Fine (gg/mm/aaaa)']),
        companyId: companyId,
        storicoEventi: [] // 🆕 Inizializza array vuoto per storico eventi
      };
    });

    // Aggiungi workers
    setWorkers([...workers, ...newWorkers]);
    
    // Chiudi modal e reset
    setShowImportExcelModal(false);
    setShowImportPreview(false);
    setExcelFile(null);
    setExcelData([]);
    setExcelPreview([]);
    setImportErrors([]);
    
    // Report finale
    const invalidCount = excelData.length - validData.length;
    let message = `✅ Import completato con successo!\n\n`;
    message += `📊 Lavoratori importati: ${validData.length}\n`;
    if (invalidCount > 0) {
      message += `⚠️ Righe con errori saltate: ${invalidCount}\n`;
    }
    message += `\n🎉 Totale lavoratori azienda: ${workers.filter(w => String(w.companyId) === String(companyId)).length + validData.length}`;
    
    alert(message);
  };

  const handleAddMansione = () => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    if (newMansione.trim()) {
      setMansioniTemplate([...mansioniTemplate, newMansione.trim()]);
      setNewMansione('');
      closeModal();
      alert('Mansione aggiunta con successo!');
    }
  };

  const handleDeleteMansione = (mansione) => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    if (window.confirm(`Sei sicuro di voler eliminare la mansione "${mansione}"?`)) {
      setMansioniTemplate(mansioniTemplate.filter(m => m !== mansione));
      alert('Mansione eliminata con successo!');
    }
  };

  const handleAddRuolo = () => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    if (newRuolo.trim()) {
      setRuoliTemplate([...ruoliTemplate, newRuolo.trim()]);
      setNewRuolo('');
      closeModal();
      alert('Ruolo aggiunto con successo!');
    }
  };

  const handleDeleteRuolo = (ruolo) => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    if (window.confirm(`Sei sicuro di voler eliminare il ruolo "${ruolo}"?`)) {
      setRuoliTemplate(ruoliTemplate.filter(r => r !== ruolo));
      alert('Ruolo eliminato con successo!');
    }
  };

  // 🆕 FUNZIONI GESTIONE DOCUMENTI AZIENDALI
  const handleSaveDocument = async () => {
    if (!newDocument.descrizione || !newDocument.dataRevisione) {
      alert('Compila i campi obbligatori: Descrizione e Data Revisione');
      return;
    }

    // 🆕 DOCUMENTI PRIVATI: salva direttamente senza conferma (sono sempre modificabili)
    if (newDocument.stato === 'Privato') {
      await confirmAndSaveDocument();
      return;
    }

    // DOCUMENTI CONDIVISI: mostra modal conferma (sono immutabili dopo salvataggio)
    const docCompanyId = currentUser.role === 'AZIENDA' 
      ? currentUser.companyId 
      : (selectedCompanyForDocuments || selectedCompany);

    const company = companies.find(c => c.id === docCompanyId);
    
    setPendingDocumentData({
      document: newDocument,
      company,
      isEdit: !!editingDocument,
      editingDocument: editingDocument
    });
    
    setShowDocumentConfirmModal(true);
  };

  // 🆕 CONFERMA E SALVATAGGIO DOCUMENTO (logica completa) - VERSIONE DATABASE
  const confirmAndSaveDocument = async () => {
    // Chiudi modal conferma
    setShowDocumentConfirmModal(false);
    setPendingDocumentData(null);

    try {
      // Determina companyId
      const docCompanyId = currentUser.role === 'AZIENDA' 
        ? currentUser.companyId 
        : (selectedCompanyForDocuments || selectedCompany);

      // ===== HELPER: Calcola recipients =====
      const calculateDocRecipients = (companyIdForRecipients, sourceCompanyId) => {
        let recipients = [];
        const companyWorkers = workers.filter(w => String(w.companyId) === String(companyIdForRecipients));
        
        if (newDocument.destinatari.tuttiLavoratori) {
          recipients = companyWorkers.map(w => ({
            id: w.id,
            nome: `${w.nome} ${w.cognome}`,
            confermato: false,
            dataConferma: null,
            firmaDigitale: null
          }));
        } else {
          // Per mansioni
          if (newDocument.destinatari.mansioni.length > 0) {
            const byMansione = companyWorkers.filter(w => 
              newDocument.destinatari.mansioni.includes(w.mansione)
            );
            recipients = [...recipients, ...byMansione.map(w => ({
              id: w.id,
              nome: `${w.nome} ${w.cognome}`,
              confermato: false,
              dataConferma: null,
              firmaDigitale: null
            }))];
          }
          
          // Per ruoli
          if (newDocument.destinatari.ruoli.length > 0) {
            const byRuolo = companyWorkers.filter(w => 
              w.ruoli && w.ruoli.some(r => newDocument.destinatari.ruoli.includes(r))
            );
            recipients = [...recipients, ...byRuolo.map(w => ({
              id: w.id,
              nome: `${w.nome} ${w.cognome}`,
              confermato: false,
              dataConferma: null,
              firmaDigitale: null
            }))];
          }
          
          // Per persone specifiche
          if (newDocument.destinatari.personeSpecifiche.length > 0) {
            const byPersone = companyWorkers.filter(w => 
              newDocument.destinatari.personeSpecifiche.includes(w.id)
            );
            recipients = [...recipients, ...byPersone.map(w => ({
              id: w.id,
              nome: `${w.nome} ${w.cognome}`,
              confermato: false,
              dataConferma: null,
              firmaDigitale: null
            }))];
          }
          
          // Per ruoli organizzativi (Figure Aziendali)
          if (newDocument.destinatari.ruoliOrganizzativi && newDocument.destinatari.ruoliOrganizzativi.length > 0) {
            const company = companies.find(c => c.id === sourceCompanyId);
            if (company) {
              newDocument.destinatari.ruoliOrganizzativi.forEach(ruolo => {
                if (ruolo === 'Datore di Lavoro' && company.datoreLavoro && company.datoreLavoro.email) {
                  recipients.push({
                    id: `org-datore-${company.id}`,
                    nome: `${company.datoreLavoro.nome} ${company.datoreLavoro.cognome} (Datore di Lavoro)`,
                    email: company.datoreLavoro.email,
                    confermato: false,
                    dataConferma: null,
                    firmaDigitale: null,
                    tipo: 'organizzativo'
                  });
                } else if (ruolo === 'RSPP - Responsabile Servizio Prevenzione e Protezione' && company.rspp && company.rspp.email) {
                  recipients.push({
                    id: `org-rspp-${company.id}`,
                    nome: `${company.rspp.nome} ${company.rspp.cognome} (RSPP)`,
                    email: company.rspp.email,
                    confermato: false,
                    dataConferma: null,
                    firmaDigitale: null,
                    tipo: 'organizzativo'
                  });
                } else if (ruolo === 'Medico Competente' && company.medicoCompetente && company.medicoCompetente.email) {
                  recipients.push({
                    id: `org-medico-${company.id}`,
                    nome: `${company.medicoCompetente.nome} ${company.medicoCompetente.cognome} (Medico Competente)`,
                    email: company.medicoCompetente.email,
                    confermato: false,
                    dataConferma: null,
                    firmaDigitale: null,
                    tipo: 'organizzativo'
                  });
                }
              });
            }
          }
          
          // Rimuovi duplicati
          const workerRecipients = recipients.filter(r => !r.tipo || r.tipo !== 'organizzativo');
          const orgRecipients = recipients.filter(r => r.tipo === 'organizzativo');
          const uniqueWorkers = workerRecipients.filter((r, index, self) => 
            index === self.findIndex(t => t.id === r.id)
          );
          recipients = [...uniqueWorkers, ...orgRecipients];
        }
        
        return recipients;
      };

      // ===== MODIFICA DOCUMENTO ESISTENTE =====
      if (editingDocument) {
        let recipients = [];
        
        if (newDocument.stato === 'Condiviso') {
          recipients = calculateDocRecipients(editingDocument.companyId, editingDocument.companyId);
        }

        // Mostra anteprima email se condiviso con recipients
        if (newDocument.stato === 'Condiviso' && recipients.length > 0) {
          const emailList = recipients.map(r => {
            if (r.tipo === 'organizzativo' && r.email) return `• ${r.nome} (${r.email})`;
            const w = workers.find(w => w.id === r.id);
            return `• ${r.nome} (${w ? w.email : 'N/A'})`;
          }).join('\n');
          
          const emailPreview = `📧 Verranno inviate ${recipients.length} email a:\n\n${emailList}\n\n📌 Documento: ${newDocument.descrizione}\n\nConfermi?`;
          
          if (!window.confirm(emailPreview)) return;
        }

        // 🆕 Upload file su Storage se presente
        let storagePath = editingDocument.storagePath || null;
        if (documentFileObject) {
          const filePath = `companies/${editingDocument.companyId}/documents/${Date.now()}_${documentFileObject.name}`;
          await storageService.upload(filePath, documentFileObject);
          storagePath = filePath;
        }

        // Salva nel database
        await documentsService.update(editingDocument.id, {
          ...newDocument,
          companyId: editingDocument.companyId,
          file: documentFileObject ? documentFileObject.name : newDocument.file,
          storagePath: storagePath,
          recipients: recipients
        });

        // Condividi con lavoratori se necessario
        if (newDocument.stato === 'Condiviso' && recipients.length > 0) {
          const workerIds = recipients.filter(r => !r.tipo || r.tipo !== 'organizzativo').map(r => r.id);
          if (workerIds.length > 0) {
            await documentsService.shareWithWorkers(editingDocument.id, workerIds);
          }
          await auditLogService.log('SHARE', 'document', editingDocument.id, {
            userEmail: currentUser?.email,
            newValues: { descrizione: newDocument.descrizione, tipo: newDocument.tipo, azione: 'modifica e condivisione', companyId: editingDocument.companyId, companyName: companies.find(c => c.id === editingDocument.companyId)?.ragioneSociale }
          });

          // 📧 Attiva modal conferma invio email ai destinatari
          const editDocCompany = companies.find(c => c.id === editingDocument.companyId);
          const editEmailList = recipients.map(r => {
            if (r.tipo === 'organizzativo' && r.email) return { workerId: r.id, name: r.nome, email: r.email };
            const w = workers.find(w => w.id === r.id);
            return { workerId: r.id, name: r.nome, email: w ? w.email : 'N/A' };
          }).filter(e => e.email !== 'N/A');
          if (editEmailList.length > 0) {
            setPendingEmailData({
              document: { ...newDocument, id: editingDocument.id },
              company: editDocCompany,
              recipients: editEmailList,
              timestamp: new Date().toISOString()
            });
            setShowEmailModal(true);
          }

          alert('✅ Documento modificato e condiviso con successo!');
        } else {
          await auditLogService.log('UPDATE', 'document', editingDocument.id, {
            userEmail: currentUser?.email,
            newValues: { descrizione: newDocument.descrizione, tipo: newDocument.tipo, companyId: editingDocument.companyId, companyName: companies.find(c => c.id === editingDocument.companyId)?.ragioneSociale }
          });
          alert('Documento modificato con successo!');
        }

      // ===== NUOVO DOCUMENTO =====
      } else {
        let recipients = [];
        
        if (newDocument.stato === 'Condiviso') {
          recipients = calculateDocRecipients(docCompanyId, docCompanyId);
        }

        // Mostra anteprima email se condiviso con recipients
        if (newDocument.stato === 'Condiviso' && recipients.length > 0) {
          const emailList = recipients.map(r => {
            if (r.tipo === 'organizzativo' && r.email) return `• ${r.nome} (${r.email})`;
            const w = workers.find(w => w.id === r.id);
            return `• ${r.nome} (${w ? w.email : 'N/A'})`;
          }).join('\n');
          
          const emailPreview = `📧 Verranno inviate ${recipients.length} email a:\n\n${emailList}\n\n📌 Documento: ${newDocument.descrizione}\n\nConfermi?`;
          
          if (!window.confirm(emailPreview)) return;
        }

        // 🆕 Upload file su Storage se presente
        let storagePath = null;
        let fileName = newDocument.file || `${newDocument.tipo}_${Date.now()}.pdf`;
        if (documentFileObject) {
          const filePath = `companies/${docCompanyId}/documents/${Date.now()}_${documentFileObject.name}`;
          await storageService.upload(filePath, documentFileObject);
          storagePath = filePath;
          fileName = documentFileObject.name;
        }

        // 🔐 FASE 6.1: Genera hash SHA-256 alla creazione
        const dataCreazione = new Date().toISOString();
        const docData = {
          companyId: docCompanyId,
          tipo: newDocument.tipo,
          descrizione: newDocument.descrizione,
          dataRevisione: newDocument.dataRevisione,
          dataScadenza: newDocument.dataScadenza || null,
          file: fileName,
          storagePath: storagePath,
          stato: newDocument.stato,
          destinatari: newDocument.destinatari,
          recipients: recipients
        };
        const hashInput = { ...docData, dataCreazione: dataCreazione };
        const hashContent = buildDocumentHashContent(hashInput);
        const sha256Hash = await generateSHA256Hash(hashContent);

        // Salva nel database con hash
        const createdDoc = await documentsService.create({
          ...docData,
          hashSha256: sha256Hash
        });

        // Condividi con lavoratori se necessario
        if (newDocument.stato === 'Condiviso' && recipients.length > 0 && createdDoc) {
          const workerIds = recipients.filter(r => !r.tipo || r.tipo !== 'organizzativo').map(r => r.id);
          if (workerIds.length > 0) {
            await documentsService.shareWithWorkers(createdDoc.id, workerIds);
          }
          await auditLogService.log('SHARE', 'document', createdDoc.id, {
            userEmail: currentUser?.email,
            newValues: { descrizione: newDocument.descrizione, tipo: newDocument.tipo, destinatari: workerIds.length, hashSha256: sha256Hash, companyId: docCompanyId, companyName: companies.find(c => c.id === docCompanyId)?.ragioneSociale }
          });

          // 📧 Attiva modal conferma invio email ai destinatari
          const docCompany = companies.find(c => c.id === docCompanyId);
          const emailList = recipients.map(r => {
            if (r.tipo === 'organizzativo' && r.email) return { workerId: r.id, name: r.nome, email: r.email };
            const w = workers.find(w => w.id === r.id);
            return { workerId: r.id, name: r.nome, email: w ? w.email : 'N/A' };
          }).filter(e => e.email !== 'N/A');
          if (emailList.length > 0) {
            setPendingEmailData({
              document: { ...docData, id: createdDoc.id },
              company: docCompany,
              recipients: emailList,
              timestamp: new Date().toISOString()
            });
            setShowEmailModal(true);
          }

          alert('✅ Documento caricato e condiviso con successo!');
        } else {
          await auditLogService.log('CREATE', 'document', createdDoc?.id, {
            userEmail: currentUser?.email,
            newValues: { descrizione: newDocument.descrizione, tipo: newDocument.tipo, stato: newDocument.stato, hashSha256: sha256Hash, companyId: docCompanyId, companyName: companies.find(c => c.id === docCompanyId)?.ragioneSociale }
          });
          alert('Documento caricato con successo!');
        }
      }

      await reloadData();
      closeModal();
    } catch (err) {
      console.error('Errore salvataggio documento:', err);
      alert('Errore: ' + err.message);
    }
  };

  // 🔒 FASE 6.4: Soft Delete - Archiviazione al posto di eliminazione fisica
  const handleDeleteDocument = async (docId) => {
    const docToArchive = documents.find(d => d.id === docId);
    
    if (window.confirm(`Confermi l'archiviazione del documento "${docToArchive?.descrizione || docToArchive?.tipo}"?\n\n⚠️ Per tracciabilità legale (D.Lgs. 81/2008), il documento viene archiviato e conservato nel database.\nNon sarà più visibile nelle liste ma resterà consultabile nello storico.`)) {
      try {
        await documentsService.update(docId, {
          ...docToArchive,
          stato: 'Archiviato',
          isArchived: true
        });
        await auditLogService.log('UPDATE', 'document', docId, {
          userEmail: currentUser?.email,
          newValues: { azione: 'archiviazione', descrizione: docToArchive?.descrizione, tipo: docToArchive?.tipo, companyId: docToArchive?.companyId, companyName: companies.find(c => c.id === docToArchive?.companyId)?.ragioneSociale }
        });
        await reloadData();
        alert('✅ Documento archiviato con successo.\n\nI dati sono conservati per tracciabilità legale.');
      } catch (err) {
        console.error('Errore archiviazione documento:', err);
        alert('Errore: ' + err.message);
      }
    }
  };

  // 🆕 FUNZIONE CARICA NUOVA REVISIONE - VERSIONE DATABASE
  const handleUploadRevision = async () => {
    if (!newRevision.descrizione || !newRevision.dataRevisione) {
      alert('Compila i campi obbligatori: Descrizione e Data Revisione');
      return;
    }

    try {
      const oldDoc = revisionDocument;
      const newVersion = (oldDoc.version || 1) + 1;

      // 1. Archivia versione precedente nel database
      await documentsService.update(oldDoc.id, {
        ...oldDoc,
        stato: 'Archiviato',
        isArchived: true
      });

      // 2. Upload file revisione su Storage se presente
      let storagePath = null;
      let fileName = newRevision.file || `${oldDoc.tipo}_v${newVersion}_${Date.now()}.pdf`;
      if (revisionFileObject) {
        const filePath = `companies/${oldDoc.companyId}/documents/${Date.now()}_${revisionFileObject.name}`;
        await storageService.upload(filePath, revisionFileObject);
        storagePath = filePath;
        fileName = revisionFileObject.name;
      }

      // 3. Crea nuova versione nel database
      const createdDoc = await documentsService.create({
        companyId: oldDoc.companyId,
        tipo: oldDoc.tipo,
        descrizione: newRevision.descrizione,
        dataRevisione: newRevision.dataRevisione,
        dataScadenza: newRevision.dataScadenza || null,
        file: fileName,
        storagePath: storagePath,
        stato: newRevision.autoReshare ? 'Condiviso' : 'Privato',
        version: newVersion,
        replacedDocumentId: oldDoc.id,
        destinatari: newRevision.autoReshare ? oldDoc.destinatari : {
          mansioni: [],
          ruoli: [],
          ruoliOrganizzativi: [],
          tuttiLavoratori: false,
          personeSpecifiche: []
        },
        recipients: []
      });

      // 4. Se autoReshare, condividi con gli stessi destinatari
      if (newRevision.autoReshare && oldDoc.recipients && oldDoc.recipients.length > 0 && createdDoc) {
        const workerIds = oldDoc.recipients
          .filter(r => !r.tipo || r.tipo !== 'organizzativo')
          .map(r => r.id);
        if (workerIds.length > 0) {
          await documentsService.shareWithWorkers(createdDoc.id, workerIds);
        }
      }

      await reloadData();
      
      // Chiudi modal revisione
      setShowRevisionModal(false);
      setRevisionDocument(null);
      
      // 🆕 Se autoReshare = false, apri modal modifica per scegliere nuovi destinatari
      if (!newRevision.autoReshare && oldDoc.stato === 'Condiviso') {
        setTimeout(() => {
          setNewDocument({
            tipo: oldDoc.tipo,
            descrizione: newRevision.descrizione,
            dataRevisione: newRevision.dataRevisione,
            dataScadenza: newRevision.dataScadenza || '',
            file: newRevision.file || `${oldDoc.tipo}_v${newVersion}_${Date.now()}.pdf`,
            stato: 'Condiviso',
            destinatari: {
              mansioni: [],
              ruoli: [],
              ruoliOrganizzativi: [],
              tuttiLavoratori: false,
              personeSpecifiche: []
            }
          });
          // Usa il doc appena creato per l'editing
          if (createdDoc) {
            setEditingDocument(createdDoc);
          }
          openModal('editDocument');
        }, 300);
        alert(`✅ Revisione v${newVersion} caricata con successo!\n\n📤 Ora puoi scegliere i nuovi destinatari per la condivisione.`);
      } else {
        alert(`✅ Revisione v${newVersion} caricata con successo!\n\n${newRevision.autoReshare ? '📧 Documento ricondiviso con i destinatari originali' : ''}`);
      }
      
      // Reset form
      setNewRevision({
        descrizione: '',
        dataRevisione: '',
        dataScadenza: '',
        file: null,
        autoReshare: true
      });
      setRevisionFileObject(null);
    } catch (err) {
      console.error('Errore upload revisione:', err);
      alert('Errore: ' + err.message);
    }
  };

  // 🆕 FUNZIONE MODIFICA DOCUMENTO
  const handleEditDocument = (doc) => {
    setNewDocument({
      tipo: doc.tipo,
      descrizione: doc.descrizione,
      dataRevisione: doc.dataRevisione,
      dataScadenza: doc.dataScadenza || '',
      file: doc.file,
      stato: doc.stato || 'Privato',
      destinatari: doc.destinatari || {
        mansioni: [],
        ruoli: [],
        ruoliOrganizzativi: [],
        tuttiLavoratori: false,
        personeSpecifiche: []
      }
    });
    setEditingDocument(doc);
    openModal('editDocument');
  };

  // 🆕 FUNZIONE DOWNLOAD DOCUMENTO
  const handleDownloadDocument = async (doc) => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    
    try {
      if (doc.storagePath) {
        // File reale su Supabase Storage
        const url = await storageService.getSignedUrl(doc.storagePath);
        window.open(url, '_blank');
      } else {
        // Fallback per documenti vecchi senza file reale
        alert(`📥 Documento: ${doc.descrizione}\nFile: ${doc.file}\n\n⚠️ Questo documento non ha un file caricato su Storage`);
      }
    } catch (err) {
      console.error('Errore download documento:', err);
      alert('Errore nel download: ' + err.message);
    }
  };

  // 🆕 FUNZIONI TEMPLATE DOCUMENTI (profilo AZIENDA)
  const handleAddDocumento = () => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    if (newDocumento.trim()) {
      setDocumentiTemplate([...documentiTemplate, newDocumento.trim()]);
      setNewDocumento('');
      closeModal();
      alert('Template documento aggiunto con successo!');
    }
  };

  const handleDeleteDocumento = (documento) => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    if (window.confirm(`Sei sicuro di voler eliminare il template "${documento}"?`)) {
      setDocumentiTemplate(documentiTemplate.filter(d => d !== documento));
      alert('Template documento eliminato con successo!');
    }
  };

  
  // ============================================================================
  // 🎨 HELPER PDF - Layout professionale per tutti gli export
  // ============================================================================
  const createPdfHelper = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    
    // Colori corporate SafeTracker
    const colors = {
      primary: [37, 99, 235],      // Blu SafeTracker
      secondary: [100, 116, 139],  // Grigio
      success: [34, 197, 94],      // Verde
      warning: [245, 158, 11],     // Arancione
      danger: [239, 68, 68],       // Rosso
      dark: [30, 41, 59],          // Grigio scuro
      light: [248, 250, 252]       // Grigio chiaro
    };
    
    // Header comune
    const addHeader = (title, subtitle = '') => {
      // Barra blu in alto
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Titolo
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('SAFETRACKER', margin, 12);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Sistema Tracciabilità Sicurezza - D.Lgs. 81/2008', margin, 19);
      
      // Titolo documento
      doc.setTextColor(...colors.dark);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin, 38);
      
      if (subtitle) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...colors.secondary);
        doc.text(subtitle, margin, 45);
      }
      
      return subtitle ? 52 : 45;
    };
    
    // Footer comune
    const addFooter = () => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Linea separatrice
        doc.setDrawColor(...colors.secondary);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        
        // Testo footer
        doc.setFontSize(8);
        doc.setTextColor(...colors.secondary);
        doc.text(
          `SafeTracker - D.Lgs. 81/2008 | Generato: ${formatDate(new Date().toISOString())}`,
          margin,
          pageHeight - 8
        );
        doc.text(
          `Pagina ${i} di ${pageCount}`,
          pageWidth - margin - 25,
          pageHeight - 8
        );
      }
    };
    
    // Sezione con titolo
    const addSection = (title, y) => {
      doc.setFillColor(...colors.light);
      doc.rect(margin, y, pageWidth - 2 * margin, 8, 'F');
      doc.setDrawColor(...colors.primary);
      doc.setLineWidth(0.5);
      doc.line(margin, y, margin, y + 8);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.primary);
      doc.text(title, margin + 3, y + 5.5);
      
      return y + 12;
    };
    
    // Box statistiche
    const addStatBox = (label, value, x, y, width = 40, color = colors.primary) => {
      doc.setFillColor(...color);
      doc.roundedRect(x, y, width, 18, 2, 2, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(String(value), x + width/2, y + 8, { align: 'center' });
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(label, x + width/2, y + 14, { align: 'center' });
      
      return y + 22;
    };
    
    return {
      doc,
      pageWidth,
      pageHeight,
      margin,
      colors,
      addHeader,
      addFooter,
      addSection,
      addStatBox
    };
  };

  const handleExportReport = (comm) => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    
    const stats = calculateCommunicationStats(comm);
    const companyName = getCompanyName(comm.aziendaId);
    const pdf = createPdfHelper();
    const { doc, margin, colors, pageWidth } = pdf;
    
    // Header
    let y = pdf.addHeader(
      'REPORT TRACCIABILITÀ COMUNICAZIONE',
      `${comm.titolo} - ${companyName}`
    );
    
    // Statistiche in box
    y += 5;
    const boxWidth = (pageWidth - 2 * margin - 15) / 4;
    pdf.addStatBox('DESTINATARI', stats.destinatari, margin, y, boxWidth, colors.primary);
    pdf.addStatBox('LETTI', stats.letti, margin + boxWidth + 5, y, boxWidth, colors.success);
    pdf.addStatBox('NON LETTI', stats.nonLetti, margin + 2 * (boxWidth + 5), y, boxWidth, colors.danger);
    const percentage = stats.destinatari > 0 ? ((stats.letti / stats.destinatari) * 100).toFixed(0) : 0;
    pdf.addStatBox('COMPLETATO', `${percentage}%`, margin + 3 * (boxWidth + 5), y, boxWidth, colors.secondary);
    y += 25;
    
    // Sezione Dettagli
    y = pdf.addSection('DETTAGLI COMUNICAZIONE', y);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.dark);
    
    const details = [
      ['Titolo:', comm.titolo],
      ['Azienda:', companyName],
      ['Tipologia:', comm.tipologia],
      ['Data Invio:', formatDate(comm.dataInvio)],
      ['Scadenza:', comm.dataScadenza ? formatDateOnly(comm.dataScadenza) : 'Nessuna'],
      ['Stato:', comm.stato]
    ];
    
    details.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), margin + 30, y);
      y += 6;
    });
    
    y += 5;
    
    // Sezione Destinatari
    y = pdf.addSection('ELENCO DESTINATARI', y);
    
    if (comm.recipients && comm.recipients.length > 0) {
      const tableData = comm.recipients.map((recipient, idx) => [
        idx + 1,
        recipient.nome,
        recipient.letto ? 'SI - LETTO' : 'NO - NON LETTO',
        recipient.letto ? formatDate(recipient.dataLettura) : '-',
        recipient.firmaDigitale || '-',
        recipient.solleciti ? recipient.solleciti.length : 0
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['#', 'Nome', 'Stato', 'Data Lettura', 'Firma Digitale', 'Solleciti']],
        body: tableData,
        theme: 'striped',
        headStyles: { 
          fillColor: colors.primary,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 10 },
          2: { cellWidth: 25 },
          5: { cellWidth: 18, halign: 'center' }
        },
        margin: { left: margin, right: margin }
      });
      
      y = doc.lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(10);
      doc.text('Nessun destinatario', margin, y);
      y += 10;
    }
    
    // Sezione Contenuto
    if (y < 230) {
      y = pdf.addSection('CONTENUTO', y);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const splitContent = doc.splitTextToSize(comm.contenuto || '', pageWidth - 2 * margin);
      doc.text(splitContent, margin, y);
      y += splitContent.length * 5 + 10;
    }
    
    // 🔐 FASE 6.1: Sezione Certificato di Tracciabilità
    if (comm.hashSha256) {
      // Controlla se serve nuova pagina
      if (y > 240) {
        doc.addPage();
        y = 20;
      }
      
      y = pdf.addSection('CERTIFICATO DI TRACCIABILITÀ', y);
      
      // Box sfondo verde chiaro
      const certBoxHeight = 35;
      doc.setFillColor(236, 253, 245); // emerald-50
      doc.setDrawColor(167, 243, 208); // emerald-300
      doc.roundedRect(margin, y - 5, pageWidth - 2 * margin, certBoxHeight, 2, 2, 'FD');
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(6, 95, 70); // emerald-800
      doc.text('Hash SHA-256:', margin + 3, y + 3);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(comm.hashSha256, margin + 3, y + 9);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Timestamp Certificazione (UTC):', margin + 3, y + 17);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDateUTC(comm.dataInvio), margin + 55, y + 17);
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(5, 150, 105); // emerald-600
      doc.text('Questo hash certifica che il contenuto non e stato alterato dalla data di creazione.', margin + 3, y + 25);
      
      doc.setTextColor(0, 0, 0); // reset colore testo
    }
    
    // Footer
    pdf.addFooter();
    
    // Salva
    doc.save(`Report_${comm.titolo.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    alert('✅ Report PDF generato e scaricato con successo!');
  };
  // 📊 EXPORT ELENCO DOCUMENTI AZIENDALI
  // ⚠️ VERSIONE TEST: Export in formato TXT
  // 🚀 PRODUCTION: Implementare export PDF professionale con jsPDF
  //    - Tabelle formattate con bordi e colori
  //    - Grafici statistiche (torta/barre)
  //    - Logo aziendale nell'header
  //    - Firma digitale e timestamp certificato
  //    - Impaginazione professionale con intestazione/piè di pagina
  const handleExportDocumentsList = (filteredDocs, profileType = 'AZIENDA') => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    
    const pdf = createPdfHelper();
    const { doc, margin, colors, pageWidth } = pdf;
    
    // Calcola statistiche
    const total = filteredDocs.length;
    const scaduti = filteredDocs.filter(d => {
      if (!d.dataScadenza) return false;
      return Math.ceil((new Date(d.dataScadenza) - new Date()) / (1000 * 60 * 60 * 24)) < 0;
    }).length;
    const inScadenza = filteredDocs.filter(d => {
      if (!d.dataScadenza) return false;
      const days = Math.ceil((new Date(d.dataScadenza) - new Date()) / (1000 * 60 * 60 * 24));
      return days >= 0 && days <= 30;
    }).length;
    const privati = filteredDocs.filter(d => d.stato === 'Privato').length;
    const condivisi = filteredDocs.filter(d => d.stato === 'Condiviso').length;
    
    // Determina titolo in base al profilo
    const companyTitle = profileType === 'AZIENDA' 
      ? companies.find(c => String(c.id) === String(currentUser.companyId))?.ragioneSociale || ''
      : selectedCompanyForDocuments === 'all' 
        ? 'Tutte le Aziende' 
        : companies.find(c => c.id === selectedCompanyForDocuments)?.ragioneSociale || '';
    
    // Header
    let y = pdf.addHeader(
      'ELENCO DOCUMENTI AZIENDALI',
      companyTitle
    );
    
    // Statistiche in box
    y += 5;
    const boxWidth = (pageWidth - 2 * margin - 20) / 5;
    pdf.addStatBox('TOTALE', total, margin, y, boxWidth, colors.primary);
    pdf.addStatBox('SCADUTI', scaduti, margin + boxWidth + 5, y, boxWidth, colors.danger);
    pdf.addStatBox('IN SCADENZA', inScadenza, margin + 2 * (boxWidth + 5), y, boxWidth, colors.warning);
    pdf.addStatBox('PRIVATI', privati, margin + 3 * (boxWidth + 5), y, boxWidth, colors.secondary);
    pdf.addStatBox('CONDIVISI', condivisi, margin + 4 * (boxWidth + 5), y, boxWidth, colors.success);
    y += 28;
    
    // Tabella documenti
    y = pdf.addSection('ELENCO DOCUMENTI', y);
    
    if (filteredDocs.length > 0) {
      const tableData = filteredDocs.map((docItem, idx) => {
        const company = companies.find(c => c.id === docItem.companyId);
        const daysToExpiry = docItem.dataScadenza 
          ? Math.ceil((new Date(docItem.dataScadenza) - new Date()) / (1000 * 60 * 60 * 24))
          : null;
        
        let statoScadenza = '-';
        if (daysToExpiry !== null) {
          if (daysToExpiry < 0) statoScadenza = '!! SCADUTO';
          else if (daysToExpiry <= 30) statoScadenza = `-> ${daysToExpiry}gg`;
          else statoScadenza = `OK ${daysToExpiry}gg`;
        }
        
        let conferme = '-';
        if (docItem.stato === 'Condiviso' && docItem.recipients) {
          const confirmed = docItem.recipients.filter(r => r.letto).length;
          conferme = `${confirmed}/${docItem.recipients.length}`;
        }
        
        return [
          idx + 1,
          docItem.descrizione.substring(0, 30) + (docItem.descrizione.length > 30 ? '...' : ''),
          docItem.tipo,
          profileType === 'ADMIN' ? (company?.ragioneSociale?.substring(0, 15) || '-') : '',
          formatDateOnly(docItem.dataRevisione),
          statoScadenza,
          docItem.stato,
          conferme
        ].filter((_, i) => profileType === 'ADMIN' || i !== 3);
      });
      
      const headers = profileType === 'ADMIN'
        ? [['#', 'Descrizione', 'Tipo', 'Azienda', 'Revisione', 'Scadenza', 'Stato', 'Conferme']]
        : [['#', 'Descrizione', 'Tipo', 'Revisione', 'Scadenza', 'Stato', 'Conferme']];
      
      autoTable(doc, {
        startY: y,
        head: headers,
        body: tableData,
        theme: 'striped',
        headStyles: { 
          fillColor: colors.primary,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8
        },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 8 },
          1: { cellWidth: 45 }
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => {
          if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
            doc.setFillColor(...colors.primary);
            doc.rect(0, 0, pageWidth, 15, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.text('SAFETRACKER - Elenco Documenti (continua)', margin, 10);
          }
        }
      });
    } else {
      doc.setFontSize(10);
      doc.text('Nessun documento trovato con i filtri selezionati.', margin, y);
    }
    
    // Footer
    pdf.addFooter();
    
    // Salva
    const fileName = profileType === 'ADMIN'
      ? `Documenti_${selectedCompanyForDocuments === 'all' ? 'Tutte_Aziende' : companies.find(c => c.id === selectedCompanyForDocuments)?.ragioneSociale.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
      : `Documenti_${companies.find(c => String(c.id) === String(currentUser.companyId))?.ragioneSociale.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    doc.save(fileName);
    alert(`✅ Elenco documenti PDF esportato!\n\n📊 ${total} documenti\n📁 ${fileName}`);
  };

  const handleExportPersonaleAziendale = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (!company) {
      alert('Azienda non trovata!');
      return;
    }
    
    const companyWorkers = workers.filter(w => String(w.companyId) === String(companyId));
    const pdf = createPdfHelper();
    const { doc, margin, colors, pageWidth } = pdf;
    
    // Calcola statistiche
    const totalWorkers = companyWorkers.length;
    const activeWorkers = companyWorkers.filter(w => !w.dataFine).length;
    const inactiveWorkers = companyWorkers.filter(w => w.dataFine).length;
    
    // Statistiche per mansione
    const mansioniCount = {};
    companyWorkers.forEach(w => {
      mansioniCount[w.mansione] = (mansioniCount[w.mansione] || 0) + 1;
    });
    
    // Header
    let y = pdf.addHeader(
      'ELENCO PERSONALE AZIENDALE',
      `${company.ragioneSociale} - P.IVA: ${company.partitaIva}`
    );
    
    // Statistiche
    y += 5;
    const boxWidth = (pageWidth - 2 * margin - 10) / 3;
    pdf.addStatBox('TOTALE', totalWorkers, margin, y, boxWidth, colors.primary);
    pdf.addStatBox('ATTIVI', activeWorkers, margin + boxWidth + 5, y, boxWidth, colors.success);
    pdf.addStatBox('CESSATI', inactiveWorkers, margin + 2 * (boxWidth + 5), y, boxWidth, colors.danger);
    y += 28;
    
    // Sezione Figure Aziendali
    y = pdf.addSection('FIGURE AZIENDALI (D.Lgs. 81/2008)', y);
    
    const figureData = [
      ['Datore di Lavoro', `${company.datoreLavoro.nome} ${company.datoreLavoro.cognome}`, company.datoreLavoro.email, '-'],
      ['RSPP', `${company.rspp.nome} ${company.rspp.cognome}`, company.rspp.email, company.rspp.pec],
      ['Medico Competente', `${company.medicoCompetente.nome} ${company.medicoCompetente.cognome}`, company.medicoCompetente.email, company.medicoCompetente.pec]
    ];
    
    autoTable(doc, {
      startY: y,
      head: [['Ruolo', 'Nome e Cognome', 'Email', 'PEC']],
      body: figureData,
      theme: 'grid',
      headStyles: { 
        fillColor: colors.primary,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 }
      },
      margin: { left: margin, right: margin }
    });
    
    y = doc.lastAutoTable.finalY + 10;
    
    // Sezione Lavoratori
    y = pdf.addSection(`ELENCO LAVORATORI (${totalWorkers})`, y);
    
    if (companyWorkers.length > 0) {
      const sortedWorkers = [...companyWorkers].sort((a, b) => a.cognome.localeCompare(b.cognome));
      
      const workersData = sortedWorkers.map((worker, idx) => [
        idx + 1,
        `${worker.cognome} ${worker.nome}`,
        worker.codiceFiscale,
        worker.mansione,
        worker.ruoli.length > 0 ? worker.ruoli.join(', ').substring(0, 25) : '-',
        formatDateOnly(worker.dataAssunzione),
        worker.dataFine ? 'CESSATO' : 'ATTIVO'
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['#', 'Nome e Cognome', 'Codice Fiscale', 'Mansione', 'Ruoli', 'Assunzione', 'Stato']],
        body: workersData,
        theme: 'striped',
        headStyles: { 
          fillColor: colors.primary,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8
        },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 8 },
          2: { cellWidth: 32 },
          6: { cellWidth: 18 }
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => {
          if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
            doc.setFillColor(...colors.primary);
            doc.rect(0, 0, pageWidth, 15, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.text(`SAFETRACKER - ${company.ragioneSociale} - Personale (continua)`, margin, 10);
          }
        }
      });
      
      y = doc.lastAutoTable.finalY + 10;
    }
    
    // Riepilogo per mansione (se c'è spazio)
    if (y < 200 && Object.keys(mansioniCount).length > 0) {
      y = pdf.addSection('RIEPILOGO PER MANSIONE', y);
      
      const mansioneData = Object.entries(mansioniCount)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([mansione, count]) => [mansione, count]);
      
      autoTable(doc, {
        startY: y,
        head: [['Mansione', 'N° Lavoratori']],
        body: mansioneData,
        theme: 'striped',
        headStyles: { fillColor: colors.secondary, textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 1: { halign: 'center', cellWidth: 30 } },
        margin: { left: margin, right: margin }
      });
    }
    
    // Footer
    pdf.addFooter();
    
    // Salva
    const fileName = `Personale_${company.ragioneSociale.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    alert(`✅ Elenco personale PDF esportato!\n\n👥 ${totalWorkers} lavoratori\n🏢 3 figure aziendali\n📁 ${fileName}`);
  };

  // 👥 EXPORT ELENCO LAVORATORI
  const handleExportElencoLavoratori = (companyId) => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    
    const company = companies.find(c => c.id === companyId);
    if (!company) {
      alert('Azienda non trovata!');
      return;
    }
    
    // 🆕 Applica filtro stato
    let companyWorkers = workers.filter(w => String(w.companyId) === String(companyId));
    companyWorkers = getFilteredWorkersByStatus(companyWorkers);
    
    // 🆕 Label filtro per report
    const filterLabel = workerStatusFilter === 'attivi' ? 'Solo Attivi' :
                       workerStatusFilter === 'dimessi' ? 'Solo Dimessi' : 'Tutti';
    
    const pdf = createPdfHelper();
    const { doc, margin, colors, pageWidth } = pdf;
    
    // Calcola statistiche
    const totalWorkers = companyWorkers.length;
    const activeWorkers = companyWorkers.filter(w => isLavoratoreAttivo(w)).length;
    const inactiveWorkers = companyWorkers.filter(w => !isLavoratoreAttivo(w)).length;
    
    // Statistiche per mansione
    const mansioniCount = {};
    companyWorkers.forEach(w => {
      mansioniCount[w.mansione] = (mansioniCount[w.mansione] || 0) + 1;
    });
    
    // Header
    let y = pdf.addHeader(
      'ELENCO LAVORATORI',
      `${company.ragioneSociale} | Filtro: ${filterLabel}`
    );
    
    // Statistiche
    y += 5;
    const boxWidth = (pageWidth - 2 * margin - 10) / 3;
    pdf.addStatBox('TOTALE', totalWorkers, margin, y, boxWidth, colors.primary);
    pdf.addStatBox('ATTIVI', activeWorkers, margin + boxWidth + 5, y, boxWidth, colors.success);
    pdf.addStatBox('CESSATI', inactiveWorkers, margin + 2 * (boxWidth + 5), y, boxWidth, colors.danger);
    y += 28;
    
    // Tabella lavoratori
    y = pdf.addSection('ELENCO COMPLETO', y);
    
    if (companyWorkers.length > 0) {
      const sortedWorkers = [...companyWorkers].sort((a, b) => a.cognome.localeCompare(b.cognome));
      
      const workersData = sortedWorkers.map((worker, idx) => [
        idx + 1,
        `${worker.cognome} ${worker.nome}`,
        worker.mansione,
        worker.ruoli.length > 0 ? worker.ruoli.join(', ').substring(0, 30) : '-',
        worker.email,
        worker.cellulare || '-',
        worker.dataFine ? 'Cessato' : 'Attivo'
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['#', 'Nome e Cognome', 'Mansione', 'Ruoli', 'Email', 'Telefono', 'Stato']],
        body: workersData,
        theme: 'striped',
        headStyles: { 
          fillColor: colors.primary,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8
        },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 8 },
          6: { cellWidth: 15, halign: 'center' }
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => {
          if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
            doc.setFillColor(...colors.primary);
            doc.rect(0, 0, pageWidth, 15, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.text(`SAFETRACKER - ${company.ragioneSociale} - Lavoratori (continua)`, margin, 10);
          }
        }
      });
      
      y = doc.lastAutoTable.finalY + 10;
    }
    
    // Riepilogo per mansione
    if (y < 200 && Object.keys(mansioniCount).length > 0) {
      y = pdf.addSection('DISTRIBUZIONE PER MANSIONE', y);
      
      const mansioneData = Object.entries(mansioniCount)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([mansione, count]) => [mansione, count, `${((count / totalWorkers) * 100).toFixed(0)}%`]);
      
      autoTable(doc, {
        startY: y,
        head: [['Mansione', 'N°', '%']],
        body: mansioneData,
        theme: 'striped',
        headStyles: { fillColor: colors.secondary, textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 
          1: { halign: 'center', cellWidth: 15 },
          2: { halign: 'center', cellWidth: 15 }
        },
        margin: { left: margin, right: margin }
      });
    }
    
    // Footer
    pdf.addFooter();
    
    // Salva
    const fileName = `Lavoratori_${company.ragioneSociale.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    alert(`✅ Elenco lavoratori PDF esportato!\n\n👥 ${totalWorkers} lavoratori\n✅ ${activeWorkers} attivi | ❌ ${inactiveWorkers} cessati\n📁 ${fileName}`);
  };

  // 🎯 EXPORT RUOLI AZIENDALI
  const handleExportRuoliAziendali = (companyId) => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    
    const company = companies.find(c => c.id === companyId);
    if (!company) {
      alert('Azienda non trovata!');
      return;
    }
    
    const companyWorkers = workers.filter(w => String(w.companyId) === String(companyId));
    const pdf = createPdfHelper();
    const { doc, margin, colors, pageWidth } = pdf;
    
    // Calcola statistiche
    const totalWorkers = companyWorkers.length;
    const workersWithRoles = companyWorkers.filter(w => w.ruoli && w.ruoli.length > 0).length;
    const workersWithoutRoles = totalWorkers - workersWithRoles;
    
    // Raggruppa per ruolo
    const workersByRuolo = {};
    companyWorkers.forEach(w => {
      if (w.ruoli && w.ruoli.length > 0) {
        w.ruoli.forEach(r => {
          if (!workersByRuolo[r]) workersByRuolo[r] = [];
          workersByRuolo[r].push(w);
        });
      }
    });
    
    const totalRuoli = Object.keys(workersByRuolo).length;
    
    // Header
    let y = pdf.addHeader(
      'RUOLI E FIGURE AZIENDALI',
      `${company.ragioneSociale} - D.Lgs. 81/2008`
    );
    
    // Statistiche
    y += 5;
    const boxWidth = (pageWidth - 2 * margin - 15) / 4;
    pdf.addStatBox('LAVORATORI', totalWorkers, margin, y, boxWidth, colors.primary);
    pdf.addStatBox('CON RUOLI', workersWithRoles, margin + boxWidth + 5, y, boxWidth, colors.success);
    pdf.addStatBox('SENZA RUOLI', workersWithoutRoles, margin + 2 * (boxWidth + 5), y, boxWidth, colors.warning);
    pdf.addStatBox('RUOLI ATTIVI', totalRuoli, margin + 3 * (boxWidth + 5), y, boxWidth, colors.secondary);
    y += 28;
    
    // Sezione Figure Aziendali
    y = pdf.addSection('FIGURE AZIENDALI OBBLIGATORIE', y);
    
    const figureData = [
      ['DATORE DI LAVORO', `${company.datoreLavoro.nome} ${company.datoreLavoro.cognome}`, company.datoreLavoro.email, 'Art. 2 D.Lgs. 81/08'],
      ['RSPP', `${company.rspp.nome} ${company.rspp.cognome}`, company.rspp.email, 'Art. 31-35 D.Lgs. 81/08'],
      ['MEDICO COMPETENTE', `${company.medicoCompetente.nome} ${company.medicoCompetente.cognome}`, company.medicoCompetente.email, 'Art. 38-42 D.Lgs. 81/08']
    ];
    
    autoTable(doc, {
      startY: y,
      head: [['Ruolo', 'Nominativo', 'Email', 'Riferimento']],
      body: figureData,
      theme: 'grid',
      headStyles: { 
        fillColor: colors.primary,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45 },
        3: { cellWidth: 40, fontStyle: 'italic' }
      },
      margin: { left: margin, right: margin }
    });
    
    y = doc.lastAutoTable.finalY + 10;
    
    // Sezione Ruoli Lavoratori
    if (totalRuoli > 0) {
      y = pdf.addSection('RUOLI ASSEGNATI AI LAVORATORI', y);
      
      const ruoliData = [];
      Object.entries(workersByRuolo)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([ruolo, wkrs]) => {
          const sortedWorkers = [...wkrs].sort((a, b) => a.cognome.localeCompare(b.cognome));
          sortedWorkers.forEach((w, idx) => {
            ruoliData.push([
              idx === 0 ? ruolo : '',
              `${w.cognome} ${w.nome}`,
              w.mansione,
              w.email,
              w.dataFine ? 'Cessato' : 'Attivo'
            ]);
          });
        });
      
      autoTable(doc, {
        startY: y,
        head: [['Ruolo', 'Lavoratore', 'Mansione', 'Email', 'Stato']],
        body: ruoliData,
        theme: 'striped',
        headStyles: { 
          fillColor: colors.primary,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8
        },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 40 },
          4: { cellWidth: 15, halign: 'center' }
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => {
          if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
            doc.setFillColor(...colors.primary);
            doc.rect(0, 0, pageWidth, 15, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.text(`SAFETRACKER - ${company.ragioneSociale} - Ruoli (continua)`, margin, 10);
          }
        }
      });
      
      y = doc.lastAutoTable.finalY + 10;
    }
    
    // Lavoratori senza ruoli (se pochi e c'è spazio)
    const workersNoRoles = companyWorkers.filter(w => !w.ruoli || w.ruoli.length === 0);
    if (workersNoRoles.length > 0 && workersNoRoles.length <= 10 && y < 220) {
      y = pdf.addSection(`LAVORATORI SENZA RUOLI (${workersNoRoles.length})`, y);
      
      const noRolesData = workersNoRoles
        .sort((a, b) => a.cognome.localeCompare(b.cognome))
        .map(w => [`${w.cognome} ${w.nome}`, w.mansione, w.email]);
      
      autoTable(doc, {
        startY: y,
        head: [['Lavoratore', 'Mansione', 'Email']],
        body: noRolesData,
        theme: 'striped',
        headStyles: { fillColor: colors.warning, textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        margin: { left: margin, right: margin }
      });
    }
    
    // Footer
    pdf.addFooter();
    
    // Salva
    const fileName = `Ruoli_${company.ragioneSociale.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    alert(`✅ Elenco ruoli PDF esportato!\n\n🎯 ${totalRuoli} ruoli lavoratori\n🏢 3 figure aziendali\n👥 ${workersWithRoles} con ruoli | ⚠️ ${workersWithoutRoles} senza\n📁 ${fileName}`);
  };

  // 🆕 EXPORT REPORT TRACCIABILITÀ DOCUMENTO
  const handleExportDocumentReport = (docItem) => {
    // 🆕 Blocca se contratto scaduto
    if (isActionBlocked()) {
      showBlockedActionAlert();
      return;
    }
    
    const stats = calculateCommunicationStats(docItem);
    const companyName = getCompanyName(docItem.companyId);
    const pdf = createPdfHelper();
    const { doc, margin, colors, pageWidth } = pdf;
    
    // Header
    let y = pdf.addHeader(
      'REPORT TRACCIABILITÀ DOCUMENTO',
      `${docItem.descrizione} - ${companyName}`
    );
    
    // Statistiche
    y += 5;
    const boxWidth = (pageWidth - 2 * margin - 15) / 4;
    pdf.addStatBox('DESTINATARI', stats.destinatari, margin, y, boxWidth, colors.primary);
    pdf.addStatBox('CONFERMATI', stats.letti, margin + boxWidth + 5, y, boxWidth, colors.success);
    pdf.addStatBox('IN ATTESA', stats.nonLetti, margin + 2 * (boxWidth + 5), y, boxWidth, colors.warning);
    const percentage = stats.destinatari > 0 ? ((stats.letti / stats.destinatari) * 100).toFixed(0) : 0;
    pdf.addStatBox('COMPLETATO', `${percentage}%`, margin + 3 * (boxWidth + 5), y, boxWidth, colors.secondary);
    y += 28;
    
    // Dettagli documento
    y = pdf.addSection('DETTAGLI DOCUMENTO', y);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.dark);
    
    const details = [
      ['Descrizione:', docItem.descrizione],
      ['Tipo:', docItem.tipo],
      ['Azienda:', companyName],
      ['Data Caricamento:', formatDate(docItem.dataCaricamento)],
      ['Data Revisione:', formatDateOnly(docItem.dataRevisione)],
      ['Data Scadenza:', docItem.dataScadenza ? formatDateOnly(docItem.dataScadenza) : 'Nessuna'],
      ['File:', docItem.file],
      ['Stato:', docItem.stato]
    ];
    
    details.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value).substring(0, 80), margin + 35, y);
      y += 6;
    });
    
    y += 8;
    
    // Elenco destinatari
    y = pdf.addSection('ELENCO DESTINATARI', y);
    
    if (docItem.recipients && docItem.recipients.length > 0) {
      const tableData = docItem.recipients.map((recipient, idx) => [
        idx + 1,
        recipient.nome,
        recipient.letto ? 'SI - CONFERMATO' : 'NO - NON CONFERMATO',
        recipient.letto ? formatDate(recipient.dataLettura) : '-',
        recipient.firmaDigitale || '-',
        recipient.solleciti ? recipient.solleciti.length : 0
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['#', 'Nome', 'Stato', 'Data Conferma', 'Firma Digitale', 'Solleciti']],
        body: tableData,
        theme: 'striped',
        headStyles: { 
          fillColor: colors.primary,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 10 },
          2: { cellWidth: 30 },
          5: { cellWidth: 18, halign: 'center' }
        },
        margin: { left: margin, right: margin }
      });
    } else {
      doc.setFontSize(10);
      doc.text('Documento Privato - Nessun destinatario', margin, y);
    }
    
    // 🔐 FASE 6.1: Sezione Certificato di Tracciabilità
    if (docItem.hashSha256) {
      y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : y + 15;
      
      // Controlla se serve nuova pagina
      if (y > 240) {
        doc.addPage();
        y = 20;
      }
      
      y = pdf.addSection('CERTIFICATO DI TRACCIABILITÀ', y);
      
      // Box sfondo verde chiaro
      const certBoxHeight = 35;
      doc.setFillColor(236, 253, 245);
      doc.setDrawColor(167, 243, 208);
      doc.roundedRect(margin, y - 5, pageWidth - 2 * margin, certBoxHeight, 2, 2, 'FD');
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(6, 95, 70);
      doc.text('Hash SHA-256:', margin + 3, y + 3);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(docItem.hashSha256, margin + 3, y + 9);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Timestamp Certificazione (UTC):', margin + 3, y + 17);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDateUTC(docItem.dataCaricamento), margin + 55, y + 17);
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(5, 150, 105);
      doc.text('Questo hash certifica che il documento non e stato alterato dalla data di creazione.', margin + 3, y + 25);
      
      doc.setTextColor(0, 0, 0);
    }
    
    // Footer
    pdf.addFooter();
    
    // Salva
    const fileName = `Report_Documento_${docItem.descrizione.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    alert('✅ Report documento PDF generato e scaricato con successo!');
  };

  const addAtecoCode = () => {
    setNewCompany({
      ...newCompany,
      codiciAteco: [...newCompany.codiciAteco, { codice: '', descrizione: '' }]
    });
  };

  const removeAtecoCode = (index) => {
    const newCodici = newCompany.codiciAteco.filter((_, i) => i !== index);
    setNewCompany({ ...newCompany, codiciAteco: newCodici });
  };

  const getFilteredCommunications = () => {
    return communications.filter(comm => {
      const matchCompany = filterCompany === 'all' || String(comm.aziendaId) === String(filterCompany);
      const matchType = filterType === 'all' || comm.tipologia === filterType;
      
      // Filtro scadenze
      let matchExpire = true;
      if (filterExpire !== 'all') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (filterExpire === 'noexpiry') {
          matchExpire = !comm.dataScadenza;
        } else if (comm.dataScadenza) {
          const scadenza = new Date(comm.dataScadenza);
          scadenza.setHours(0, 0, 0, 0);
          const daysToExpiry = Math.ceil((scadenza - today) / (1000 * 60 * 60 * 24));
          
          if (filterExpire === 'expired') {
            matchExpire = daysToExpiry < 0;
          } else if (filterExpire === 'expiring') {
            matchExpire = daysToExpiry >= 0 && daysToExpiry <= 30;
          }
        } else {
          matchExpire = false; // Se non ha scadenza e si filtra per scaduto/in scadenza
        }
      }
      
      return matchCompany && matchType && matchExpire;
    });
  };

  // Components
  
  // 🆕 MODAL IMPORT EXCEL LAVORATORI
  const ImportExcelModal = () => {
    if (!showImportExcelModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowImportExcelModal(false)}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
          <div className="bg-orange-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Upload className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Importa Lavoratori da Excel</h2>
                <p className="text-sm text-orange-100 mt-1">Carica file per import massivo</p>
              </div>
            </div>
            <button onClick={() => setShowImportExcelModal(false)} className="p-2 hover:bg-orange-700 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Istruzioni */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-2">📋 Come funziona:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Clicca <strong>"Scarica Template"</strong> (pulsante viola in basso)</li>
                    <li><strong>Doppio click</strong> sul file .XLSX scaricato → Si apre con <strong>Excel </strong></li>
                    <li>Compila le righe con i dati dei lavoratori (vedi esempi già presenti)</li>
                    <li>Salva il file (File → Salva o Ctrl+S)</li>
                    <li>Carica il file salvato qui usando il pulsante "Seleziona file"</li>
                    <li>Verifica l'anteprima e conferma l'import</li>
                  </ol>
                  <p className="mt-3 text-xs bg-yellow-100 border border-yellow-300 rounded p-2">
                    <strong>💡 IMPORTANTE:</strong> Il template si scarica come file <strong>.XLSX</strong> (Excel nativo). Puoi anche caricare file .TSV per retrocompatibilità.
                  </p>
                </div>
              </div>
            </div>

            {/* Area upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Seleziona file compilato (.XLSX, .TSV o .TXT) *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 transition-colors">
                <label className="flex flex-col items-center justify-center p-8 cursor-pointer">
                  <Upload className="w-12 h-12 text-gray-400 mb-3" />
                  <p className="text-base text-gray-600 mb-1">
                    <span className="font-semibold text-orange-600">Clicca per selezionare</span> o trascina qui
                  </p>
                  <p className="text-xs text-gray-500">File .xlsx (Excel), .tsv, .txt, .csv</p>
                  <input
                    type="file"
                    onChange={handleExcelFileUpload}
                    accept=".xlsx,.xls,.txt,.tsv,.csv"
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Info formato */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs text-gray-700 font-semibold mb-2">📄 Formato file:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• <strong>Formato consigliato:</strong> .XLSX (Excel nativo) - il template si scarica già in questo formato</li>
                <li>• <strong>Retrocompatibilità:</strong> accetta anche .TSV, .TXT, .CSV (separati da TAB)</li>
                <li>• <strong>Date:</strong> formato gg/mm/aaaa (es. 15/03/1985)</li>
                <li>• <strong>Ruoli multipli:</strong> separati da virgola (es. Preposto,Addetto Antincendio)</li>
                <li>• <strong>Campi obbligatori:</strong> Nome, Cognome, CF, Email, Mansione, Data Assunzione</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <button 
              onClick={() => setShowImportExcelModal(false)}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
            >
              Annulla
            </button>
            <button 
              onClick={handleDownloadTemplateExcel}
              className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
            >
              <Download className="w-5 h-5" />
              Scarica Template
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 🆕 MODAL PREVIEW IMPORT EXCEL
  const ImportPreviewModal = () => {
    if (!showImportPreview) return null;

    const validCount = excelData.filter(row => row.valid).length;
    const errorCount = excelData.length - validCount;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full my-8" onClick={(e) => e.stopPropagation()}>
          <div className="bg-green-600 text-white p-6 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <Eye className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Anteprima Import</h2>
                <p className="text-sm text-green-100 mt-1">Verifica i dati prima dell'import definitivo</p>
              </div>
            </div>
            <button onClick={() => {
              setShowImportPreview(false);
              setShowImportExcelModal(false);
            }} className="p-2 hover:bg-green-700 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Statistiche */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-600 font-semibold">Totale Righe</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">{excelData.length}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-green-600 font-semibold">✅ Valide</p>
                <p className="text-3xl font-bold text-green-900 mt-1">{validCount}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm text-red-600 font-semibold">❌ Con Errori</p>
                <p className="text-3xl font-bold text-red-900 mt-1">{errorCount}</p>
              </div>
            </div>

            {/* Errori */}
            {importErrors.length > 0 && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800 flex-1">
                    <p className="font-semibold mb-2">⚠️ Righe con errori (verranno saltate):</p>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {importErrors.slice(0, 5).map((error, idx) => (
                        <div key={idx} className="bg-white p-2 rounded border border-red-200">
                          <p className="font-semibold text-xs">Riga {error.row}: {error.data.Nome} {error.data.Cognome}</p>
                          <p className="text-xs text-red-600">• {error.errors.join(' • ')}</p>
                        </div>
                      ))}
                      {importErrors.length > 5 && (
                        <p className="text-xs italic">... e altri {importErrors.length - 5} errori</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabella preview */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3">📋 Anteprima Dati (prime 10 righe valide)</h3>
              <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">#</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Nome</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Cognome</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">CF</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Mansione</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Ruoli</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excelData.filter(row => row.valid).slice(0, 10).map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 text-xs text-gray-600">{row.rowNumber}</td>
                        <td className="px-3 py-2 text-xs">{row.Nome}</td>
                        <td className="px-3 py-2 text-xs">{row.Cognome}</td>
                        <td className="px-3 py-2 text-xs font-mono">{row['Codice Fiscale']}</td>
                        <td className="px-3 py-2 text-xs">{row.Email}</td>
                        <td className="px-3 py-2 text-xs">{row.Mansione}</td>
                        <td className="px-3 py-2 text-xs">{row['Ruoli (separati da virgola)'] || '-'}</td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            ✓ Valido
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <button 
              onClick={() => {
                setShowImportPreview(false);
                setShowImportExcelModal(true);
              }}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
            >
              ← Indietro
            </button>
            <button 
              onClick={handleConfirmImport}
              disabled={validCount === 0}
              className={`flex-1 px-6 py-3 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 ${
                validCount === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <CheckCircle className="w-5 h-5" />
              Conferma Import ({validCount} lavoratori)
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // 🆕 MODAL CONFERMA DATI DOCUMENTO
  const DocumentConfirmModal = () => {
    if (!showDocumentConfirmModal || !pendingDocumentData) return null;

    const { document, company, isEdit } = pendingDocumentData;

    // Calcola preview destinatari
    const getDestinatariPreview = () => {
      if (document.stato === 'Privato') {
        return 'Nessuno (Documento Privato)';
      }

      const parts = [];
      
      if (document.destinatari.tuttiLavoratori) {
        parts.push('📢 Tutti i lavoratori');
      } else {
        if (document.destinatari.mansioni.length > 0) {
          parts.push(`👔 Mansioni: ${document.destinatari.mansioni.join(', ')}`);
        }
        if (document.destinatari.ruoli.length > 0) {
          parts.push(`🛡️ Ruoli Lavoratori: ${document.destinatari.ruoli.join(', ')}`);
        }
        if (document.destinatari.ruoliOrganizzativi && document.destinatari.ruoliOrganizzativi.length > 0) {
          parts.push(`🏢 Figure Aziendali: ${document.destinatari.ruoliOrganizzativi.join(', ')}`);
        }
        if (document.destinatari.personeSpecifiche.length > 0) {
          const nomi = document.destinatari.personeSpecifiche.map(id => {
            const worker = workers.find(w => w.id === id);
            return worker ? `${worker.nome} ${worker.cognome}` : `ID ${id}`;
          });
          parts.push(`👤 Persone: ${nomi.join(', ')}`);
        }
      }

      return parts.length > 0 ? parts.join('\n') : 'Nessun destinatario selezionato';
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={cancelDocumentConfirm}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Conferma Dati Documento</h2>
                <p className="text-sm text-blue-100 mt-1">
                  ⚠️ Verifica accuratamente - dopo il salvataggio non potrai più modificare
                </p>
              </div>
            </div>
            <button onClick={cancelDocumentConfirm} className="p-2 hover:bg-blue-800 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* Alert importante */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold mb-1">⚠️ ATTENZIONE</p>
                  <p>Una volta salvato, questo documento NON potrà più essere modificato. Verifica attentamente tutti i dati prima di procedere.</p>
                </div>
              </div>
            </div>

            {/* Dati Documento */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-blue-600" />
                Dati Documento
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Tipo Documento</p>
                    <p className="font-semibold text-gray-900">{document.tipo}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Azienda</p>
                    <p className="font-semibold text-gray-900">{company?.ragioneSociale || 'N/A'}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 mb-1">Descrizione</p>
                  <p className="font-semibold text-gray-900">{document.descrizione}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Data Revisione</p>
                    <p className="font-semibold text-gray-900">
                      {formatDateOnly(document.dataRevisione)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Data Scadenza</p>
                    <p className="font-semibold text-gray-900">
                      {document.dataScadenza 
                        ? formatDateOnly(document.dataScadenza) 
                        : 'Nessuna scadenza'}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">File</p>
                  <p className="font-semibold text-gray-900">
                    {document.file || `${document.tipo}_nuovo.pdf`}
                  </p>
                </div>
              </div>
            </div>

            {/* Stato e Condivisione */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                {document.stato === 'Condiviso' ? (
                  <>
                    <Users className="w-5 h-5 text-green-600" />
                    Stato: Condiviso con lavoratori
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 text-gray-600" />
                    Stato: Privato
                  </>
                )}
              </h3>
              <div className={`p-4 rounded-lg border ${
                document.stato === 'Condiviso' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
              }`}>
                <div>
                  <p className="text-xs text-gray-500 mb-2">Destinatari:</p>
                  <div className="text-sm font-medium text-gray-900 whitespace-pre-line">
                    {getDestinatariPreview()}
                  </div>
                </div>
              </div>
            </div>

            {/* Info aggiuntiva se Condiviso */}
            {document.stato === 'Condiviso' && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <div className="flex items-start gap-2">
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">📧 Email di notifica</p>
                    <p>Dopo la conferma, verrà inviata un'email di notifica a tutti i destinatari selezionati.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer con pulsanti */}
          <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <button 
              onClick={cancelDocumentConfirm}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
            >
              ❌ Annulla - Torna alla Modifica
            </button>
            <button 
              onClick={confirmAndSaveDocument}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              ✅ Confermo - Salva Documento
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 🆕 MODAL STORICO EVENTI LAVORATORE
  // 🆕 MODAL CONFERMA EMAIL
  const EmailConfirmModal = () => {
    if (!showEmailModal || !pendingEmailData) return null;

    const isCommunication = !!pendingEmailData.communication;
    const isDocument = !!pendingEmailData.document;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={cancelEmailSend}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
          <div className="bg-blue-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Conferma Invio Email</h2>
                <p className="text-sm text-blue-100 mt-1">
                  {isCommunication ? 'Pubblicazione comunicazione' : 'Condivisione documento'}
                </p>
              </div>
            </div>
            <button onClick={cancelEmailSend} className="p-2 hover:bg-blue-700 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-800">
                  <p className="font-semibold mb-1">📧 INVIO EMAIL REALE</p>
                  <p>Le email verranno inviate realmente ai destinatari tramite SafeTracker.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">
                {isCommunication ? '📋 Dettagli Comunicazione' : '📄 Dettagli Documento'}
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {isCommunication && (
                  <>
                    <p className="text-sm"><span className="font-semibold">Titolo:</span> {pendingEmailData.communication.titolo}</p>
                    <p className="text-sm mt-1"><span className="font-semibold">Azienda:</span> {pendingEmailData.company.ragioneSociale}</p>
                  </>
                )}
                {isDocument && (
                  <>
                    <p className="text-sm"><span className="font-semibold">Descrizione:</span> {pendingEmailData.document.descrizione}</p>
                    <p className="text-sm mt-1"><span className="font-semibold">Tipo:</span> {pendingEmailData.document.tipo}</p>
                    <p className="text-sm mt-1"><span className="font-semibold">Azienda:</span> {pendingEmailData.company.ragioneSociale}</p>
                  </>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-3">📧 Email da inviare ({pendingEmailData.recipients.length})</h3>
              <div className="max-h-60 overflow-y-auto space-y-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                {pendingEmailData.recipients.map((recipient, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{recipient.name}</p>
                      <p className="text-xs text-gray-600">{recipient.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <button 
              onClick={cancelEmailSend}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
            >
              Annulla
            </button>
            <button 
              onClick={confirmEmailSend}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Send className="w-5 h-5" />
              {isCommunication ? 'Conferma e Pubblica' : 'Conferma e Condividi'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const Sidebar = () => {
    const isAzienda = currentUser.role === 'AZIENDA';
    const isLavoratore = currentUser.role === 'LAVORATORE';
    const profileLabel = isLavoratore ? 'Area Personale' : (isAzienda ? 'Pannello Azienda' : 'Admin Panel');
    const userLabel = isLavoratore ? 'Lavoratore' : (isAzienda ? 'Datore di Lavoro' : 'Consulente');
    
    // Determina quale logo mostrare
    const getLogo = () => {
      if (isAzienda || isLavoratore) {
        const company = companies.find(c => String(c.id) === String(currentUser.companyId));
        return company?.logo || null;
      }
      return consultantLogo;
    };
    
    const currentLogo = getLogo();
    
    return (
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-blue-900 to-blue-800 text-white transition-all duration-300 flex flex-col fixed h-full z-20 shadow-2xl`}>
        <div className="p-4 border-b border-blue-700 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-3">
              {currentLogo ? (
                <img src={currentLogo} alt="Logo" className="w-10 h-10 rounded-lg object-contain bg-white p-1" />
              ) : (
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6" />
                </div>
              )}
              <div>
                <h1 className="font-bold text-lg">SafeTracker</h1>
                <p className="text-xs text-blue-300">{profileLabel}</p>
              </div>
            </div>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {/* Dashboard - diversa per ogni profilo */}
          <NavItem 
            icon={Home} 
            label={isLavoratore ? "La Mia Dashboard" : (isAzienda ? "Dashboard Aziendale" : "Dashboard")}
            active={activeSection === 'dashboard'}
            onClick={() => setActiveSection('dashboard')}
            collapsed={!sidebarOpen}
          />
          
          {/* ADMIN: Gestione Aziende */}
          {!isAzienda && !isLavoratore && (
            <NavItem 
              icon={Building2} 
              label="Gestione Aziende" 
              active={activeSection === 'companies'}
              onClick={() => {
                setActiveSection('companies');
                setSelectedCompany(null); // 🆕 Torna sempre all'elenco tabellare
              }}
              collapsed={!sidebarOpen}
            />
          )}
          
          {/* AZIENDA: Gestione Azienda + Documenti Aziendali */}
          {isAzienda && (
            <>
              <NavItem 
                icon={Building2} 
                label="Gestione Azienda" 
                active={activeSection === 'company'}
                onClick={() => setActiveSection('company')}
                collapsed={!sidebarOpen}
              />
              <NavItem 
                icon={FileCheck} 
                label="Documenti Aziendali" 
                active={activeSection === 'documents'}
                onClick={() => setActiveSection('documents')}
                collapsed={!sidebarOpen}
              />
            </>
          )}
          
          {/* ADMIN: Documenti Aziendali */}
          {!isAzienda && !isLavoratore && (
            <NavItem 
              icon={FileCheck} 
              label="Documenti Aziendali" 
              active={activeSection === 'documents-admin'}
              onClick={() => setActiveSection('documents-admin')}
              collapsed={!sidebarOpen}
            />
          )}
          
          {/* LAVORATORE: Documenti Ricevuti */}
          {isLavoratore && (
            <NavItem 
              icon={FileCheck} 
              label="Documenti Ricevuti" 
              active={activeSection === 'worker-documents'}
              onClick={() => setActiveSection('worker-documents')}
              collapsed={!sidebarOpen}
            />
          )}
          
          {/* Comunicazioni - ADMIN e AZIENDA */}
          {!isLavoratore && (
            <NavItem 
              icon={MessageSquare} 
              label="Comunicazioni" 
              active={activeSection === 'communications'}
              onClick={() => { setActiveSection('communications'); setSelectedCommunication(null); }}
              collapsed={!sidebarOpen}
            />
          )}
          
          {/* LAVORATORE: Comunicazioni Ricevute */}
          {isLavoratore && (
            <NavItem 
              icon={Mail} 
              label="Comunicazioni" 
              active={activeSection === 'worker-communications'}
              onClick={() => setActiveSection('worker-communications')}
              collapsed={!sidebarOpen}
            />
          )}
          
          {/* LAVORATORE: Il Mio Profilo */}
          {isLavoratore && (
            <NavItem 
              icon={UserCircle} 
              label="Il Mio Profilo" 
              active={activeSection === 'worker-profile'}
              onClick={() => setActiveSection('worker-profile')}
              collapsed={!sidebarOpen}
            />
          )}
          
          {/* Registro Tracciabilità - ADMIN e AZIENDA */}
          {(currentUser.role === 'ADMIN' || currentUser.role === 'AZIENDA') && (
            <>
              {/* Separatore Area Legale */}
              <div className="my-2 mx-2">
                <div className="h-px bg-blue-600/40"></div>
              </div>
              <button
                onClick={() => { setActiveSection('audit-log'); loadAuditLogs(); }}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
                  activeSection === 'audit-log'
                    ? 'bg-amber-600/30 shadow-lg text-amber-200' 
                    : 'hover:bg-amber-700/20 text-amber-300/80 hover:text-amber-200'
                } ${!sidebarOpen && 'justify-center'}`}
              >
                <Shield className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">Registro Tracciabilità</span>}
              </button>
            </>
          )}

          {/* Impostazioni - solo ADMIN e AZIENDA */}
          {!isLavoratore && (
            <NavItem 
              icon={Settings} 
              label="Impostazioni" 
              active={activeSection === 'settings'}
              onClick={() => setActiveSection('settings')}
              collapsed={!sidebarOpen}
            />
          )}
        </nav>

        <div className="p-4 border-t border-blue-700">
          {/* 🆕 SWITCH CAMBIA PROFILO - Solo per ADMIN */}
          {sidebarOpen && isRealAdmin && (
            <div className="mb-3 p-2 bg-blue-500/30 border border-blue-300/40 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 bg-blue-400 rounded flex items-center justify-center">
                  <Users className="w-3 h-3 text-white" />
                </div>
                <span className="text-xs font-bold text-white">Cambia Profilo</span>
              </div>
              
              <select
                value={currentUser.role}
                onChange={(e) => handleRoleChange(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-blue-400/40 rounded bg-blue-900/50 text-blue-100 font-medium mb-2"
              >
                <option value="ADMIN">🛡️ ADMIN (Consulente)</option>
                <option value="AZIENDA">🏢 AZIENDA</option>
                <option value="LAVORATORE">👷 LAVORATORE</option>
              </select>

              {currentUser.role === 'AZIENDA' && (
                <select
                  value={currentUser.companyId || ''}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-blue-400/40 rounded bg-blue-900/50 text-blue-100"
                >
                  <option value="">Seleziona azienda...</option>
                  {companies
                    .filter(c => c.tipoContratto === 'gestione_completa')
                    .map(c => (
                      <option key={c.id} value={c.id}>
                        {c.ragioneSociale}
                      </option>
                    ))}
                </select>
              )}

              {currentUser.role === 'LAVORATORE' && (
                <>
                  <select
                    value={currentUser.companyId || ''}
                    onChange={(e) => {
                      const cId = isNaN(e.target.value) ? e.target.value : Number(e.target.value);
                      setCurrentUser(prev => ({ ...prev, companyId: cId || null, workerId: null }));
                      setProfileWorkerSearch('');
                    }}
                    className="w-full px-2 py-1.5 text-xs border border-blue-400/40 rounded bg-blue-900/50 text-blue-100 mb-1"
                  >
                    <option value="">Seleziona azienda...</option>
                    {companies
                      .filter(c => c.tipoContratto === 'gestione_completa')
                      .map(c => (
                        <option key={c.id} value={c.id}>
                          {c.ragioneSociale}
                        </option>
                      ))}
                  </select>
                  {currentUser.companyId && (
                    <>
                      <input
                        type="text"
                        value={profileWorkerSearch}
                        onChange={(e) => setProfileWorkerSearch(e.target.value)}
                        placeholder="🔍 Cerca per cognome..."
                        className="w-full px-2 py-1.5 text-xs border border-blue-400/40 rounded bg-blue-900/50 text-blue-100 placeholder-blue-300/60 mb-1"
                        autoFocus
                      />
                      <select
                        value={currentUser.workerId || ''}
                        onChange={(e) => handleWorkerChange(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-blue-400/40 rounded bg-blue-900/50 text-blue-100"
                        size={6}
                      >
                        <option value="">Seleziona lavoratore...</option>
                        {workers
                          .filter(w => {
                            if (!currentUser.companyId) return false;
                            if (w.companyId != currentUser.companyId) return false;
                            if (profileWorkerSearch) {
                              const search = profileWorkerSearch.toLowerCase();
                              return w.cognome.toLowerCase().includes(search) || w.nome.toLowerCase().includes(search);
                            }
                            return true;
                          })
                          .sort((a, b) => a.cognome.localeCompare(b.cognome))
                          .map(w => (
                            <option key={w.id} value={w.id}>
                              {w.cognome} {w.nome}
                            </option>
                          ))}
                      </select>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'justify-center'}`}>
            {sidebarOpen && (
              <div className="flex-1">
                <p className="font-semibold text-sm">{currentUser.nome} {currentUser.cognome}</p>
                <p className="text-xs text-blue-300">{userLabel}</p>
              </div>
            )}
            <button 
                onClick={handleLogout}
                title="Esci"
                className="p-2 hover:bg-blue-700 rounded-lg transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>
    );
  };

  const NavItem = ({ icon: Icon, label, active, onClick, collapsed }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
        active 
          ? 'bg-blue-600 shadow-lg' 
          : 'hover:bg-blue-700/50'
      } ${collapsed && 'justify-center'}`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && <span className="font-medium">{label}</span>}
    </button>
  );

  const StatCard = ({ icon: Icon, label, value, color, trend, onClick }) => (
    <div 
      className={`bg-white rounded-xl shadow-md p-6 transition-shadow ${onClick ? 'hover:shadow-lg cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-12 h-12 bg-${color}-100 rounded-xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        {trend && (
          <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
            trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );

  const CompanyCard = ({ company, onClick }) => {
    const companyWorkers = workers.filter(w => w.companyId === company.id);
    const companyWorkersAttivi = getLavoratoriAttivi(company.id); // 🆕 Solo attivi
    const companyComms = communications.filter(c => c.aziendaId === company.id && c.stato === 'Pubblicato');
    
    let compliance = 100;
    if (companyComms.length > 0) {
      const total = companyComms.reduce((sum, comm) => {
        const stats = calculateCommunicationStats(comm);
        return sum + (stats.destinatari > 0 ? (stats.letti / stats.destinatari * 100) : 0);
      }, 0);
      compliance = Math.round(total / companyComms.length);
    }

    return (
      <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-400">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 cursor-pointer" onClick={onClick}>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">{company.ragioneSociale}</h3>
            </div>
            <p className="text-sm text-gray-600 flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {company.citta}
            </p>
            <p className="text-xs text-gray-500 mt-1">P.IVA: {company.partitaIva}</p>
          </div>
          <div className="flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openModal('editCompany', company);
              }}
              className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
              title="Modifica"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteCompany(company.id);
              }}
              className="p-2 hover:bg-red-50 rounded-lg text-red-600 transition-colors"
              title="Elimina"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
          <div>
            <p className="text-xs text-gray-500 mb-1">Dipendenti</p>
            <p className="text-lg font-bold text-gray-900">{companyWorkersAttivi.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Comunicazioni</p>
            <p className="text-lg font-bold text-blue-600">{companyComms.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Compliance</p>
            <p className="text-lg font-bold text-green-600">{compliance}%</p>
          </div>
        </div>
      </div>
    );
  };

  const WorkerRow = ({ worker }) => (
    <tr className={`hover:bg-gray-50 border-b border-gray-200 ${worker.isDatore ? 'bg-amber-50' : ''}`}>
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            worker.isDatore ? 'bg-amber-100' : 'bg-blue-100'
          }`}>
            <span className={`font-semibold ${worker.isDatore ? 'text-amber-600' : 'text-blue-600'}`}>
              {worker.nome[0]}{worker.cognome[0]}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold text-gray-900">{worker.nome} {worker.cognome}</p>
              {worker.isDatore && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded border border-amber-400">
                  <Building2 className="w-3 h-3" />
                  DATORE
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500">{worker.email}</p>
            {worker.isDatore && (
              <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                Ruoli cumulati (Art. 34 D.Lgs. 81/2008)
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className={`px-3 py-1 text-sm font-medium rounded-full ${
          worker.isDatore 
            ? 'bg-amber-100 text-amber-800 border border-amber-300' 
            : 'bg-blue-100 text-blue-700'
        }`}>
          {worker.mansione}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {worker.ruoli.map((ruolo, idx) => (
            <span key={idx} className={`px-2 py-1 text-xs font-medium rounded ${
              worker.isDatore
                ? 'bg-amber-100 text-amber-800 border border-amber-300'
                : 'bg-purple-100 text-purple-700'
            }`}>
              {ruolo}
            </span>
          ))}
          {worker.ruoli.length === 0 && <span className="text-sm text-gray-400">-</span>}
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-600">{worker.cellulare || '-'}</td>
      <td className="px-6 py-4 text-sm text-gray-600">
        {worker.dataAssunzione ? formatDateOnly(worker.dataAssunzione) : '-'}
      </td>
      <td className="px-6 py-4">
        <div className="flex gap-2">
          <button 
            onClick={() => {
              setSelectedWorkerForStorico(worker);
              setShowStoricoEventoModal(true);
            }}
            className="p-2 hover:bg-purple-50 rounded-lg text-purple-600 transition-colors"
            title="Storico Eventi"
          >
            <Clock className="w-4 h-4" />
          </button>
          <button 
            onClick={() => openModal('editWorker', worker)}
            disabled={worker.isDatore}
            className={`p-2 rounded-lg transition-colors ${
              worker.isDatore
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'hover:bg-blue-50 text-blue-600'
            }`}
            title={worker.isDatore ? 'Modifica dall\'anagrafica aziendale' : 'Modifica'}
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button 
            onClick={() => handleDeleteWorker(worker.id)}
            disabled={worker.isDatore}
            className={`p-2 rounded-lg transition-colors ${
              worker.isDatore
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'hover:bg-red-50 text-red-600'
            }`}
            title={worker.isDatore ? 'Il Datore non può essere eliminato' : 'Elimina'}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  const CommunicationCard = ({ comm, onClick }) => {
    const typeColors = {
      'Messaggio': 'blue',
      'Azione': 'orange',
      'Documento': 'purple'
    };
    const typeIcons = {
      'Messaggio': MessageSquare,
      'Azione': Target,
      'Documento': File
    };
    const color = typeColors[comm.tipologia];
    const Icon = typeIcons[comm.tipologia];
    
    const stats = calculateCommunicationStats(comm);
    const completionRate = stats.destinatari > 0 ? ((stats.letti / stats.destinatari) * 100).toFixed(0) : 0;
    const companyName = getCompanyName(comm.aziendaId);

    return (
      <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-blue-300">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 cursor-pointer" onClick={onClick}>
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`w-5 h-5 text-${color}-600`} />
              <span className={`px-2 py-1 bg-${color}-100 text-${color}-700 text-xs font-semibold rounded`}>
                {comm.tipologia}
              </span>
              {comm.stato === 'Bozza' && (
                <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded">
                  BOZZA
                </span>
              )}
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">{comm.titolo}</h3>
            <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
              <Building2 className="w-4 h-4" />
              {companyName}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatDate(comm.dataInvio)}
              </span>
              {comm.dataScadenza && (
                <span className="flex items-center gap-1 text-orange-600">
                  <Calendar className="w-4 h-4" />
                  Scadenza: {formatDateOnly(comm.dataScadenza)}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openModal('editCommunication', comm);
            }}
            className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
            title="Modifica"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Letture</span>
            <span className="font-bold text-gray-900">{completionRate}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`bg-${color}-600 h-2 rounded-full transition-all`}
              style={{ width: `${completionRate}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Destinatari: {stats.destinatari}</span>
            <span className="text-green-600">Letti: {stats.letti}</span>
            <span className="text-red-600">Non letti: {stats.nonLetti}</span>
          </div>
        </div>
      </div>
    );
  };

  // 🆕 COMPONENTI HELPER PER CONTROLLO ACCESSO AZIENDA/LAVORATORE
  
  // Componente blocco totale per azienda DISATTIVATA
  const AccessBlockedComponent = ({ companyName, role }) => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <Lock className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-red-700 mb-2">Accesso Non Disponibile</h2>
        <p className="text-red-600 mb-4">
          L'accesso alla piattaforma è stato disabilitato per {role === 'LAVORATORE' ? 'la tua azienda' : 'questa azienda'}.
        </p>
        <p className="text-gray-600 mb-6">
          {role === 'LAVORATORE' 
            ? 'Per maggiori informazioni, contatta il tuo datore di lavoro.'
            : 'Per riattivare il servizio, contatta il tuo consulente di riferimento.'}
        </p>
        <div className="bg-white rounded-lg p-4 inline-block">
          <p className="text-sm text-gray-500">Azienda</p>
          <p className="font-semibold text-gray-900">{companyName}</p>
        </div>
      </div>
    </div>
  );

  // Componente banner per contratto SCADUTO (solo visualizzazione)
  const ContractExpiredBanner = ({ scadenzaDate, role }) => (
    <div className="bg-orange-50 border border-orange-300 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-3">
        <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
        <div>
          <p className="font-semibold text-orange-800">Contratto Scaduto - Modalità Solo Visualizzazione</p>
          <p className="text-sm text-orange-700">
            {role === 'LAVORATORE' 
              ? `Il contratto SafeTracker della tua azienda è scaduto. Puoi consultare i contenuti esistenti ma alcune funzionalità potrebbero essere limitate. Per maggiori informazioni, contatta il tuo datore di lavoro.`
              : `Il contratto SafeTracker è scaduto${scadenzaDate ? ` il ${formatDateOnly(scadenzaDate)}` : ''}. Puoi consultare i dati esistenti ma non puoi effettuare modifiche. Per riattivare il servizio, contatta il tuo consulente di riferimento.`}
          </p>
        </div>
      </div>
    </div>
  );

  // Verifica stato accesso per profilo AZIENDA
  const getAziendaAccessStatus = () => {
    if (currentUser.role !== 'AZIENDA') return { canAccess: true, isReadOnly: false };
    const company = companies.find(c => String(c.id) === String(currentUser.companyId));
    if (!company) return { canAccess: true, isReadOnly: false };
    if (!company.attiva) return { canAccess: false, isReadOnly: false, company };
    if (getStatoContratto(company) === 'scaduto') return { canAccess: true, isReadOnly: true, company };
    return { canAccess: true, isReadOnly: false, company };
  };

  // Verifica stato accesso per profilo LAVORATORE
  const getLavoratoreAccessStatus = () => {
    if (currentUser.role !== 'LAVORATORE') return { canAccess: true, isReadOnly: false };
    const company = companies.find(c => String(c.id) === String(currentUser.companyId));
    if (!company) return { canAccess: true, isReadOnly: false };
    if (!company.attiva) return { canAccess: false, isReadOnly: false, company };
    if (getStatoContratto(company) === 'scaduto') return { canAccess: true, isReadOnly: true, company };
    return { canAccess: true, isReadOnly: false, company };
  };

  // 🆕 Helper per verificare se le azioni sono bloccate (contratto scaduto)
  const isActionBlocked = () => {
    if (currentUser.role === 'AZIENDA') {
      return getAziendaAccessStatus().isReadOnly;
    }
    if (currentUser.role === 'LAVORATORE') {
      return getLavoratoreAccessStatus().isReadOnly;
    }
    return false;
  };

  // 🆕 Mostra messaggio azione bloccata
  // 🔐 FASE 6.2: Carica Registro Tracciabilità
  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const logs = await auditLogService.getAll(500);
      // Se profilo AZIENDA, filtra solo eventi della propria azienda
      if (currentUser.role === 'AZIENDA' && currentUser.companyId) {
        const companyLogs = logs.filter(log => 
          String(log.new_values?.companyId) === String(currentUser.companyId)
        );
        setAuditLogs(companyLogs);
      } else {
        setAuditLogs(logs);
      }
    } catch (err) {
      console.error('Errore caricamento audit log:', err);
    }
    setAuditLoading(false);
  };

  // 🔐 Formattazione azioni audit log
  const formatAuditAction = (action) => {
    const map = {
      'CREATE': { label: 'Creazione', color: 'bg-green-100 text-green-800' },
      'UPDATE': { label: 'Modifica', color: 'bg-blue-100 text-blue-800' },
      'DELETE': { label: 'Eliminazione', color: 'bg-red-100 text-red-800' },
      'SHARE': { label: 'Condivisione', color: 'bg-purple-100 text-purple-800' },
      'CONFIRM': { label: 'Conferma', color: 'bg-yellow-100 text-yellow-800' },
      'LOGIN': { label: 'Accesso', color: 'bg-gray-100 text-gray-800' },
      'EXPORT': { label: 'Esportazione', color: 'bg-indigo-100 text-indigo-800' },
    };
    return map[action] || { label: action, color: 'bg-gray-100 text-gray-800' };
  };

  const formatAuditEntity = (entityType) => {
    const map = {
      'company': 'Azienda',
      'worker': 'Lavoratore',
      'communication': 'Comunicazione',
      'document': 'Documento',
      'document_share': 'Condivisione Doc.',
      'communication_read': 'Lettura Com.',
    };
    return map[entityType] || entityType;
  };

  const showBlockedActionAlert = () => {
    alert('⚠️ Azione non disponibile\n\nIl contratto SafeTracker è scaduto.\nPuoi solo visualizzare i contenuti esistenti.\n\nContatta il tuo consulente per riattivare il servizio.');
  };

  // Main content render
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        // 🆕 DASHBOARD LAVORATORE
        if (currentUser.role === 'LAVORATORE') {
          const myComms = getWorkerCommunications();
          const myDocs = getWorkerDocuments();
          const unreadComms = myComms.filter(c => {
            const r = c.recipients?.find(r => r.id === currentUser.workerId);
            return r && !r.letto;
          });
          const unreadDocs = myDocs.filter(d => {
            const r = d.recipients?.find(r => r.id === currentUser.workerId);
            return r && !r.letto;
          });
          const unapprovedDocs = myDocs.filter(d => {
            const r = d.recipients?.find(r => r.id === currentUser.workerId);
            return r && !r.approvato;
          });
          const myWorker = workers.find(w => w.id === currentUser.workerId);
          const myCompany = companies.find(c => String(c.id) === String(currentUser.companyId));
          
          // 🆕 Blocco accesso azienda DISATTIVATA (blocco totale)
          if (myCompany && !myCompany.attiva) {
            return (
              <div className="space-y-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
                  <Lock className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-red-700 mb-2">Accesso Non Disponibile</h2>
                  <p className="text-red-600 mb-4">
                    L'accesso alla piattaforma è stato disabilitato per la tua azienda.
                  </p>
                  <p className="text-gray-600 mb-6">
                    Per maggiori informazioni, contatta il tuo datore di lavoro.
                  </p>
                  <div className="bg-white rounded-lg p-4 inline-block">
                    <p className="text-sm text-gray-500">Azienda</p>
                    <p className="font-semibold text-gray-900">{myCompany.ragioneSociale}</p>
                  </div>
                </div>
              </div>
            );
          }
          
          // 🆕 Stato SCADUTO = solo visualizzazione (con banner avviso)
          const isContrattoScaduto = myCompany && getStatoContratto(myCompany) === 'scaduto';
          
          return (
            <div className="space-y-6">
              {/* 🆕 Banner avviso contratto scaduto */}
              {isContrattoScaduto && (
                <div className="bg-orange-50 border border-orange-300 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-orange-800">Contratto Scaduto - Modalità Solo Visualizzazione</p>
                      <p className="text-sm text-orange-700">
                        Il contratto SafeTracker della tua azienda è scaduto. Puoi consultare i contenuti esistenti ma alcune funzionalità potrebbero essere limitate. 
                        Per maggiori informazioni, contatta il tuo datore di lavoro.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">La Mia Dashboard</h1>
                <p className="text-gray-600">Benvenuto, {currentUser.nome} {currentUser.cognome}</p>
              </div>

              {/* Alert avvisi urgenti - solo se ci sono elementi da leggere/visualizzare/approvare */}
              {(unreadComms.length > 0 || unreadDocs.length > 0 || unapprovedDocs.length > 0) && (
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <AlertCircle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-amber-800">Hai elementi in attesa</p>
                      <div className="flex flex-wrap gap-4 mt-1 text-sm text-amber-700">
                        {unreadComms.length > 0 && (
                          <button 
                            onClick={() => setActiveSection('worker-communications')}
                            className="flex items-center gap-1 hover:text-amber-900 hover:underline"
                          >
                            <Mail className="w-4 h-4" />
                            <span><strong>{unreadComms.length}</strong> comunicazion{unreadComms.length === 1 ? 'e' : 'i'} da leggere</span>
                          </button>
                        )}
                        {unreadDocs.length > 0 && (
                          <button 
                            onClick={() => setActiveSection('worker-documents')}
                            className="flex items-center gap-1 hover:text-amber-900 hover:underline"
                          >
                            <Eye className="w-4 h-4" />
                            <span><strong>{unreadDocs.length}</strong> document{unreadDocs.length === 1 ? 'o' : 'i'} da visualizzare</span>
                          </button>
                        )}
                        {unapprovedDocs.length > 0 && (
                          <button 
                            onClick={() => setActiveSection('worker-documents')}
                            className="flex items-center gap-1 hover:text-amber-900 hover:underline"
                          >
                            <ClipboardCheck className="w-4 h-4" />
                            <span><strong>{unapprovedDocs.length}</strong> document{unapprovedDocs.length === 1 ? 'o' : 'i'} da approvare</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Card statistiche - solo 2 card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <StatCard 
                  icon={MessageSquare} 
                  label="Comunicazioni Ricevute" 
                  value={myComms.length} 
                  color="blue"
                  onClick={() => setActiveSection('worker-communications')}
                />
                <StatCard 
                  icon={FileCheck} 
                  label="Documenti Ricevuti" 
                  value={myDocs.length} 
                  color="purple"
                  onClick={() => setActiveSection('worker-documents')}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Widget Ultime Comunicazioni */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900">Ultime Comunicazioni</h2>
                    <button
                      onClick={() => setActiveSection('worker-communications')}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Vedi tutte →
                    </button>
                  </div>
                  <div className="space-y-3">
                    {myComms.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Nessuna comunicazione ricevuta</p>
                    ) : (
                      myComms.slice(0, 4).map(comm => {
                        const recipient = comm.recipients?.find(r => r.id === currentUser.workerId);
                        const isRead = recipient?.letto;
                        return (
                          <div 
                            key={comm.id}
                            onClick={() => {
                              setSelectedWorkerComm(comm);
                              setActiveSection('worker-communications');
                            }}
                            className={`p-3 rounded-lg cursor-pointer transition-colors border-l-4 ${
                              isRead 
                                ? 'bg-gray-50 hover:bg-gray-100 border-gray-300' 
                                : 'bg-blue-50 hover:bg-blue-100 border-blue-500'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {!isRead && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                                <span className={`font-medium ${!isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                                  {comm.titolo}
                                </span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                comm.tipologia === 'Documento' ? 'bg-purple-100 text-purple-700' :
                                comm.tipologia === 'Azione' ? 'bg-orange-100 text-orange-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {comm.tipologia}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(comm.dataInvio)}
                            </p>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Widget Ultimi Documenti */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900">Ultimi Documenti</h2>
                    <button
                      onClick={() => setActiveSection('worker-documents')}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Vedi tutti →
                    </button>
                  </div>
                  <div className="space-y-3">
                    {myDocs.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Nessun documento ricevuto</p>
                    ) : (
                      myDocs.slice(0, 4).map(doc => {
                        const recipient = doc.recipients?.find(r => r.id === currentUser.workerId);
                        const isRead = recipient?.letto;
                        const isApproved = recipient?.approvato;
                        return (
                          <div 
                            key={doc.id}
                            onClick={() => {
                              setSelectedWorkerDoc(doc);
                              setActiveSection('worker-documents');
                            }}
                            className={`p-3 rounded-lg cursor-pointer transition-colors border-l-4 ${
                              isRead 
                                ? 'bg-gray-50 hover:bg-gray-100 border-gray-300' 
                                : 'bg-purple-50 hover:bg-purple-100 border-purple-500'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {!isRead && <div className="w-2 h-2 bg-purple-500 rounded-full"></div>}
                                <span className={`font-medium ${!isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                                  {doc.descrizione}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                  {doc.tipo}
                                </span>
                                {isApproved && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                                    ✓ Approvato
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Caricato il {formatDateOnly(doc.dataCaricamento)}
                            </p>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Card Il Mio Profilo */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Il Mio Profilo</h2>
                  <button
                    onClick={() => setActiveSection('worker-profile')}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Dettagli →
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserCircle className="w-10 h-10 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-lg text-gray-900">{myWorker?.nome} {myWorker?.cognome}</p>
                      <p className="text-gray-600">{myWorker?.mansione || 'Mansione non assegnata'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Azienda</p>
                    <p className="font-medium text-gray-900">{myCompany?.ragioneSociale || '-'}</p>
                    <p className="text-sm text-gray-600">{myCompany?.citta || ''}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Ruoli Sicurezza</p>
                    {myWorker?.ruoli && myWorker.ruoli.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {myWorker.ruoli.map((ruolo, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                            {ruolo}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-600">Nessun ruolo assegnato</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        // Dashboard ADMIN e AZIENDA (esistente)
        // 🆕 Controllo accesso AZIENDA
        if (currentUser.role === 'AZIENDA') {
          const accessStatus = getAziendaAccessStatus();
          if (!accessStatus.canAccess) {
            return <AccessBlockedComponent companyName={accessStatus.company?.ragioneSociale} role="AZIENDA" />;
          }
        }
        
        const aziendaReadOnly = currentUser.role === 'AZIENDA' && getAziendaAccessStatus().isReadOnly;
        const aziendaCompanyForBanner = currentUser.role === 'AZIENDA' ? getAziendaAccessStatus().company : null;
        
        return (
          <div className="space-y-6">
            {/* 🆕 Banner contratto scaduto per AZIENDA */}
            {aziendaReadOnly && aziendaCompanyForBanner && (
              <ContractExpiredBanner scadenzaDate={aziendaCompanyForBanner.dataScadenzaContratto} role="AZIENDA" />
            )}
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
              <p className="text-gray-600">Panoramica generale delle attività</p>
            </div>

            <div className={`grid grid-cols-1 md:grid-cols-2 ${currentUser.role === 'ADMIN' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
              {/* Card "Aziende Gestite" solo per ADMIN */}
              {currentUser.role === 'ADMIN' && (
                <StatCard 
                  icon={Building2} 
                  label="Aziende Gestite" 
                  value={companies.filter(c => c.tipoContratto === 'gestione_completa').length} 
                  color="blue" 
                />
              )}
              <StatCard 
                icon={Users} 
                label={currentUser.role === 'AZIENDA' ? "Lavoratori" : "Dipendenti Totali"} 
                value={getTotalWorkers()} 
                color="indigo" 
              />
              <StatCard 
                icon={MessageSquare} 
                label="Comunicazioni Attive" 
                value={getActiveCommunications()} 
                color="purple" 
              />
              <StatCard 
                icon={CheckCircle} 
                label="Compliance Media" 
                value={`${getAverageCompliance()}%`} 
                color="green"
                onClick={() => alert(`Compliance Media: ${getAverageCompliance()}%\n\nCalcolata come media delle percentuali di lettura di tutte le comunicazioni pubblicate.\n\nFormula: (Totale Letti / Totale Destinatari) * 100`)}
              />
            </div>

            {/* 🆕 WIDGET SCADENZE CONTRATTI - Solo ADMIN */}
            {currentUser.role === 'ADMIN' && getAziendeInScadenza().length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-400">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <AlertCircle className="w-6 h-6 text-orange-500" />
                    Contratti in Scadenza
                  </h2>
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                    {getAziendeInScadenza().length} aziend{getAziendeInScadenza().length === 1 ? 'a' : 'e'}
                  </span>
                </div>
                <div className="space-y-3">
                  {getAziendeInScadenza().slice(0, 5).map(company => {
                    const stato = getStatoContratto(company);
                    const badge = getStatoContrattoBadge(company);
                    const tipoBadge = getTipoContrattoBadge(company.tipoContratto);
                    const giorni = getGiorniAllaScadenza(company.dataScadenzaContratto);
                    
                    return (
                      <div 
                        key={company.id}
                        className={`p-4 rounded-lg cursor-pointer transition-colors border-l-4 ${
                          stato === 'scaduto' ? 'bg-red-50 border-red-500 hover:bg-red-100' :
                          stato === 'in_scadenza' ? 'bg-orange-50 border-orange-500 hover:bg-orange-100' :
                          'bg-yellow-50 border-yellow-500 hover:bg-yellow-100'
                        }`}
                        onClick={() => {
                          setSelectedCompany(company.id);
                          setActiveSection('companies');
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-gray-900">{company.ragioneSociale}</p>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${tipoBadge.bg} ${tipoBadge.text}`}>
                                {tipoBadge.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm">
                              <span className="text-gray-600">
                                📅 Scadenza: {formatDateOnly(company.dataScadenzaContratto)}
                              </span>
                              <span className={`font-medium ${
                                stato === 'scaduto' ? 'text-red-600' :
                                stato === 'in_scadenza' ? 'text-orange-600' :
                                'text-yellow-600'
                              }`}>
                                {giorni < 0 ? `Scaduto da ${Math.abs(giorni)} giorni` : 
                                 giorni === 0 ? 'Scade oggi!' : 
                                 `${giorni} giorni rimasti`}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
                              {badge.label}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const emailData = sendContractExpiryEmail(company, stato);
                                alert(`📧 Email inviata a: ${emailData.to}\n\nOggetto: ${emailData.subject}`);
                              }}
                              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                              title="Invia email promemoria"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* 🆕 Link sempre visibile se ci sono aziende in scadenza */}
                {getAziendeInScadenza().length > 0 && (
                  <button 
                    onClick={() => {
                      setSelectedCompany(null); // 🆕 Torna all'elenco tabellare
                      setActiveSection('companies');
                    }}
                    className="mt-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Vedi tutte le {getAziendeInScadenza().length} aziende →
                  </button>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Widget dinamico: Aziende Recenti (ADMIN) o Documenti Recenti (AZIENDA) */}
              {currentUser.role === 'ADMIN' ? (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Aziende Recenti</h2>
                  <div className="space-y-3">
                    {companies.slice(0, 3).map(company => (
                      <div 
                        key={company.id} 
                        className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedCompany(company.id);
                          setActiveSection('companies');
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{company.ragioneSociale}</p>
                            <p className="text-sm text-gray-500">
                              {getLavoratoriAttivi(company.id).length} dipendenti
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Documenti Recenti</h2>
                  <div className="space-y-3">
                    {documents
                      .filter(d => String(d.companyId) === String(currentUser.companyId))
                      .sort((a, b) => new Date(b.dataCaricamento) - new Date(a.dataCaricamento))
                      .slice(0, 3)
                      .map(doc => {
                        const daysToExpiry = doc.dataScadenza ? Math.ceil((new Date(doc.dataScadenza) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                        const isExpiringSoon = daysToExpiry && daysToExpiry <= 30;
                        
                        return (
                          <div 
                            key={doc.id} 
                            className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                            onClick={() => setActiveSection('documents')}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                doc.tipo === 'DVR' ? 'bg-blue-100' : 'bg-purple-100'
                              }`}>
                                <FileCheck className={`w-5 h-5 ${
                                  doc.tipo === 'DVR' ? 'text-blue-600' : 'text-purple-600'
                                }`} />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 text-sm">{doc.descrizione}</p>
                                <p className="text-xs text-gray-500">{doc.tipo}</p>
                              </div>
                            </div>
                            {isExpiringSoon && (
                              <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded">
                                Scade tra {daysToExpiry} gg
                              </span>
                            )}
                          </div>
                        );
                      })}
                    {documents.filter(d => String(d.companyId) === String(currentUser.companyId)).length === 0 && (
                      <p className="text-center text-gray-500 text-sm py-4">Nessun documento caricato</p>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Comunicazioni Recenti</h2>

                <div className="space-y-3">
                  {(() => {
                    let comms = communications.filter(c => c.stato === 'Pubblicato');
                    if (currentUser.role === 'AZIENDA' && currentUser.companyId) {
                      comms = comms.filter(c => String(c.aziendaId) === String(currentUser.companyId));
                    }
                    return comms
                      .sort((a, b) => new Date(b.dataInvio) - new Date(a.dataInvio))
                      .slice(0, 5)
                      .map(comm => (
                        <div 
                          key={comm.id} 
                          className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedCommunication(comm.id);
                            setActiveSection('communications');
                          }}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <MessageSquare className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 text-sm">{comm.titolo}</p>
                              <p className="text-xs text-gray-600 mt-0.5">{getCompanyName(comm.aziendaId)}</p>
                              {/* 🆕 DATA INVIO */}
                              <p className="text-xs text-gray-500">📅 {formatDateOnly(comm.dataInvio)} • {calculateCommunicationStats(comm).letti}/{calculateCommunicationStats(comm).destinatari} letti</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            (calculateCommunicationStats(comm).letti / calculateCommunicationStats(comm).destinatari) > 0.8 
                              ? 'bg-green-100 text-green-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {calculateCommunicationStats(comm).destinatari > 0 ? ((calculateCommunicationStats(comm).letti / calculateCommunicationStats(comm).destinatari) * 100).toFixed(0) : 0}%
                          </span>
                        </div>
                      ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        );

      case 'companies':
        if (selectedCompany) {
          const company = companies.find(c => c.id === selectedCompany);
          const companyWorkers = workers.filter(w => w.companyId === selectedCompany);
          
          return (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <button 
                  onClick={() => setSelectedCompany(null)}
                  className="hover:text-blue-600 flex items-center gap-1 transition-colors"
                >
                  Aziende
                </button>
                <ChevronRight className="w-4 h-4" />
                <span className="font-semibold text-gray-900">{company?.ragioneSociale}</span>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{company?.ragioneSociale}</h1>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTipoContrattoBadge(company?.tipoContratto).bg} ${getTipoContrattoBadge(company?.tipoContratto).text}`}>
                        {getTipoContrattoBadge(company?.tipoContratto).label}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatoContrattoBadge(company).bg} ${getStatoContrattoBadge(company).text}`}>
                        {getStatoContrattoBadge(company).label}
                      </span>
                      {company?.dataScadenzaContratto && (
                        <span className="text-xs text-gray-500">
                          Scadenza: {formatDateOnly(company.dataScadenzaContratto)}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Toggle Attivazione rapida */}
                  <button
                    onClick={() => {
                      setCompanies(companies.map(c => 
                        c.id === company.id ? { ...c, attiva: !c.attiva } : c
                      ));
                    }}
                    className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                      company?.attiva 
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {company?.attiva ? <Lock className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    {company?.attiva ? 'Disattiva' : 'Attiva'}
                  </button>
                </div>
                
                {/* Avviso Solo Piattaforma */}
                {company?.tipoContratto === 'solo_piattaforma' && (
                  <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-700 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      <span><strong>Azienda Solo Piattaforma</strong> - Il cliente gestisce autonomamente lavoratori e documenti. Nessuna comunicazione/documento inviato dal consulente.</span>
                    </p>
                  </div>
                )}
                
                <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
                  <button
                    onClick={() => setCompanySubMenu('anagrafica')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                      companySubMenu === 'anagrafica'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Anagrafica
                    </div>
                  </button>
                  {/* Tab nascosti per Solo Piattaforma */}
                  {company?.tipoContratto !== 'solo_piattaforma' && (
                    <>
                      <button
                        onClick={() => setCompanySubMenu('workers')}
                        className={`px-4 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                          companySubMenu === 'workers'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Lavoratori ({companyWorkers.filter(w => !isDatoreDiLavoro(w, selectedCompany)).length})
                        </div>
                      </button>
                      <button
                        onClick={() => setCompanySubMenu('roles')}
                        className={`px-4 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                          companySubMenu === 'roles'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Ruoli Aziendali
                        </div>
                      </button>
                      <button
                        onClick={() => setCompanySubMenu('mansioni')}
                        className={`px-4 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                          companySubMenu === 'mansioni'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4" />
                          Mansioni
                        </div>
                      </button>
                    </>
                  )}
                </div>

                {companySubMenu === 'anagrafica' && (
                  <div className="space-y-6">
                    {/* Header con pulsante Modifica */}
                    <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                      <h3 className="text-xl font-bold text-gray-900">Anagrafica Aziendale</h3>
                      <button
                        onClick={() => openModal('editCompany', company)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Modifica Anagrafica
                      </button>
                    </div>

                    {/* Dati Azienda */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Dati Azienda</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Ragione Sociale</p>
                          <p className="font-semibold text-gray-900">{company.ragioneSociale}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Partita IVA</p>
                          <p className="font-semibold text-gray-900">{company.partitaIva}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Indirizzo</p>
                          <p className="font-semibold text-gray-900">{company.indirizzo}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Città</p>
                          <p className="font-semibold text-gray-900">{company.citta}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">PEC</p>
                          <p className="font-semibold text-gray-900">{company.pec}</p>
                        </div>
                      </div>
                    </div>

                    {/* Datore di Lavoro */}
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Datore di Lavoro</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Nome</p>
                          <p className="font-semibold text-gray-900">{company.datoreLavoro.nome}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Cognome</p>
                          <p className="font-semibold text-gray-900">{company.datoreLavoro.cognome}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Email</p>
                          <p className="font-semibold text-gray-900">{company.datoreLavoro.email}</p>
                        </div>
                      </div>
                    </div>

                    {/* Medico Competente */}
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Medico Competente</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-green-50 p-4 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Nome e Cognome</p>
                          <p className="font-semibold text-gray-900">
                            {company.medicoCompetente.nome} {company.medicoCompetente.cognome}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Email</p>
                          <p className="font-semibold text-gray-900">{company.medicoCompetente.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">PEC</p>
                          <p className="font-semibold text-gray-900">{company.medicoCompetente.pec}</p>
                        </div>
                      </div>
                    </div>

                    {/* RSPP */}
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">RSPP</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-purple-50 p-4 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Nome e Cognome</p>
                          <p className="font-semibold text-gray-900">
                            {company.rspp.nome} {company.rspp.cognome}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Email</p>
                          <p className="font-semibold text-gray-900">{company.rspp.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">PEC</p>
                          <p className="font-semibold text-gray-900">{company.rspp.pec}</p>
                        </div>
                      </div>
                    </div>

                    {/* Codici ATECO */}
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Codici ATECO</h3>
                      <div className="space-y-3">
                        {company.codiciAteco.map((ateco, idx) => (
                          <div key={idx} className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                            <p className="font-semibold text-gray-900">{ateco.codice}</p>
                            <p className="text-sm text-gray-600">{ateco.descrizione}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 🆕 Dati Contratto */}
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <FileSignature className="w-5 h-5 text-blue-600" />
                        Dati Contratto
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Tipo Contratto</p>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTipoContrattoBadge(company.tipoContratto).bg} ${getTipoContrattoBadge(company.tipoContratto).text}`}>
                            {getTipoContrattoBadge(company.tipoContratto).label}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Stato</p>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatoContrattoBadge(company).bg} ${getStatoContrattoBadge(company).text}`}>
                            {getStatoContrattoBadge(company).label}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Data Inizio</p>
                          <p className="font-semibold text-gray-900">
                            {company.dataInizioContratto ? formatDateOnly(company.dataInizioContratto) : 'N/D'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Data Scadenza</p>
                          <p className="font-semibold text-gray-900">
                            {company.dataScadenzaContratto ? formatDateOnly(company.dataScadenzaContratto) : 'N/D'}
                            {company.dataScadenzaContratto && (
                              <span className={`ml-2 text-xs ${
                                getGiorniAllaScadenza(company.dataScadenzaContratto) < 0 ? 'text-red-600' :
                                getGiorniAllaScadenza(company.dataScadenzaContratto) <= 30 ? 'text-orange-600' :
                                getGiorniAllaScadenza(company.dataScadenzaContratto) <= 60 ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>
                                ({getGiorniAllaScadenza(company.dataScadenzaContratto) < 0 
                                  ? `Scaduto da ${Math.abs(getGiorniAllaScadenza(company.dataScadenzaContratto))}gg` 
                                  : `${getGiorniAllaScadenza(company.dataScadenzaContratto)}gg rimasti`})
                              </span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Importo Annuo</p>
                          <p className="font-semibold text-gray-900">
                            {company.importoContratto ? `€ ${Number(company.importoContratto).toLocaleString('it-IT')}` : 'N/D'}
                          </p>
                        </div>
                        <div className="md:col-span-3">
                          <p className="text-xs text-gray-500 mb-1">Note</p>
                          <p className="font-semibold text-gray-900">{company.noteContratto || '-'}</p>
                        </div>
                      </div>
                      {/* Pulsante invio email promemoria */}
                      {(getStatoContratto(company) === 'attenzione' || getStatoContratto(company) === 'in_scadenza') && (
                        <div className="mt-4">
                          <button
                            onClick={() => {
                              const emailData = sendContractExpiryEmail(company, getStatoContratto(company));
                              alert(`📧 Email inviata a: ${emailData.to}\n\nOggetto: ${emailData.subject}`);
                            }}
                            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                          >
                            <Mail className="w-4 h-4" />
                            Invia Email Promemoria Scadenza
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {companySubMenu === 'workers' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div className="relative">
                        <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Cerca lavoratori..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => setShowImportExcelModal(true)}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                          title="Importa lavoratori da file Excel"
                        >
                          <Upload className="w-4 h-4" />
                          Importa da Excel
                        </button>
                        <button 
                          onClick={() => handleExportElencoLavoratori(selectedCompany)}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                          title="Esporta elenco lavoratori con mansioni e ruoli"
                        >
                          <Download className="w-4 h-4" />
                          Export Lavoratori
                        </button>
                        <button 
                          onClick={() => openModal('newWorker')}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                        >
                          <Plus className="w-5 h-5" />
                          Nuovo Lavoratore
                        </button>
                      </div>
                    </div>

                    {/* 🆕 FILTRO STATO LAVORATORI */}
                    <div className="flex items-center gap-4 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <label className="text-sm font-medium text-gray-700">Mostra:</label>
                      <select
                        value={workerStatusFilter}
                        onChange={(e) => setWorkerStatusFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      >
                        <option value="all">Tutti i lavoratori ({companyWorkers.filter(w => !isDatoreDiLavoro(w, selectedCompany)).length})</option>
                        <option value="attivi">Solo attivi ({getLavoratoriAttivi(selectedCompany).filter(w => !isDatoreDiLavoro(w, selectedCompany)).length})</option>
                        <option value="dimessi">Solo dimessi ({getLavoratoriDimessi(selectedCompany).filter(w => !isDatoreDiLavoro(w, selectedCompany)).length})</option>
                      </select>
                      <div className="flex-1"></div>
                      <div className="text-sm text-gray-600">
                        {workerStatusFilter === 'all' && `Visualizzati: ${companyWorkers.filter(w => !isDatoreDiLavoro(w, selectedCompany)).length} lavoratori`}
                        {workerStatusFilter === 'attivi' && `Visualizzati: ${getLavoratoriAttivi(selectedCompany).filter(w => !isDatoreDiLavoro(w, selectedCompany)).length} lavoratori attivi`}
                        {workerStatusFilter === 'dimessi' && `Visualizzati: ${getLavoratoriDimessi(selectedCompany).filter(w => !isDatoreDiLavoro(w, selectedCompany)).length} lavoratori dimessi`}
                      </div>
                      <div className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
                        ℹ️ Il Datore di Lavoro è escluso dai conteggi (D.Lgs. 81/2008)
                      </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th 
                              className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:text-blue-600 select-none"
                              onClick={() => setWorkerSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                              title="Clicca per ordinare per cognome"
                            >
                              Nome {workerSortOrder === 'asc' ? '↑' : '↓'}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Mansione</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Ruoli</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cellulare</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Assunzione</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Stato</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // 🆕 Filtra in base al filtro stato selezionato
                            const filteredWorkers = getFilteredWorkersByStatus(companyWorkers);
                            
                            return filteredWorkers
                              .filter(w => 
                                w.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                w.cognome.toLowerCase().includes(searchTerm.toLowerCase())
                              )
                              .sort((a, b) => {
                                const cmp = a.cognome.localeCompare(b.cognome);
                                return workerSortOrder === 'asc' ? cmp : -cmp;
                              })
                              .map(worker => {
                                // 🆕 Verifica se è il Datore di Lavoro
                                const isDatore = isDatoreDiLavoro(worker, selectedCompany);
                                
                                return (
                                <tr key={worker.id} className={`hover:bg-gray-50 border-b border-gray-200 ${isDatore ? 'bg-amber-50' : ''}`}>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDatore ? 'bg-amber-100' : 'bg-blue-100'}`}>
                                        <span className={`font-semibold ${isDatore ? 'text-amber-600' : 'text-blue-600'}`}>{worker.nome[0]}{worker.cognome[0]}</span>
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <p className="font-semibold text-gray-900">{worker.nome} {worker.cognome}</p>
                                          {isDatore && (
                                            <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded border border-amber-400">
                                              <Building2 className="w-3 h-3" />
                                              DATORE
                                            </div>
                                          )}
                                        </div>
                                        <p className="text-sm text-gray-500">{worker.email}</p>
                                        {isDatore && (
                                          <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                                            <Shield className="w-3 h-3" />
                                            Ruoli cumulati (Art. 34 D.Lgs. 81/2008)
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                                      isDatore 
                                        ? 'bg-amber-100 text-amber-800 border border-amber-300' 
                                        : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {worker.mansione}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1">
                                      {worker.ruoli.map((ruolo, idx) => (
                                        <span key={idx} className={`px-2 py-1 text-xs font-medium rounded ${
                                          isDatore
                                            ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                            : 'bg-purple-100 text-purple-700'
                                        }`}>
                                          {ruolo}
                                        </span>
                                      ))}
                                      {worker.ruoli.length === 0 && <span className="text-sm text-gray-400">-</span>}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-600">{worker.cellulare || (isDatore ? 'Vedi anagrafica' : '-')}</td>
                                  <td className="px-6 py-4 text-sm text-gray-600">
                                    {isDatore ? (
                                      <span className="text-gray-500 italic">Non applicabile</span>
                                    ) : (
                                      worker.dataAssunzione ? formatDateOnly(worker.dataAssunzione) : '-'
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    {isLavoratoreAttivo(worker) ? (
                                      <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                        ATTIVO
                                      </span>
                                    ) : (
                                      <div>
                                        <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                          <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                                          DIMESSO
                                        </span>
                                        <p className="text-xs text-gray-500 mt-1">dal {formatDateOnly(worker.dataFine)}</p>
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    {isDatore ? (
                                      <div className="flex items-center gap-2">
                                        <button 
                                          onClick={() => {
                                            setSelectedWorkerForStorico(worker);
                                            setShowStoricoEventoModal(true);
                                          }}
                                          className="p-2 hover:bg-purple-50 rounded-lg text-purple-600 transition-colors"
                                          title="Storico Eventi"
                                        >
                                          <Clock className="w-4 h-4" />
                                        </button>
                                        <div className="group relative">
                                          <Info className="w-4 h-4 text-amber-600 cursor-help" />
                                          <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-72 p-3 bg-amber-50 border-2 border-amber-300 rounded-lg shadow-xl z-10">
                                            <p className="text-xs text-amber-900 font-medium">
                                              ℹ️ Per modificare o rimuovere il Datore dall'elenco lavoratori, vai in <strong>Anagrafica Azienda</strong> e modifica i dati o deseleziona "Il Datore cumula ruoli operativi"
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex gap-2">
                                        <button 
                                          onClick={() => {
                                            setSelectedWorkerForStorico(worker);
                                            setShowStoricoEventoModal(true);
                                          }}
                                          className="p-2 hover:bg-purple-50 rounded-lg text-purple-600 transition-colors"
                                          title="Storico Eventi"
                                        >
                                          <Clock className="w-4 h-4" />
                                        </button>
                                        <button 
                                          onClick={() => openModal('editWorker', worker)}
                                          className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
                                          title="Modifica"
                                        >
                                          <Edit2 className="w-4 h-4" />
                                        </button>
                                        {isLavoratoreAttivo(worker) ? (
                                          <button 
                                            onClick={() => handleDismissioneLavoratore(worker.id)}
                                            className="p-2 hover:bg-orange-50 rounded-lg text-orange-600 transition-colors"
                                            title="Dimetti lavoratore"
                                          >
                                            <UserMinus className="w-4 h-4" />
                                          </button>
                                        ) : (
                                          <button 
                                            onClick={() => handleRiattivazioneLavoratore(worker.id)}
                                            className="p-2 hover:bg-green-50 rounded-lg text-green-600 transition-colors"
                                            title="Riattiva lavoratore"
                                          >
                                            <CheckCircle className="w-4 h-4" />
                                          </button>
                                        )}
                                        <button 
                                          onClick={() => handleDeleteWorker(worker.id)}
                                          className="p-2 hover:bg-red-50 rounded-lg text-red-600 transition-colors"
                                          title="Elimina"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                              });
                          })()}
                        </tbody>
                      </table>
                    </div>

                    {(() => {
                      const filteredWorkers = getFilteredWorkersByStatus(companyWorkers);
                      const searchFiltered = filteredWorkers.filter(w => 
                        w.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        w.cognome.toLowerCase().includes(searchTerm.toLowerCase())
                      );
                      return searchFiltered.length === 0 && (
                        <div className="text-center py-12 bg-gray-50 rounded-lg">
                          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-600">
                            {workerStatusFilter === 'attivi' && 'Nessun lavoratore attivo'}
                            {workerStatusFilter === 'dimessi' && 'Nessun lavoratore dimesso'}
                            {workerStatusFilter === 'all' && 'Nessun lavoratore trovato'}
                          </p>
                        </div>
                      );
                    })()}

                  </div>
                )}

                {companySubMenu === 'roles' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900">Ruoli Aziendali</h3>
                      <button
                        onClick={() => handleExportRuoliAziendali(selectedCompany)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                        title="Esporta elenco completo ruoli organizzativi e lavoratori"
                      >
                        <Download className="w-4 h-4" />
                        Export Ruoli Aziendali
                      </button>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                      <MousePointerClick className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-800">
                        <strong>Suggerimento:</strong> Clicca su una card per visualizzare l'elenco nominativo dei lavoratori con quel ruolo
                      </p>
                    </div>
                    
                    {/* 🆕 RUOLI SPECIALI (Datore, RSPP, Medico) */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Ruoli Organizzativi</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Datore di Lavoro */}
                        {company.datoreLavoro && company.datoreLavoro.nome && (
                          <div 
                            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-300 hover:shadow-lg hover:border-blue-500 transition-all cursor-pointer"
                            onClick={() => setSelectedSpecialRole('datore')}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Building2 className="w-5 h-5 text-blue-600" />
                              <span className="px-2 py-1 bg-blue-200 text-blue-800 text-xs font-bold rounded">DATORE</span>
                            </div>
                            <p className="font-bold text-gray-900 text-sm">Datore di Lavoro</p>
                            <p className="text-sm text-gray-700 mt-1">{company.datoreLavoro.nome} {company.datoreLavoro.cognome}</p>
                            <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
                              <MousePointerClick className="w-3 h-3" />
                              <span>Clicca per dettagli</span>
                            </div>
                          </div>
                        )}

                        {/* RSPP */}
                        {company.rspp && company.rspp.nome && (
                          <div 
                            className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-300 hover:shadow-lg hover:border-orange-500 transition-all cursor-pointer"
                            onClick={() => setSelectedSpecialRole('rspp')}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Shield className="w-5 h-5 text-orange-600" />
                              <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs font-bold rounded">RSPP</span>
                            </div>
                            <p className="font-bold text-gray-900 text-sm">Responsabile SPP</p>
                            <p className="text-sm text-gray-700 mt-1">{company.rspp.nome} {company.rspp.cognome}</p>
                            <div className="mt-2 flex items-center gap-1 text-xs text-orange-600">
                              <MousePointerClick className="w-3 h-3" />
                              <span>Clicca per dettagli</span>
                            </div>
                          </div>
                        )}

                        {/* Medico Competente */}
                        {company.medicoCompetente && company.medicoCompetente.nome && (
                          <div 
                            className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-300 hover:shadow-lg hover:border-green-500 transition-all cursor-pointer"
                            onClick={() => setSelectedSpecialRole('medico')}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <FileCheck className="w-5 h-5 text-green-600" />
                              <span className="px-2 py-1 bg-green-200 text-green-800 text-xs font-bold rounded">MEDICO</span>
                            </div>
                            <p className="font-bold text-gray-900 text-sm">Medico Competente</p>
                            <p className="text-sm text-gray-700 mt-1">{company.medicoCompetente.nome} {company.medicoCompetente.cognome}</p>
                            <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                              <MousePointerClick className="w-3 h-3" />
                              <span>Clicca per dettagli</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Dettaglio Ruolo Speciale Selezionato */}
                    {selectedSpecialRole && (
                      <div className="border-t border-gray-200 pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900">
                            Elenco Lavoratori - {
                              selectedSpecialRole === 'datore' ? 'Datore di Lavoro' :
                              selectedSpecialRole === 'rspp' ? 'RSPP' :
                              'Medico Competente'
                            }
                          </h3>
                          <button 
                            onClick={() => setSelectedSpecialRole(null)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="bg-white rounded-lg border border-gray-200">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome Completo</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Mansione</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cellulare</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedSpecialRole === 'datore' && (
                                <tr className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="font-semibold text-blue-600">
                                          {company.datoreLavoro.nome[0]}{company.datoreLavoro.cognome[0]}
                                        </span>
                                      </div>
                                      <span className="font-semibold text-gray-900">
                                        {company.datoreLavoro.nome} {company.datoreLavoro.cognome}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-gray-600">Datore di Lavoro</td>
                                  <td className="px-6 py-4 text-gray-600">{company.datoreLavoro.email}</td>
                                  <td className="px-6 py-4 text-gray-600">{company.datoreLavoro.cellulare || '-'}</td>
                                </tr>
                              )}
                              {selectedSpecialRole === 'rspp' && (
                                <tr className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                        <span className="font-semibold text-orange-600">
                                          {company.rspp.nome[0]}{company.rspp.cognome[0]}
                                        </span>
                                      </div>
                                      <span className="font-semibold text-gray-900">
                                        {company.rspp.nome} {company.rspp.cognome}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-gray-600">RSPP</td>
                                  <td className="px-6 py-4 text-gray-600">{company.rspp.email}</td>
                                  <td className="px-6 py-4 text-gray-600">{company.rspp.cellulare || '-'}</td>
                                </tr>
                              )}
                              {selectedSpecialRole === 'medico' && (
                                <tr className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <span className="font-semibold text-green-600">
                                          {company.medicoCompetente.nome[0]}{company.medicoCompetente.cognome[0]}
                                        </span>
                                      </div>
                                      <span className="font-semibold text-gray-900">
                                        {company.medicoCompetente.nome} {company.medicoCompetente.cognome}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-gray-600">Medico Competente</td>
                                  <td className="px-6 py-4 text-gray-600">{company.medicoCompetente.email}</td>
                                  <td className="px-6 py-4 text-gray-600">{company.medicoCompetente.cellulare || '-'}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    <h3 className="text-lg font-bold text-gray-900">Ruoli Assegnati - Dashboard</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[...new Set(companyWorkers.filter(w => isLavoratoreAttivo(w)).flatMap(w => w.ruoli))].map((ruolo, idx) => {
                        const count = companyWorkers.filter(w => isLavoratoreAttivo(w) && w.ruoli.includes(ruolo)).length;
                        return (
                          <div 
                            key={idx} 
                            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200 hover:shadow-lg hover:border-purple-400 transition-all cursor-pointer"
                            onClick={() => setSelectedRoleForList(ruolo)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Shield className="w-5 h-5 text-purple-600" />
                              <span className="text-2xl font-bold text-purple-900">{count}</span>
                            </div>
                            <p className="font-semibold text-gray-900">{ruolo}</p>
                            <p className="text-sm text-gray-600 mt-1">{count} lavoratori</p>
                            <div className="mt-2 flex items-center gap-1 text-xs text-purple-600">
                              <MousePointerClick className="w-3 h-3" />
                              <span>Clicca per dettagli</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {[...new Set(companyWorkers.filter(w => isLavoratoreAttivo(w)).flatMap(w => w.ruoli))].length === 0 && (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600">Nessun ruolo assegnato ai lavoratori attivi</p>
                      </div>
                    )}

                    {/* Elenco nominativo per ruolo selezionato */}
                    {selectedRoleForList && (
                      <div className="border-t border-gray-200 pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900">
                            Elenco Lavoratori - {selectedRoleForList}
                          </h3>
                          <button 
                            onClick={() => setSelectedRoleForList(null)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="bg-white rounded-lg border border-gray-200">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome Completo</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Mansione</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cellulare</th>
                              </tr>
                            </thead>
                            <tbody>
                              {companyWorkers
                                .filter(w => w.ruoli.includes(selectedRoleForList))
                                .map(worker => (
                                  <tr key={worker.id} className="border-b border-gray-200 hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                      <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                          <span className="font-semibold text-purple-600">
                                            {worker.nome[0]}{worker.cognome[0]}
                                          </span>
                                        </div>
                                        <span className="font-semibold text-gray-900">
                                          {worker.nome} {worker.cognome}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 text-gray-600">{worker.mansione}</td>
                                    <td className="px-6 py-4 text-gray-600">{worker.email}</td>
                                    <td className="px-6 py-4 text-gray-600">{worker.cellulare}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {companySubMenu === 'mansioni' && (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                      <MousePointerClick className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-800">
                        <strong>Suggerimento:</strong> Clicca su una card per visualizzare l'elenco nominativo dei lavoratori con quella mansione
                      </p>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900">Mansioni Attive</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[...new Set(companyWorkers.filter(w => isLavoratoreAttivo(w)).map(w => w.mansione))].map((mansione, idx) => {
                        const count = companyWorkers.filter(w => isLavoratoreAttivo(w) && w.mansione === mansione).length;
                        return (
                          <div 
                            key={idx} 
                            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200 hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer"
                            onClick={() => setSelectedMansioneForList(mansione)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Briefcase className="w-5 h-5 text-blue-600" />
                              <span className="text-2xl font-bold text-blue-900">{count}</span>
                            </div>
                            <p className="font-semibold text-gray-900">{mansione}</p>
                            <p className="text-sm text-gray-600 mt-1">{count} lavoratori</p>
                            <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
                              <MousePointerClick className="w-3 h-3" />
                              <span>Clicca per dettagli</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {[...new Set(companyWorkers.filter(w => isLavoratoreAttivo(w)).map(w => w.mansione))].length === 0 && (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600">Nessuna mansione assegnata ai lavoratori attivi</p>
                      </div>
                    )}

                    {/* Elenco nominativo per mansione selezionata */}
                    {selectedMansioneForList && (
                      <div className="border-t border-gray-200 pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900">
                            Elenco Lavoratori - {selectedMansioneForList}
                          </h3>
                          <button 
                            onClick={() => setSelectedMansioneForList(null)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="bg-white rounded-lg border border-gray-200">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome Completo</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cellulare</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Ruoli</th>
                              </tr>
                            </thead>
                            <tbody>
                              {companyWorkers
                                .filter(w => w.mansione === selectedMansioneForList)
                                .map(worker => (
                                  <tr key={worker.id} className="border-b border-gray-200 hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                      <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                          <span className="font-semibold text-blue-600">
                                            {worker.nome[0]}{worker.cognome[0]}
                                          </span>
                                        </div>
                                        <span className="font-semibold text-gray-900">
                                          {worker.nome} {worker.cognome}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 text-gray-600">{worker.email}</td>
                                    <td className="px-6 py-4 text-gray-600">{worker.cellulare}</td>
                                    <td className="px-6 py-4">
                                      <div className="flex flex-wrap gap-1">
                                        {worker.ruoli.map((ruolo, idx) => (
                                          <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                                            {ruolo}
                                          </span>
                                        ))}
                                        {worker.ruoli.length === 0 && <span className="text-sm text-gray-400">-</span>}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestione Aziende</h1>
                <p className="text-gray-600">Elenco di tutte le aziende clienti</p>
              </div>
              <button 
                onClick={() => openModal('newCompany')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-colors shadow-lg"
              >
                <Plus className="w-5 h-5" />
                Nuova Azienda
              </button>
            </div>

            {/* 🆕 Filtri Aziende */}
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Ricerca */}
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <Search className="w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca azienda..."
                    value={searchCompany || ''}
                    onChange={(e) => setSearchCompany(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {/* Tipo Contratto */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Tipo:</label>
                  <select
                    value={filterTipoContratto || 'all'}
                    onChange={(e) => setFilterTipoContratto(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Tutti</option>
                    <option value="gestione_completa">Gestione Completa</option>
                    <option value="solo_piattaforma">Solo Piattaforma</option>
                  </select>
                </div>
                {/* Stato Contratto */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Stato:</label>
                  <select
                    value={filterStatoContratto || 'all'}
                    onChange={(e) => setFilterStatoContratto(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Tutti</option>
                    <option value="attivo">Attivi</option>
                    <option value="attenzione">In Attenzione (≤60gg)</option>
                    <option value="in_scadenza">In Scadenza (≤30gg)</option>
                    <option value="scaduto">Scaduti</option>
                    <option value="disattivata">Solo Disattivate</option>
                  </select>
                </div>
                {/* 🆕 Checkbox mostra disattivate (visibile solo con filtro "Tutti") */}
                {filterStatoContratto === 'all' && (
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showDisabledCompanies}
                        onChange={(e) => setShowDisabledCompanies(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Includi disattivate</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Tabella Aziende */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ragione Sociale</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scadenza</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Dipendenti</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Compliance</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {companies
                      .filter(company => {
                        // Filtro ricerca
                        if (searchCompany && !company.ragioneSociale.toLowerCase().includes(searchCompany.toLowerCase())) {
                          return false;
                        }
                        // Filtro tipo contratto
                        if (filterTipoContratto && filterTipoContratto !== 'all' && company.tipoContratto !== filterTipoContratto) {
                          return false;
                        }
                        
                        const stato = getStatoContratto(company);
                        
                        // 🆕 Logica filtro disattivate:
                        if (filterStatoContratto === 'disattivata') {
                          // Filtro "Solo Disattivate" → mostra SOLO le disattivate
                          return stato === 'disattivata';
                        } else if (filterStatoContratto === 'all') {
                          // Filtro "Tutti" → nascondi disattivate (a meno che checkbox sia attivo)
                          if (stato === 'disattivata' && !showDisabledCompanies) {
                            return false;
                          }
                        } else {
                          // Altri filtri (Attivi, Scaduti, etc.) → mostra solo quello stato, MAI le disattivate
                          if (stato !== filterStatoContratto) return false;
                        }
                        
                        return true;
                      })
                      .map(company => {
                      const companyWorkers = workers.filter(w => w.companyId === company.id);
                      const companyComms = communications.filter(c => c.aziendaId === company.id && c.stato === 'Pubblicato');
                      const statoBadge = getStatoContrattoBadge(company);
                      const tipoBadge = getTipoContrattoBadge(company.tipoContratto);
                      const giorniScadenza = getGiorniAllaScadenza(company.dataScadenzaContratto);
                      
                      let compliance = 100;
                      if (companyComms.length > 0) {
                        const total = companyComms.reduce((sum, comm) => {
                          const stats = calculateCommunicationStats(comm);
                          return sum + (stats.destinatari > 0 ? (stats.letti / stats.destinatari * 100) : 0);
                        }, 0);
                        compliance = Math.round(total / companyComms.length);
                      }

                      return (
                        <tr 
                          key={company.id} 
                          className={`hover:bg-gray-50 cursor-pointer transition-colors ${!company.attiva ? 'opacity-60 bg-gray-50' : ''}`}
                          onClick={() => {
                            setSelectedCompany(company.id);
                            setCompanySubMenu('anagrafica');
                          }}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                company.tipoContratto === 'solo_piattaforma' ? 'bg-purple-100' : 'bg-blue-100'
                              }`}>
                                <Building2 className={`w-4 h-4 ${
                                  company.tipoContratto === 'solo_piattaforma' ? 'text-purple-600' : 'text-blue-600'
                                }`} />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{company.ragioneSociale}</p>
                                <p className="text-xs text-gray-500">{company.citta}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${tipoBadge.bg} ${tipoBadge.text}`}>
                              {company.tipoContratto === 'solo_piattaforma' ? 'PIATTAFORMA' : 'GESTITA'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statoBadge.bg} ${statoBadge.text}`}>
                              {statoBadge.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {company.dataScadenzaContratto ? (
                              <div className="text-sm">
                                <p className="text-gray-900">{formatDateOnly(company.dataScadenzaContratto)}</p>
                                <p className={`text-xs ${
                                  giorniScadenza < 0 ? 'text-red-600' :
                                  giorniScadenza <= 30 ? 'text-orange-600' :
                                  giorniScadenza <= 60 ? 'text-yellow-600' :
                                  'text-green-600'
                                }`}>
                                  {giorniScadenza < 0 ? `Scaduto da ${Math.abs(giorniScadenza)}gg` : `${giorniScadenza}gg rimasti`}
                                </p>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">N/D</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm font-semibold text-gray-900">
                              {companyWorkers.filter(w => !isDatoreDiLavoro(w, company.id)).length}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {company.tipoContratto === 'gestione_completa' ? (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                compliance >= 80 ? 'bg-green-100 text-green-700' :
                                compliance >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {compliance}%
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-1">
                              {/* Toggle Attivazione */}
                              <button
                                onClick={() => {
                                  setCompanies(companies.map(c => 
                                    c.id === company.id ? { ...c, attiva: !c.attiva } : c
                                  ));
                                }}
                                className={`p-1.5 rounded transition-colors ${
                                  company.attiva 
                                    ? 'hover:bg-yellow-50 text-yellow-600' 
                                    : 'hover:bg-green-50 text-green-600'
                                }`}
                                title={company.attiva ? 'Disattiva' : 'Attiva'}
                              >
                                {company.attiva ? <Lock className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => openModal('editCompany', company)}
                                className="p-1.5 hover:bg-blue-50 rounded text-blue-600 transition-colors"
                                title="Modifica"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCompany(company.id)}
                                className="p-1.5 hover:bg-red-50 rounded text-red-600 transition-colors"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {companies.length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl shadow-md">
                <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">Nessuna azienda in archivio</p>
              </div>
            )}
          </div>
        );

      case 'company':
        // 🆕 GESTIONE AZIENDA (Profilo AZIENDA) - Singola azienda dell'utente loggato
        const myCompany = companies.find(c => String(c.id) === String(currentUser.companyId));
        const myCompanyWorkers = workers.filter(w => w.companyId === currentUser.companyId);
        
        if (!myCompany) {
          return (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestione Azienda</h1>
                <p className="text-gray-600">Carica i dati della tua azienda</p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Dati Azienda Non Trovati</h2>
                <p className="text-gray-600 mb-6">Inserisci i dati della tua azienda per iniziare</p>
                <button
                  onClick={() => openModal('newCompany')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Inserisci Dati Azienda
                </button>
              </div>
            </div>
          );
        }
        
        // 🆕 Blocco accesso azienda DISATTIVATA (blocco totale)
        if (!myCompany.attiva) {
          return (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
                <Lock className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-red-700 mb-2">Accesso Non Disponibile</h2>
                <p className="text-red-600 mb-4">
                  L'accesso alla piattaforma è stato disabilitato per la tua azienda.
                </p>
                <p className="text-gray-600 mb-6">
                  Per riattivare il servizio, contatta il tuo consulente di riferimento.
                </p>
                <div className="bg-white rounded-lg p-4 inline-block">
                  <p className="text-sm text-gray-500">Azienda</p>
                  <p className="font-semibold text-gray-900">{myCompany.ragioneSociale}</p>
                </div>
              </div>
            </div>
          );
        }
        
        // 🆕 Stato SCADUTO = solo visualizzazione
        const isContrattoScadutoAzienda = getStatoContratto(myCompany) === 'scaduto';
        
        return (
          <div className="space-y-6">
            {/* 🆕 Banner avviso contratto scaduto */}
            {isContrattoScadutoAzienda && (
              <div className="bg-orange-50 border border-orange-300 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-orange-800">Contratto Scaduto - Modalità Solo Visualizzazione</p>
                    <p className="text-sm text-orange-700">
                      Il contratto SafeTracker è scaduto il {formatDateOnly(myCompany.dataScadenzaContratto)}. 
                      Puoi consultare i dati esistenti ma non puoi effettuare modifiche. 
                      Per riattivare il servizio, contatta il tuo consulente di riferimento.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{myCompany.ragioneSociale}</h1>
                {/* Pulsante rimosso - la modifica si fa dalla sezione Anagrafica */}
              </div>
              
              <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
                <button
                  onClick={() => setCompanySubMenu('anagrafica')}
                  className={`px-4 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                    companySubMenu === 'anagrafica'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Anagrafica
                  </div>
                </button>
                <button
                  onClick={() => setCompanySubMenu('workers')}
                  className={`px-4 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                    companySubMenu === 'workers'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Lavoratori ({myCompanyWorkers.filter(w => !isDatoreDiLavoro(w, currentUser.companyId)).length})
                  </div>
                </button>
                <button
                  onClick={() => setCompanySubMenu('roles')}
                  className={`px-4 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                    companySubMenu === 'roles'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Ruoli Aziendali
                  </div>
                </button>
                <button
                  onClick={() => setCompanySubMenu('mansioni')}
                  className={`px-4 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                    companySubMenu === 'mansioni'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Mansioni
                  </div>
                </button>
              </div>

              {companySubMenu === 'anagrafica' && (
                <div className="space-y-6">
                  {/* Header con pulsante Modifica */}
                  <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900">Anagrafica Aziendale</h3>
                    {/* Pulsante nascosto se contratto scaduto */}
                    {!isContrattoScadutoAzienda && (
                      <button
                        onClick={() => openModal('editCompany', myCompany)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Modifica Anagrafica
                      </button>
                    )}
                  </div>

                  {/* 🆕 Logo Aziendale */}
                  <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-shrink-0">
                      {myCompany.logo ? (
                        <div className="relative">
                          <img 
                            src={myCompany.logo} 
                            alt="Logo Aziendale" 
                            className="w-24 h-24 object-contain border rounded-lg bg-white p-2"
                          />
                          {/* Pulsante rimuovi nascosto se scaduto */}
                          {!isContrattoScadutoAzienda && (
                            <button
                              onClick={() => {
                                setCompanies(companies.map(c => 
                                  c.id === myCompany.id ? {...c, logo: null} : c
                                ));
                              }}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Logo Aziendale</h4>
                      <p className="text-sm text-gray-600 mb-3">Il logo sarà visibile nella sidebar e nel profilo dei lavoratori</p>
                      {/* Pulsante carica nascosto se scaduto */}
                      {!isContrattoScadutoAzienda ? (
                        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                          <Upload className="w-4 h-4" />
                          {myCompany.logo ? 'Cambia Logo' : 'Carica Logo'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setCompanies(companies.map(c => 
                                    c.id === myCompany.id ? {...c, logo: reader.result} : c
                                  ));
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      ) : (
                        <p className="text-sm text-orange-600 italic">Modifica non disponibile - contratto scaduto</p>
                      )}
                      {!isContrattoScadutoAzienda && (
                        <p className="text-xs text-gray-500 mt-2">Formati: PNG, JPG, SVG. Max 2MB.</p>
                      )}
                    </div>
                  </div>

                  {/* Dati Azienda */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Dati Azienda</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Ragione Sociale</p>
                        <p className="font-semibold text-gray-900">{myCompany.ragioneSociale}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Partita IVA</p>
                        <p className="font-semibold text-gray-900">{myCompany.partitaIva}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Indirizzo</p>
                        <p className="font-semibold text-gray-900">{myCompany.indirizzo}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Città</p>
                        <p className="font-semibold text-gray-900">{myCompany.citta}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">PEC</p>
                        <p className="font-semibold text-gray-900">{myCompany.pec}</p>
                      </div>
                    </div>
                  </div>

                  {/* Datore di Lavoro */}
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Datore di Lavoro</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Nome</p>
                        <p className="font-semibold text-gray-900">{myCompany.datoreLavoro.nome}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Cognome</p>
                        <p className="font-semibold text-gray-900">{myCompany.datoreLavoro.cognome}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Email</p>
                        <p className="font-semibold text-gray-900">{myCompany.datoreLavoro.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Medico Competente */}
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Medico Competente</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-green-50 p-4 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Nome e Cognome</p>
                        <p className="font-semibold text-gray-900">
                          {myCompany.medicoCompetente.nome} {myCompany.medicoCompetente.cognome}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Email</p>
                        <p className="font-semibold text-gray-900">{myCompany.medicoCompetente.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">PEC</p>
                        <p className="font-semibold text-gray-900">{myCompany.medicoCompetente.pec}</p>
                      </div>
                    </div>
                  </div>

                  {/* RSPP */}
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">RSPP</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-purple-50 p-4 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Nome e Cognome</p>
                        <p className="font-semibold text-gray-900">
                          {myCompany.rspp.nome} {myCompany.rspp.cognome}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Email</p>
                        <p className="font-semibold text-gray-900">{myCompany.rspp.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">PEC</p>
                        <p className="font-semibold text-gray-900">{myCompany.rspp.pec}</p>
                      </div>
                    </div>
                  </div>

                  {/* Codici ATECO */}
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Codici ATECO</h3>
                    <div className="space-y-3">
                      {myCompany.codiciAteco.map((ateco, idx) => (
                        <div key={idx} className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                          <p className="font-semibold text-gray-900">{ateco.codice}</p>
                          <p className="text-sm text-gray-600">{ateco.descrizione}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {companySubMenu === 'workers' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="relative">
                      <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Cerca lavoratori..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex gap-2">
                      {/* Pulsanti azione nascosti se contratto scaduto */}
                      {!isContrattoScadutoAzienda && (
                        <>
                          <button
                            onClick={() => setShowImportExcelModal(true)}
                            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                            title="Importa lavoratori da file Excel"
                          >
                            <Upload className="w-4 h-4" />
                            Importa da Excel
                          </button>
                          <button
                            onClick={() => handleExportElencoLavoratori(currentUser.companyId)}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                            title="Esporta elenco lavoratori con mansioni e ruoli"
                          >
                            <Download className="w-4 h-4" />
                            Export Lavoratori
                          </button>
                          <button 
                            onClick={() => {
                              setSelectedCompany(currentUser.companyId);
                              openModal('newWorker');
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                          >
                            <Plus className="w-5 h-5" />
                            Nuovo Lavoratore
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* 🆕 FILTRO STATO LAVORATORI */}
                  <div className="flex items-center gap-4 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <label className="text-sm font-medium text-gray-700">Mostra:</label>
                    <select
                      value={workerStatusFilter}
                      onChange={(e) => setWorkerStatusFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="all">Tutti i lavoratori ({myCompanyWorkers.length})</option>
                      <option value="attivi">Solo attivi ({getLavoratoriAttivi(currentUser.companyId).length})</option>
                      <option value="dimessi">Solo dimessi ({getLavoratoriDimessi(currentUser.companyId).length})</option>
                    </select>
                    <div className="flex-1"></div>
                    <div className="text-sm text-gray-600">
                      {workerStatusFilter === 'all' && `Visualizzati: ${myCompanyWorkers.length} lavoratori`}
                      {workerStatusFilter === 'attivi' && `Visualizzati: ${getLavoratoriAttivi(currentUser.companyId).length} lavoratori attivi`}
                      {workerStatusFilter === 'dimessi' && `Visualizzati: ${getLavoratoriDimessi(currentUser.companyId).length} lavoratori dimessi`}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Mansione</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Ruoli</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cellulare</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Assunzione</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Stato</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // 🆕 Filtra in base al filtro stato selezionato
                          const filteredWorkers = getFilteredWorkersByStatus(myCompanyWorkers);
                          
                          return filteredWorkers
                            .filter(w => 
                              w.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              w.cognome.toLowerCase().includes(searchTerm.toLowerCase())
                            )
                            .map(worker => {
                              // 🏷️ Verifica se è DATORE DI LAVORO con cumulo ruoli
                              const isDatore = isDatoreDiLavoro(worker, currentUser.companyId);
                              
                              return (
                                <tr key={worker.id} className={`hover:bg-gray-50 border-b border-gray-200 ${isDatore ? 'bg-amber-50' : ''}`}>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDatore ? 'bg-amber-100' : 'bg-blue-100'}`}>
                                        <span className={`font-semibold ${isDatore ? 'text-amber-600' : 'text-blue-600'}`}>{worker.nome[0]}{worker.cognome[0]}</span>
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <p className="font-semibold text-gray-900">{worker.nome} {worker.cognome}</p>
                                          {isDatore && (
                                            <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded border border-amber-400">
                                              <Building2 className="w-3 h-3" />
                                              DATORE
                                            </div>
                                          )}
                                        </div>
                                        <p className="text-sm text-gray-500">{worker.email}</p>
                                        {isDatore && (
                                          <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                                            <Shield className="w-3 h-3" />
                                            Ruoli cumulati (Art. 34 D.Lgs. 81/2008)
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                                      isDatore 
                                        ? 'bg-amber-100 text-amber-800 border border-amber-300' 
                                        : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {worker.mansione}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1">
                                      {worker.ruoli.map((ruolo, idx) => (
                                        <span key={idx} className={`px-2 py-1 text-xs font-medium rounded ${
                                          isDatore
                                            ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                            : 'bg-purple-100 text-purple-700'
                                        }`}>
                                          {ruolo}
                                        </span>
                                      ))}
                                      {worker.ruoli.length === 0 && <span className="text-sm text-gray-400">-</span>}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-600">{worker.cellulare || (isDatore ? 'Vedi anagrafica' : '-')}</td>
                                  <td className="px-6 py-4 text-sm text-gray-600">
                                    {isDatore ? (
                                      <span className="text-gray-500 italic">Non applicabile</span>
                                    ) : (
                                      worker.dataAssunzione ? formatDateOnly(worker.dataAssunzione) : '-'
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    {isLavoratoreAttivo(worker) ? (
                                      <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                        ATTIVO
                                      </span>
                                    ) : (
                                      <div>
                                        <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                          <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                                          DIMESSO
                                        </span>
                                        <p className="text-xs text-gray-500 mt-1">dal {formatDateOnly(worker.dataFine)}</p>
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    {/* 🆕 Azioni nascoste se contratto scaduto */}
                                    {isContrattoScadutoAzienda ? (
                                      <span className="text-xs text-orange-600 italic">Solo visualizzazione</span>
                                    ) : (
                                      <>
                                        {/* 🎯 ICONE DIFFERENZIATE: DATORE vs LAVORATORE */}
                                        {isDatore ? (
                                          // 🏷️ DATORE: Solo Storico + Info tooltip
                                          <div className="flex items-center gap-2">
                                            <button 
                                              onClick={() => {
                                                setSelectedWorkerForStorico(worker);
                                                setShowStoricoEventoModal(true);
                                              }}
                                              className="p-2 hover:bg-purple-50 rounded-lg text-purple-600 transition-colors"
                                              title="Storico Eventi"
                                            >
                                              <Clock className="w-4 h-4" />
                                            </button>
                                            <div className="group relative">
                                              <Info className="w-4 h-4 text-amber-600 cursor-help" />
                                              <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-72 p-3 bg-amber-50 border-2 border-amber-300 rounded-lg shadow-xl z-10">
                                                <p className="text-xs text-amber-900 font-semibold mb-2">ℹ️ Datore di Lavoro con cumulo ruoli</p>
                                                <p className="text-xs text-amber-800">
                                                  Il Datore di Lavoro può svolgere direttamente ruoli aziendali secondo l'Art. 34 D.Lgs. 81/2008.
                                                  Non è conteggiato come lavoratore dipendente e non può essere modificato o eliminato da questa sezione.
                                                </p>
                                                <p className="text-xs text-amber-700 mt-2 italic">
                                                  Per modificare i dati, utilizzare la sezione Anagrafica.
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          // 👷 LAVORATORE: Tutte le azioni
                                          <div className="flex gap-2">
                                            <button 
                                              onClick={() => {
                                                setSelectedWorkerForStorico(worker);
                                                setShowStoricoEventoModal(true);
                                              }}
                                              className="p-2 hover:bg-purple-50 rounded-lg text-purple-600 transition-colors"
                                              title="Storico Eventi"
                                            >
                                              <Clock className="w-4 h-4" />
                                            </button>
                                            <button 
                                              onClick={() => openModal('editWorker', worker)}
                                              className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
                                              title="Modifica"
                                            >
                                              <Edit2 className="w-4 h-4" />
                                            </button>
                                            {isLavoratoreAttivo(worker) ? (
                                              <button 
                                                onClick={() => handleDismissioneLavoratore(worker.id)}
                                                className="p-2 hover:bg-orange-50 rounded-lg text-orange-600 transition-colors"
                                                title="Dimetti lavoratore"
                                              >
                                                <UserMinus className="w-4 h-4" />
                                              </button>
                                            ) : (
                                              <button 
                                                onClick={() => handleRiattivazioneLavoratore(worker.id)}
                                                className="p-2 hover:bg-green-50 rounded-lg text-green-600 transition-colors"
                                                title="Riattiva lavoratore"
                                              >
                                                <CheckCircle className="w-4 h-4" />
                                              </button>
                                            )}
                                            <button 
                                              onClick={() => handleDeleteWorker(worker.id)}
                                              className="p-2 hover:bg-red-50 rounded-lg text-red-600 transition-colors"
                                              title="Elimina"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </td>
                                </tr>
                              );
                            });
                        })()}
                      </tbody>
                    </table>
                  </div>

                  {(() => {
                    const filteredWorkers = getFilteredWorkersByStatus(myCompanyWorkers);
                    const searchFiltered = filteredWorkers.filter(w => 
                      w.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      w.cognome.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                    return searchFiltered.length === 0 && (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600">
                          {workerStatusFilter === 'attivi' && 'Nessun lavoratore attivo'}
                          {workerStatusFilter === 'dimessi' && 'Nessun lavoratore dimesso'}
                          {workerStatusFilter === 'all' && 'Nessun lavoratore trovato'}
                        </p>
                      </div>
                    );
                  })()}

                </div>
              )}

              {companySubMenu === 'roles' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Ruoli Aziendali</h3>
                    {/* Export nascosto se scaduto */}
                    {!isContrattoScadutoAzienda && (
                      <button
                        onClick={() => handleExportRuoliAziendali(currentUser.companyId)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                        title="Esporta elenco completo ruoli organizzativi e lavoratori"
                      >
                        <Download className="w-4 h-4" />
                        Export Ruoli Aziendali
                      </button>
                    )}
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                    <MousePointerClick className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800">
                      <strong>Suggerimento:</strong> Clicca su una card per visualizzare l'elenco nominativo dei lavoratori con quel ruolo
                    </p>
                  </div>
                  
                  {/* 🆕 RUOLI SPECIALI (Datore, RSPP, Medico) */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Ruoli Organizzativi</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Datore di Lavoro */}
                      {myCompany.datoreLavoro && myCompany.datoreLavoro.nome && (
                        <div 
                          className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-300 hover:shadow-lg hover:border-blue-500 transition-all cursor-pointer"
                          onClick={() => setSelectedSpecialRole('datore')}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Building2 className="w-5 h-5 text-blue-600" />
                            <span className="px-2 py-1 bg-blue-200 text-blue-800 text-xs font-bold rounded">DATORE</span>
                          </div>
                          <p className="font-bold text-gray-900 text-sm">Datore di Lavoro</p>
                          <p className="text-sm text-gray-700 mt-1">{myCompany.datoreLavoro.nome} {myCompany.datoreLavoro.cognome}</p>
                          <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
                            <MousePointerClick className="w-3 h-3" />
                            <span>Clicca per dettagli</span>
                          </div>
                        </div>
                      )}

                      {/* RSPP */}
                      {myCompany.rspp && myCompany.rspp.nome && (
                        <div 
                          className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-300 hover:shadow-lg hover:border-orange-500 transition-all cursor-pointer"
                          onClick={() => setSelectedSpecialRole('rspp')}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Shield className="w-5 h-5 text-orange-600" />
                            <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs font-bold rounded">RSPP</span>
                          </div>
                          <p className="font-bold text-gray-900 text-sm">Responsabile SPP</p>
                          <p className="text-sm text-gray-700 mt-1">{myCompany.rspp.nome} {myCompany.rspp.cognome}</p>
                          <div className="mt-2 flex items-center gap-1 text-xs text-orange-600">
                            <MousePointerClick className="w-3 h-3" />
                            <span>Clicca per dettagli</span>
                          </div>
                        </div>
                      )}

                      {/* Medico Competente */}
                      {myCompany.medicoCompetente && myCompany.medicoCompetente.nome && (
                        <div 
                          className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-300 hover:shadow-lg hover:border-green-500 transition-all cursor-pointer"
                          onClick={() => setSelectedSpecialRole('medico')}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <FileCheck className="w-5 h-5 text-green-600" />
                            <span className="px-2 py-1 bg-green-200 text-green-800 text-xs font-bold rounded">MEDICO</span>
                          </div>
                          <p className="font-bold text-gray-900 text-sm">Medico Competente</p>
                          <p className="text-sm text-gray-700 mt-1">{myCompany.medicoCompetente.nome} {myCompany.medicoCompetente.cognome}</p>
                          <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                            <MousePointerClick className="w-3 h-3" />
                            <span>Clicca per dettagli</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dettaglio Ruolo Speciale Selezionato */}
                  {selectedSpecialRole && (
                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">
                          Elenco Lavoratori - {
                            selectedSpecialRole === 'datore' ? 'Datore di Lavoro' :
                            selectedSpecialRole === 'rspp' ? 'RSPP' :
                            'Medico Competente'
                          }
                        </h3>
                        <button 
                          onClick={() => setSelectedSpecialRole(null)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="bg-white rounded-lg border border-gray-200">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome Completo</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Mansione</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cellulare</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSpecialRole === 'datore' && (
                              <tr className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                      <span className="font-semibold text-blue-600">
                                        {myCompany.datoreLavoro.nome[0]}{myCompany.datoreLavoro.cognome[0]}
                                      </span>
                                    </div>
                                    <span className="font-semibold text-gray-900">
                                      {myCompany.datoreLavoro.nome} {myCompany.datoreLavoro.cognome}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-gray-600">Datore di Lavoro</td>
                                <td className="px-6 py-4 text-gray-600">{myCompany.datoreLavoro.email}</td>
                                <td className="px-6 py-4 text-gray-600">{myCompany.datoreLavoro.cellulare || '-'}</td>
                              </tr>
                            )}
                            {selectedSpecialRole === 'rspp' && (
                              <tr className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                      <span className="font-semibold text-orange-600">
                                        {myCompany.rspp.nome[0]}{myCompany.rspp.cognome[0]}
                                      </span>
                                    </div>
                                    <span className="font-semibold text-gray-900">
                                      {myCompany.rspp.nome} {myCompany.rspp.cognome}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-gray-600">RSPP</td>
                                <td className="px-6 py-4 text-gray-600">{myCompany.rspp.email}</td>
                                <td className="px-6 py-4 text-gray-600">{myCompany.rspp.cellulare || '-'}</td>
                              </tr>
                            )}
                            {selectedSpecialRole === 'medico' && (
                              <tr className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                      <span className="font-semibold text-green-600">
                                        {myCompany.medicoCompetente.nome[0]}{myCompany.medicoCompetente.cognome[0]}
                                      </span>
                                    </div>
                                    <span className="font-semibold text-gray-900">
                                      {myCompany.medicoCompetente.nome} {myCompany.medicoCompetente.cognome}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-gray-600">Medico Competente</td>
                                <td className="px-6 py-4 text-gray-600">{myCompany.medicoCompetente.email}</td>
                                <td className="px-6 py-4 text-gray-600">{myCompany.medicoCompetente.cellulare || '-'}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  <h3 className="text-lg font-bold text-gray-900">Ruoli Assegnati - Dashboard</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...new Set(myCompanyWorkers.filter(w => isLavoratoreAttivo(w)).flatMap(w => w.ruoli))].map((ruolo, idx) => {
                      const count = myCompanyWorkers.filter(w => isLavoratoreAttivo(w) && w.ruoli.includes(ruolo)).length;
                      return (
                        <div 
                          key={idx} 
                          className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200 hover:shadow-lg hover:border-purple-400 transition-all cursor-pointer"
                          onClick={() => setSelectedRoleForList(ruolo)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Shield className="w-5 h-5 text-purple-600" />
                            <span className="text-2xl font-bold text-purple-900">{count}</span>
                          </div>
                          <p className="font-semibold text-gray-900">{ruolo}</p>
                          <p className="text-sm text-gray-600 mt-1">{count} lavoratori</p>
                          <div className="mt-2 flex items-center gap-1 text-xs text-purple-600">
                            <MousePointerClick className="w-3 h-3" />
                            <span>Clicca per dettagli</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {[...new Set(myCompanyWorkers.filter(w => isLavoratoreAttivo(w)).flatMap(w => w.ruoli))].length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600">Nessun ruolo assegnato ai lavoratori attivi</p>
                    </div>
                  )}

                  {/* Elenco nominativo per ruolo selezionato */}
                  {selectedRoleForList && (
                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">
                          Elenco Lavoratori - {selectedRoleForList}
                        </h3>
                        <button 
                          onClick={() => setSelectedRoleForList(null)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="bg-white rounded-lg border border-gray-200">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome Completo</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Mansione</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cellulare</th>
                            </tr>
                          </thead>
                          <tbody>
                            {myCompanyWorkers
                              .filter(w => w.ruoli.includes(selectedRoleForList))
                              .map(worker => (
                                <tr key={worker.id} className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                        <span className="font-semibold text-purple-600">
                                          {worker.nome[0]}{worker.cognome[0]}
                                        </span>
                                      </div>
                                      <span className="font-semibold text-gray-900">
                                        {worker.nome} {worker.cognome}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-gray-600">{worker.mansione}</td>
                                  <td className="px-6 py-4 text-gray-600">{worker.email}</td>
                                  <td className="px-6 py-4 text-gray-600">{worker.cellulare}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {companySubMenu === 'mansioni' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                    <MousePointerClick className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800">
                      <strong>Suggerimento:</strong> Clicca su una card per visualizzare l'elenco nominativo dei lavoratori con quella mansione
                    </p>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900">Mansioni Attive</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...new Set(myCompanyWorkers.filter(w => isLavoratoreAttivo(w)).map(w => w.mansione))].map((mansione, idx) => {
                      const count = myCompanyWorkers.filter(w => isLavoratoreAttivo(w) && w.mansione === mansione).length;
                      return (
                        <div 
                          key={idx} 
                          className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200 hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer"
                          onClick={() => setSelectedMansioneForList(mansione)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Briefcase className="w-5 h-5 text-blue-600" />
                            <span className="text-2xl font-bold text-blue-900">{count}</span>
                          </div>
                          <p className="font-semibold text-gray-900">{mansione}</p>
                          <p className="text-sm text-gray-600 mt-1">{count} lavoratori</p>
                          <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
                            <MousePointerClick className="w-3 h-3" />
                            <span>Clicca per dettagli</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {[...new Set(myCompanyWorkers.filter(w => isLavoratoreAttivo(w)).map(w => w.mansione))].length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600">Nessuna mansione assegnata ai lavoratori attivi</p>
                    </div>
                  )}

                  {/* Elenco nominativo per mansione selezionata */}
                  {selectedMansioneForList && (
                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">
                          Elenco Lavoratori - {selectedMansioneForList}
                        </h3>
                        <button 
                          onClick={() => setSelectedMansioneForList(null)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="bg-white rounded-lg border border-gray-200">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome Completo</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cellulare</th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Ruoli</th>
                            </tr>
                          </thead>
                          <tbody>
                            {myCompanyWorkers
                              .filter(w => w.mansione === selectedMansioneForList)
                              .map(worker => (
                                <tr key={worker.id} className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="font-semibold text-blue-600">
                                          {worker.nome[0]}{worker.cognome[0]}
                                        </span>
                                      </div>
                                      <span className="font-semibold text-gray-900">
                                        {worker.nome} {worker.cognome}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-gray-600">{worker.email}</td>
                                  <td className="px-6 py-4 text-gray-600">{worker.cellulare}</td>
                                  <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1">
                                      {worker.ruoli.map((ruolo, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                                          {ruolo}
                                        </span>
                                      ))}
                                      {worker.ruoli.length === 0 && <span className="text-sm text-gray-400">-</span>}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'communications':
        // 🆕 Controllo accesso AZIENDA
        if (currentUser.role === 'AZIENDA') {
          const accessStatus = getAziendaAccessStatus();
          if (!accessStatus.canAccess) {
            return <AccessBlockedComponent companyName={accessStatus.company?.ragioneSociale} role="AZIENDA" />;
          }
        }
        const commAziendaReadOnly = currentUser.role === 'AZIENDA' && getAziendaAccessStatus().isReadOnly;
        
        if (selectedCommunication) {
          const comm = communications.find(c => c.id === selectedCommunication);
          const recipients = comm?.recipients || [];
          const notRead = recipients.filter(r => !r.letto);
          const read = recipients.filter(r => r.letto);

          return (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <button 
                  onClick={() => setSelectedCommunication(null)}
                  className="hover:text-blue-600 flex items-center gap-1 transition-colors"
                >
                  Comunicazioni
                </button>
                <ChevronRight className="w-4 h-4" />
                <span className="font-semibold text-gray-900">{comm?.titolo}</span>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{comm?.titolo}</h1>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 ${
                        comm?.tipologia === 'Messaggio' ? 'bg-blue-100 text-blue-700' :
                        comm?.tipologia === 'Azione' ? 'bg-orange-100 text-orange-700' :
                        'bg-purple-100 text-purple-700'
                      } text-sm font-semibold rounded-full`}>
                        {comm?.tipologia}
                      </span>
                      <span className={`px-3 py-1 ${
                        comm?.stato === 'Pubblicato' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                      } text-sm font-semibold rounded-full`}>
                        {comm?.stato}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-xl mb-6">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Data Invio</p>
                    <p className="font-semibold text-gray-900">{formatDate(comm?.dataInvio)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Destinatari</p>
                    <p className="font-semibold text-gray-900">{calculateCommunicationStats(comm).destinatari}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Letti</p>
                    <p className="font-semibold text-green-600">{calculateCommunicationStats(comm).letti}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Non Letti</p>
                    <p className="font-semibold text-red-600">{calculateCommunicationStats(comm).nonLetti}</p>
                  </div>
                </div>

                {/* 🆕 CONTENUTO COMUNICAZIONE */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-600" />
                    Contenuto
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-gray-900 whitespace-pre-wrap">{comm?.contenuto || 'Nessun contenuto disponibile'}</p>
                  </div>
                </div>

                {/* 🔐 FASE 6.1: CERTIFICATO TRACCIABILITÀ */}
                {comm?.hashSha256 && (
                  <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <h3 className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-emerald-600" />
                      Certificato di Tracciabilità
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Hash SHA-256</p>
                        <p className="text-xs font-mono text-emerald-900 bg-emerald-100 p-2 rounded break-all select-all">{comm.hashSha256}</p>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Timestamp Certificazione (UTC)</p>
                        <p className="text-sm font-semibold text-emerald-900">{formatDateUTC(comm.dataInvio)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-emerald-600 mt-2 italic">Questo hash certifica che il contenuto non e stato alterato dalla data di creazione. Timestamp e hash sono immutabili e non possono essere modificati.</p>
                  </div>
                )}

                <div className="flex gap-2 mb-6 border-b border-gray-200">
                  <button
                    onClick={() => setCommunicationTab('recipients')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      communicationTab === 'recipients'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Destinatari ({notRead.length})
                  </button>
                  <button
                    onClick={() => setCommunicationTab('reads')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      communicationTab === 'reads'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Registro Letture ({read.length})
                  </button>
                </div>

                {/* 📊 BOX REPORT TRACCIABILITÀ */}
                <div className="mb-6 flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-purple-900 mb-1 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      📊 Report Tracciabilità Comunicazione
                    </p>
                    <p className="text-xs text-purple-700">
                      Esporta un report completo con elenco destinatari, conferme di lettura, solleciti inviati, date e firme digitali per la tracciabilità legale
                    </p>
                  </div>
                  <button 
                    onClick={() => handleExportReport(comm)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 whitespace-nowrap shadow-md hover:shadow-lg transition-all"
                    title="Genera report TXT con tutti i dati di lettura, solleciti e conferme"
                  >
                    <Download className="w-4 h-4" />
                    Export Report
                  </button>
                </div>

                {communicationTab === 'recipients' && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-4">Destinatari che non hanno ancora letto</h3>
                    <div className="space-y-2">
                      {notRead.map(recipient => (
                        <div key={recipient.id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                              <AlertCircle className="w-5 h-5 text-red-700" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{recipient.nome}</p>
                              <p className="text-sm text-gray-600">In attesa di lettura</p>
                            </div>
                          </div>

                            {recipient.solleciti && recipient.solleciti.length > 0 && (
                              <div 
                                className={`ml-3 px-2 py-1 ${recipient.solleciti.length >= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} rounded-full text-xs font-semibold flex items-center gap-1 cursor-help`}
                                title={`Solleciti inviati:\n${recipient.solleciti.map(s => `• ${formatDate(s.data)} da ${s.inviato_da}`).join('\n')}`}
                              >
                                <Bell className="w-3 h-3" />
                                {recipient.solleciti.length} sollecit{recipient.solleciti.length === 1 ? 'o' : 'i'}
                              </div>
                            )}                          <button onClick={() => handleSollecita(recipient, comm.titolo, 'comunicazione', comm.id)} className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
                            <Send className="w-4 h-4" />
                            Sollecita
                          </button>
                        </div>
                      ))}
                      {notRead.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                          <p>Tutti i destinatari hanno letto la comunicazione</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {communicationTab === 'reads' && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-4">Registro delle letture con tracciabilità legale</h3>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Data e Ora Lettura</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Stato</th>
                          </tr>
                        </thead>
                        <tbody>
                          {read.map(recipient => (
                            <tr key={recipient.id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                  </div>
                                  <span className="font-semibold text-gray-900">{recipient.nome}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-gray-600">
                                {formatDate(recipient.dataLettura)}
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                                  ✓ Letto
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }

        // 🆕 FILTRO DATE COMUNICAZIONI (spostato qui dalla dashboard)
        // Escludi aziende "Solo Piattaforma" dalla vista ADMIN
        const aziendeGestite = companies.filter(c => c.tipoContratto === 'gestione_completa').map(c => c.id);
        const filteredComms = currentUser.role === 'AZIENDA' 
          ? communications.filter(c => String(c.aziendaId) === String(currentUser.companyId))
          : communications.filter(c => aziendeGestite.includes(String(c.aziendaId)));

        const getFilteredByDate = () => {
          const now = new Date();
          let filtered = filteredComms;
          
          if (dateFilter === 'custom') {
            if (customDateFrom && customDateTo) {
              const from = new Date(customDateFrom);
              const to = new Date(customDateTo);
              filtered = filtered.filter(c => {
                const commDate = new Date(c.dataInvio);
                return commDate >= from && commDate <= to;
              });
            }
          } else if (dateFilter !== 'all') {
            let monthsAgo;
            if (dateFilter === '3months') monthsAgo = 3;
            else if (dateFilter === '6months') monthsAgo = 6;
            else if (dateFilter === '1year') monthsAgo = 12;
            
            if (monthsAgo) {
              const filterDate = new Date(now.setMonth(now.getMonth() - monthsAgo));
              filtered = filtered.filter(c => new Date(c.dataInvio) >= filterDate);
            }
          }
          
          return filtered;
        };

        const getFilteredCommunications = () => {
          let filtered = getFilteredByDate();
          
          if (filterCompany !== 'all') {
            filtered = filtered.filter(c => String(c.aziendaId) === String(filterCompany));
          }
          
          if (filterType !== 'all') {
            filtered = filtered.filter(c => c.tipologia === filterType);
          }
          
          // Filtro scadenze
          if (filterExpire !== 'all') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            filtered = filtered.filter(c => {
              if (filterExpire === 'noexpiry') {
                return !c.dataScadenza;
              }
              
              if (!c.dataScadenza) return false;
              
              const scadenza = new Date(c.dataScadenza);
              scadenza.setHours(0, 0, 0, 0);
              const daysToExpiry = Math.ceil((scadenza - today) / (1000 * 60 * 60 * 24));
              
              if (filterExpire === 'expired') {
                return daysToExpiry < 0;
              } else if (filterExpire === 'expiring') {
                return daysToExpiry >= 0 && daysToExpiry <= 30;
              }
              return true;
            });
          }
          
          return filtered;
        };

        return (
          <div className="space-y-6">
            {/* 🆕 Banner contratto scaduto per AZIENDA */}
            {commAziendaReadOnly && (
              <ContractExpiredBanner scadenzaDate={getAziendaAccessStatus().company?.dataScadenzaContratto} role="AZIENDA" />
            )}
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Comunicazioni</h1>
                <p className="text-gray-600">Gestisci tutte le comunicazioni tracciate</p>
              </div>
              {/* Pulsante nascosto se AZIENDA in stato scaduto */}
              {!commAziendaReadOnly && (
                <button 
                  onClick={() => openModal('newCommunication')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-colors shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  Nuova Comunicazione
                </button>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Cerca comunicazioni..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                {currentUser.role !== 'AZIENDA' && (
                  <select 
                    value={filterCompany}
                    onChange={(e) => setFilterCompany(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">Tutte le aziende</option>
                    {companies
                      .filter(c => c.tipoContratto === 'gestione_completa')
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.ragioneSociale}</option>
                      ))}
                  </select>
                )}
                <select 
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tutte le tipologie</option>
                  <option value="Messaggio">Messaggio</option>
                  <option value="Azione">Azione</option>
                  <option value="Documento">Documento</option>
                </select>
                <select 
                  value={filterExpire}
                  onChange={(e) => setFilterExpire(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tutte le scadenze</option>
                  <option value="expiring">In scadenza (≤30 gg)</option>
                  <option value="expired">Scadute</option>
                  <option value="noexpiry">Senza scadenza</option>
                </select>
              </div>

              {/* 🆕 FILTRO DATE */}
              <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                <label className="text-sm font-medium text-gray-700">Periodo:</label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Tutte</option>
                  <option value="3months">Ultimi 3 mesi</option>
                  <option value="6months">Ultimi 6 mesi</option>
                  <option value="1year">Ultimo anno</option>
                  <option value="custom">Personalizza...</option>
                </select>

                {dateFilter === 'custom' && (
                  <>
                    <label className="text-sm font-medium text-gray-700 ml-2">Da:</label>
                    <input
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                    <label className="text-sm font-medium text-gray-700 ml-2">A:</label>
                    <input
                      type="date"
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </>
                )}
              </div>
            </div>

            {/* Tabella Comunicazioni */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Invio</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titolo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azienda</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Destinatari</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Letture</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredCommunications()
                      .sort((a, b) => new Date(b.dataInvio) - new Date(a.dataInvio))
                      .map(comm => {
                        const stats = calculateCommunicationStats(comm);
                        const completionRate = stats.destinatari > 0 ? ((stats.letti / stats.destinatari) * 100).toFixed(0) : 0;
                        const companyName = getCompanyName(comm.aziendaId);
                        const typeColors = {
                          'Messaggio': 'blue',
                          'Azione': 'orange',
                          'Documento': 'purple'
                        };
                        const color = typeColors[comm.tipologia];

                        return (
                          <tr 
                            key={comm.id} 
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => setSelectedCommunication(comm.id)}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatDateOnly(comm.dataInvio)}</div>
                              <div className="text-xs text-gray-500">{new Date(comm.dataInvio).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-semibold text-gray-900">{comm.titolo}</div>
                              {comm.dataScadenza && (
                                <div className="text-xs text-orange-600">
                                  Scad: {formatDateOnly(comm.dataScadenza)}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-900">{companyName}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>
                                {comm.tipologia}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  comm.stato === 'Pubblicato' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                                }`}>
                                  {comm.stato}
                                </span>
                                {/* 🆕 ICONA MAIL */}
                                {comm.stato === 'Pubblicato' && (
                                  <Mail className="w-4 h-4 text-blue-600" title="Email inviate" />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="text-sm font-semibold text-gray-900">{stats.destinatari}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                <div className="text-center">
                                  <div className="text-sm font-bold text-gray-900">{completionRate}%</div>
                                  <div className="flex gap-1 mt-1 text-xs">
                                    <span className="text-green-600">✓ {stats.letti}</span>
                                    <span className="text-red-600">✗ {stats.nonLetti}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center whitespace-nowrap">
                              {comm.stato === 'Bozza' && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openModal('editCommunication', comm);
                                    }}
                                    className="inline-flex items-center p-1.5 hover:bg-blue-50 rounded text-blue-600 transition-colors"
                                    title="Modifica"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCommunication(comm.id);
                                    }}
                                    className="inline-flex items-center p-1.5 hover:bg-red-50 rounded text-red-600 transition-colors"
                                    title="Elimina bozza"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {getFilteredCommunications().length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl shadow-md">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">Nessuna comunicazione trovata</p>
              </div>
            )}
          </div>
        );
        if (selectedCommunication) {
          const comm = communications.find(c => c.id === selectedCommunication);
          const recipients = comm?.recipients || [];
          const notRead = recipients.filter(r => !r.letto);
          const read = recipients.filter(r => r.letto);

          return (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <button 
                  onClick={() => setSelectedCommunication(null)}
                  className="hover:text-blue-600 flex items-center gap-1 transition-colors"
                >
                  Comunicazioni
                </button>
                <ChevronRight className="w-4 h-4" />
                <span className="font-semibold text-gray-900">{comm?.titolo}</span>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{comm?.titolo}</h1>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 ${
                        comm?.tipologia === 'Messaggio' ? 'bg-blue-100 text-blue-700' :
                        comm?.tipologia === 'Azione' ? 'bg-orange-100 text-orange-700' :
                        'bg-purple-100 text-purple-700'
                      } text-sm font-semibold rounded-full`}>
                        {comm?.tipologia}
                      </span>
                      <span className={`px-3 py-1 ${
                        comm?.stato === 'Pubblicato' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                      } text-sm font-semibold rounded-full`}>
                        {comm?.stato}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-xl mb-6">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Data Invio</p>
                    <p className="font-semibold text-gray-900">{formatDate(comm?.dataInvio)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Destinatari</p>
                    <p className="font-semibold text-gray-900">{calculateCommunicationStats(comm).destinatari}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Letti</p>
                    <p className="font-semibold text-green-600">{calculateCommunicationStats(comm).letti}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Non Letti</p>
                    <p className="font-semibold text-red-600">{calculateCommunicationStats(comm).nonLetti}</p>
                  </div>
                </div>

                {/* 🆕 CONTENUTO COMUNICAZIONE */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-600" />
                    Contenuto
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-gray-900 whitespace-pre-wrap">{comm?.contenuto || 'Nessun contenuto disponibile'}</p>
                  </div>
                </div>

                

                {/* 🔐 FASE 6.1: CERTIFICATO TRACCIABILITÀ */}
                {comm?.hashSha256 && (
                  <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <h3 className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-emerald-600" />
                      Certificato di Tracciabilità
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Hash SHA-256</p>
                        <p className="text-xs font-mono text-emerald-900 bg-emerald-100 p-2 rounded break-all select-all">{comm.hashSha256}</p>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Timestamp Certificazione (UTC)</p>
                        <p className="text-sm font-semibold text-emerald-900">{formatDateUTC(comm.dataInvio)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-emerald-600 mt-2 italic">Questo hash certifica che il contenuto non e stato alterato dalla data di creazione. Timestamp e hash sono immutabili e non possono essere modificati.</p>
                  </div>
                )}

<div className="flex gap-2 mb-6 border-b border-gray-200">
                  <button
                    onClick={() => setCommunicationTab('recipients')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      communicationTab === 'recipients'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Destinatari ({notRead.length})
                  </button>
                  <button
                    onClick={() => setCommunicationTab('reads')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      communicationTab === 'reads'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Registro Letture ({read.length})
                  </button>
                </div>

                {/* 📊 BOX REPORT TRACCIABILITÀ */}
                <div className="mb-6 flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-purple-900 mb-1 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      📊 Report Tracciabilità Comunicazione
                    </p>
                    <p className="text-xs text-purple-700">
                      Esporta un report completo con elenco destinatari, conferme di lettura, solleciti inviati, date e firme digitali per la tracciabilità legale
                    </p>
                  </div>
                  <button 
                    onClick={() => handleExportReport(comm)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 whitespace-nowrap shadow-md hover:shadow-lg transition-all"
                    title="Genera report TXT con tutti i dati di lettura, solleciti e conferme"
                  >
                    <Download className="w-4 h-4" />
                    Export Report
                  </button>
                </div>

                {communicationTab === 'recipients' && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-4">Destinatari che non hanno ancora letto</h3>
                    <div className="space-y-2">
                      {notRead.map(recipient => (
                        <div key={recipient.id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                              <AlertCircle className="w-5 h-5 text-red-700" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{recipient.nome}</p>
                              <p className="text-sm text-gray-600">In attesa di lettura</p>
                            </div>
                          </div>

                            {recipient.solleciti && recipient.solleciti.length > 0 && (
                              <div 
                                className={`ml-3 px-2 py-1 ${recipient.solleciti.length >= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} rounded-full text-xs font-semibold flex items-center gap-1 cursor-help`}
                                title={`Solleciti inviati:\n${recipient.solleciti.map(s => `• ${formatDate(s.data)} da ${s.inviato_da}`).join('\n')}`}
                              >
                                <Bell className="w-3 h-3" />
                                {recipient.solleciti.length} sollecit{recipient.solleciti.length === 1 ? 'o' : 'i'}
                              </div>
                            )}                          <button onClick={() => handleSollecita(recipient, comm.titolo, 'comunicazione', comm.id)} className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
                            <Send className="w-4 h-4" />
                            Sollecita
                          </button>
                        </div>
                      ))}
                      {notRead.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                          <p>Tutti i destinatari hanno letto la comunicazione</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {communicationTab === 'reads' && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-4">Registro delle letture con tracciabilità legale</h3>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Data e Ora Lettura</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Stato</th>
                          </tr>
                        </thead>
                        <tbody>
                          {read.map(recipient => (
                            <tr key={recipient.id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                  </div>
                                  <span className="font-semibold text-gray-900">{recipient.nome}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-gray-600">
                                {formatDate(recipient.dataLettura)}
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                                  ✓ Letto
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Comunicazioni</h1>
                <p className="text-gray-600">Gestisci tutte le comunicazioni tracciate</p>
              </div>
              <button 
                onClick={() => openModal('newCommunication')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-colors shadow-lg"
              >
                <Plus className="w-5 h-5" />
                Nuova Comunicazione
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Cerca comunicazioni..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select 
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tutte le aziende</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.ragioneSociale}</option>
                  ))}
                </select>
                <select 
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tutte le tipologie</option>
                  <option value="Messaggio">Messaggio</option>
                  <option value="Azione">Azione</option>
                  <option value="Documento">Documento</option>
                </select>
                <select 
                  value={filterExpire}
                  onChange={(e) => setFilterExpire(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tutte le scadenze</option>
                  <option value="expiring">In scadenza (≤30 gg)</option>
                  <option value="expired">Scadute</option>
                  <option value="noexpiry">Senza scadenza</option>
                </select>
              </div>
            </div>

            {/* Tabella Comunicazioni */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Invio</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titolo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azienda</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Destinatari</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Letture</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredCommunications()
                      .sort((a, b) => new Date(b.dataInvio) - new Date(a.dataInvio))
                      .map(comm => {
                        const stats = calculateCommunicationStats(comm);
                        const completionRate = stats.destinatari > 0 ? ((stats.letti / stats.destinatari) * 100).toFixed(0) : 0;
                        const companyName = getCompanyName(comm.aziendaId);
                        const typeColors = {
                          'Messaggio': 'blue',
                          'Azione': 'orange',
                          'Documento': 'purple'
                        };
                        const color = typeColors[comm.tipologia];

                        return (
                          <tr 
                            key={comm.id} 
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => setSelectedCommunication(comm.id)}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatDateOnly(comm.dataInvio)}</div>
                              <div className="text-xs text-gray-500">{new Date(comm.dataInvio).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-semibold text-gray-900">{comm.titolo}</div>
                              {comm.dataScadenza && (
                                <div className="text-xs text-orange-600">
                                  Scad: {formatDateOnly(comm.dataScadenza)}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-900">{companyName}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>
                                {comm.tipologia}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  comm.stato === 'Pubblicato' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                                }`}>
                                  {comm.stato}
                                </span>
                                {/* 🆕 ICONA MAIL se pubblicato */}
                                {comm.stato === 'Pubblicato' && (
                                  <Mail className="w-4 h-4 text-blue-600" title="Email inviate ai destinatari" />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="text-sm font-semibold text-gray-900">{stats.destinatari}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                <div className="text-center">
                                  <div className="text-sm font-bold text-gray-900">{completionRate}%</div>
                                  <div className="flex gap-1 mt-1 text-xs">
                                    <span className="text-green-600">✓ {stats.letti}</span>
                                    <span className="text-red-600">✗ {stats.nonLetti}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center whitespace-nowrap">
                              {comm.stato === 'Bozza' && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openModal('editCommunication', comm);
                                    }}
                                    className="inline-flex items-center p-1.5 hover:bg-blue-50 rounded text-blue-600 transition-colors"
                                    title="Modifica"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCommunication(comm.id);
                                    }}
                                    className="inline-flex items-center p-1.5 hover:bg-red-50 rounded text-red-600 transition-colors"
                                    title="Elimina bozza"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {getFilteredCommunications().length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl shadow-md">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">Nessuna comunicazione trovata</p>
              </div>
            )}
          </div>
        );

      // 🆕 GESTIONE LAVORATORI (profilo AZIENDA - CRUD completo)
      case 'documents-admin':
        // Escludi aziende "Solo Piattaforma" dalla vista documenti ADMIN
        const aziendeGestiteDoc = companies.filter(c => c.tipoContratto === 'gestione_completa').map(c => c.id);
        const selectedCompanyDocs = selectedCompanyForDocuments === 'all'
          ? documents.filter(d => aziendeGestiteDoc.includes(d.companyId)) // Solo aziende gestite
          : selectedCompanyForDocuments 
            ? documents.filter(d => d.companyId === selectedCompanyForDocuments)
            : [];
        
        // Filtri documenti ADMIN
        const getFilteredDocumentsAdmin = () => {
          let filtered = selectedCompanyDocs;
          
          // 🆕 Filtro documenti archiviati (nascondi di default)
          if (!showArchivedDocuments) {
            filtered = filtered.filter(d => !d.isArchived);
          }
          
          // Filtro ricerca
          if (searchTerm) {
            filtered = filtered.filter(d =>
              d.descrizione.toLowerCase().includes(searchTerm.toLowerCase()) ||
              d.tipo.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
          
          // Filtro scadenza
          if (filterDocumentsExpired !== 'all') {
            filtered = filtered.filter(d => {
              if (!d.dataScadenza) return false;
              const daysToExpiry = Math.ceil((new Date(d.dataScadenza) - new Date()) / (1000 * 60 * 60 * 24));
              if (filterDocumentsExpired === 'expired') return daysToExpiry < 0;
              if (filterDocumentsExpired === 'expiring') return daysToExpiry >= 0 && daysToExpiry <= 30;
              return true;
            });
          }
          
          // Ordinamento: più recente prima
          return filtered.sort((a, b) => new Date(b.dataCaricamento) - new Date(a.dataCaricamento));
        };
        
        // 🆕 DETTAGLIO DOCUMENTO CONDIVISO (ADMIN)
        if (selectedDocument) {
          const doc = documents.find(d => d.id === selectedDocument);
          if (!doc) {
            setSelectedDocument(null);
            return null;
          }

          const recipients = doc.recipients || [];
          const notRead = recipients.filter(r => !r.letto);
          const read = recipients.filter(r => r.letto);

          return (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <button 
                  onClick={() => setSelectedDocument(null)}
                  className="hover:text-blue-600 flex items-center gap-1 transition-colors"
                >
                  Documenti
                </button>
                <ChevronRight className="w-4 h-4" />
                <span className="font-semibold text-gray-900">{doc.descrizione}</span>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{doc.descrizione}</h1>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 ${
                        doc.tipo === 'DVR' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      } text-sm font-semibold rounded-full`}>
                        {doc.tipo}
                      </span>
                      <span className={`px-3 py-1 ${
                        doc.stato === 'Condiviso' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                      } text-sm font-semibold rounded-full`}>
                        {doc.stato}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => handleDownloadDocument(doc)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Scarica
                    </button>
                    <button 
                      onClick={() => {
                        setRevisionDocument(doc);
                        setNewRevision({
                          descrizione: '',
                          dataRevisione: '',
                          dataScadenza: '',
                          file: null,
                          autoReshare: doc.stato === 'Condiviso'
                        });
                        setShowRevisionModal(true);
                      }}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Carica Revisione
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-xl mb-6">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Data Caricamento</p>
                    <p className="font-semibold text-gray-900">{formatDate(doc.dataCaricamento)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Destinatari</p>
                    <p className="font-semibold text-gray-900">{calculateCommunicationStats(doc).destinatari}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Confermati</p>
                    <p className="font-semibold text-green-600">{calculateCommunicationStats(doc).letti}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Non Confermati</p>
                    <p className="font-semibold text-red-600">{calculateCommunicationStats(doc).nonLetti}</p>
                  </div>
                </div>

                {/* 🔐 FASE 6.1: CERTIFICATO TRACCIABILITÀ DOCUMENTO */}
                {doc?.hashSha256 && (
                  <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <h3 className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-emerald-600" />
                      Certificato di Tracciabilità
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Hash SHA-256</p>
                        <p className="text-xs font-mono text-emerald-900 bg-emerald-100 p-2 rounded break-all select-all">{doc.hashSha256}</p>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Timestamp Certificazione (UTC)</p>
                        <p className="text-sm font-semibold text-emerald-900">{formatDateUTC(doc.dataCaricamento)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-emerald-600 mt-2 italic">Questo hash certifica che il documento non e stato alterato dalla data di creazione. Timestamp e hash sono immutabili e non possono essere modificati.</p>
                  </div>
                )}

                <div className="flex gap-2 mb-6 border-b border-gray-200">
                  <button
                    onClick={() => setDocumentTab('recipients')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      documentTab === 'recipients'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Destinatari ({notRead.length})
                  </button>
                  <button
                    onClick={() => setDocumentTab('confirmations')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      documentTab === 'confirmations'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Registro Conferme ({read.length})
                  </button>
                </div>
                {/* 🆕 PULSANTE EXPORT REPORT CONDIVISIONI */}
                {doc.stato === 'Condiviso' && (
                  <div className="mb-6 flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-purple-900 mb-1">📊 Report Tracciabilità Condivisione</p>
                      <p className="text-xs text-purple-700">
                        Esporta un report completo con elenco destinatari, conferme di lettura, date e firme digitali per la tracciabilità legale
                      </p>
                    </div>
                    <button 
                      onClick={() => handleExportDocumentReport(doc)}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 whitespace-nowrap shadow-md hover:shadow-lg transition-all"
                      title="Genera report TXT con tutti i dati di condivisione e conferme"
                    >
                      <FileText className="w-4 h-4" />
                      Export Report Condivisioni
                    </button>
                  </div>
                )}

                {documentTab === 'recipients' && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-4">Destinatari che non hanno ancora confermato</h3>
                    <div className="space-y-2">
                      {notRead.map(recipient => (
                        <div key={recipient.id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                              <AlertCircle className="w-5 h-5 text-red-700" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{recipient.nome}</p>
                              <p className="text-sm text-gray-600">In attesa di conferma</p>
                            </div>
                          </div>

                            {recipient.solleciti && recipient.solleciti.length > 0 && (
                              <div 
                                className={`ml-3 px-2 py-1 ${recipient.solleciti.length >= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} rounded-full text-xs font-semibold flex items-center gap-1 cursor-help`}
                                title={`Solleciti inviati:\n${recipient.solleciti.map(s => `• ${formatDate(s.data)} da ${s.inviato_da}`).join('\n')}`}
                              >
                                <Bell className="w-3 h-3" />
                                {recipient.solleciti.length} sollecit{recipient.solleciti.length === 1 ? 'o' : 'i'}
                              </div>
                            )}                          <button onClick={() => handleSollecita(recipient, doc.descrizione, 'documento', doc.id)} className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
                            <Send className="w-4 h-4" />
                            Sollecita
                          </button>
                        </div>
                      ))}
                      {notRead.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                          <p>Tutti i destinatari hanno confermato la ricezione</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {documentTab === 'confirmations' && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-4">Registro delle conferme con tracciabilità legale</h3>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Data e Ora Conferma</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Firma Digitale</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Stato</th>
                          </tr>
                        </thead>
                        <tbody>
                          {read.map(recipient => (
                            <tr key={recipient.id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                  </div>
                                  <span className="font-semibold text-gray-900">{recipient.nome}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-gray-600">
                                {formatDate(recipient.dataLettura)}
                              </td>
                              <td className="px-6 py-4">
                                <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                  {recipient.firmaDigitale || '-'}
                                </code>
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                                  ✓ Confermato
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }
        
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Documenti Aziendali</h1>
                <p className="text-gray-600">Gestisci i documenti di sicurezza delle aziende clienti</p>
              </div>
              <button
                onClick={() => handleExportDocumentsList(getFilteredDocumentsAdmin(), 'ADMIN')}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-colors shadow-lg"
                title="Esporta elenco documenti filtrati in formato TXT"
              >
                <Download className="w-5 h-5" />
                Export Elenco Documenti
              </button>
            </div>

            {/* Selezione Azienda */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Seleziona Azienda *</label>
              <select
                value={selectedCompanyForDocuments}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'all') {
                    setSelectedCompanyForDocuments('all');
                  } else {
                    setSelectedCompanyForDocuments(isNaN(value) ? value : Number(value));
                  }
                  setSearchTerm(''); // Reset ricerca
                  setFilterDocumentsExpired('all'); // Reset filtro
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              >
                <option value="all">📋 Tutte le aziende (Gestione Completa)</option>
                {companies
                  .filter(c => c.tipoContratto === 'gestione_completa')
                  .map(c => (
                    <option key={c.id} value={c.id}>
                      {c.ragioneSociale} - {c.citta}
                    </option>
                  ))}
              </select>
            </div>

            {selectedCompanyForDocuments && (
              <>
                {/* Header con pulsante Carica */}
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">
                    Documenti - {selectedCompanyForDocuments === 'all' 
                      ? 'Tutte le aziende' 
                      : companies.find(c => c.id === selectedCompanyForDocuments)?.ragioneSociale}
                  </h2>
                  {selectedCompanyForDocuments === 'all' ? (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl px-6 py-3 flex items-center gap-3">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <p className="text-sm font-medium text-blue-900">
                        Per caricare un documento, seleziona un'azienda specifica dal menu
                      </p>
                    </div>
                  ) : companies.find(c => c.id === selectedCompanyForDocuments)?.tipoContratto === 'solo_piattaforma' ? (
                    <div className="bg-purple-50 border-2 border-purple-200 rounded-xl px-6 py-3 flex items-center gap-3">
                      <Info className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <p className="text-sm font-medium text-purple-900">
                        Azienda "Solo Piattaforma" - Gestione documenti autonoma dal cliente
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setSelectedCompany(selectedCompanyForDocuments);
                        openModal('newDocument');
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-colors shadow-lg"
                    >
                      <Plus className="w-5 h-5" />
                      Carica Documento
                    </button>
                  )}
                </div>

                {/* Filtri */}
                <div className="bg-white rounded-xl shadow-md p-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                      <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Cerca documento..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <select
                      value={filterDocumentsExpired}
                      onChange={(e) => setFilterDocumentsExpired(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">Tutti i documenti</option>
                      <option value="expiring">In scadenza (≤30 gg)</option>
                      <option value="expired">Scaduti</option>
                    </select>
                    <label 
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors group"
                      title="Quando spuntata: mostra tutti i documenti (attivi + versioni archiviate). Quando non spuntata: mostra solo i documenti attivi (nasconde le versioni archiviate)."
                    >
                      <input
                        type="checkbox"
                        checked={showArchivedDocuments}
                        onChange={(e) => setShowArchivedDocuments(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Mostra archiviate</span>
                      <Info className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    </label>
                  </div>
                </div>

                {/* Tabella Documenti */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrizione</th>
                          {selectedCompanyForDocuments === 'all' && (
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azienda</th>
                          )}
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Caricato</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revisione</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scadenza</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Conferme</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getFilteredDocumentsAdmin().map(doc => {
                          const daysToExpiry = doc.dataScadenza ? Math.ceil((new Date(doc.dataScadenza) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                          const isExpiringSoon = daysToExpiry && daysToExpiry >= 0 && daysToExpiry <= 30;
                          const isExpired = daysToExpiry && daysToExpiry < 0;
                          const stats = calculateCommunicationStats(doc);
                          const completionRate = stats.destinatari > 0 ? ((stats.letti / stats.destinatari) * 100).toFixed(0) : 0;
                          
                          return (
                            <tr 
                              key={doc.id} 
                              className={`hover:bg-gray-50 ${
                                isExpired ? 'bg-red-50' : isExpiringSoon ? 'bg-yellow-50' : ''
                              } ${doc.stato === 'Condiviso' ? 'cursor-pointer' : ''}`}
                              onClick={() => doc.stato === 'Condiviso' && setSelectedDocument(doc.id)}
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    doc.tipo === 'DVR' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                  }`}>
                                    {doc.tipo} {doc.version > 1 && `v${doc.version}`}
                                  </span>
                                  {(doc.version > 1 || doc.replacedDocumentId) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDocumentForHistory(doc);
                                      }}
                                      className="p-1.5 hover:bg-amber-50 rounded-lg text-amber-600 hover:text-amber-700 transition-colors"
                                      title="Vedi storico revisioni"
                                    >
                                      <History className="w-4 h-4" />
                                    </button>
                                  )}
                                  {doc.isArchived && (
                                    <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-medium rounded">
                                      ARCHIVIATO
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-semibold text-gray-900">{doc.descrizione}</div>
                              </td>
                              {selectedCompanyForDocuments === 'all' && (
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{getCompanyName(doc.companyId)}</div>
                                </td>
                              )}
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{formatDateOnly(doc.dataCaricamento)}</div>
                                <div className="text-xs text-gray-500">{new Date(doc.dataCaricamento).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}</div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {formatDateOnly(doc.dataRevisione)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {doc.dataScadenza ? (
                                  <div className={`text-sm font-medium ${
                                    isExpired ? 'text-red-600' : isExpiringSoon ? 'text-yellow-600' : 'text-gray-900'
                                  }`}>
                                    {formatDateOnly(doc.dataScadenza)}
                                    {daysToExpiry !== null && (
                                      <div className="text-xs">
                                        {isExpired ? '⚠️ Scaduto' : `${daysToExpiry} giorni`}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  doc.stato === 'Condiviso' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                                }`}>
                                  {doc.stato}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center whitespace-nowrap">
                                {doc.stato === 'Condiviso' ? (
                                  <div>
                                    <div className="text-sm font-bold text-gray-900">{completionRate}%</div>
                                    <div className="flex gap-1 mt-1 text-xs justify-center">
                                      <span className="text-green-600">✓ {stats.letti}</span>
                                      <span className="text-red-600">✗ {stats.nonLetti}</span>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleDownloadDocument(doc)}
                                    className="p-1.5 hover:bg-blue-50 rounded text-blue-600 transition-colors"
                                    title="Scarica"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  {doc.stato === 'Condiviso' && (
                                    <button
                                      onClick={() => handleExportDocumentReport(doc)}
                                      className="p-1.5 hover:bg-green-50 rounded text-green-600 transition-colors"
                                      title="Export Report Condivisione"
                                    >
                                      <FileCheck className="w-4 h-4" />
                                    </button>
                                  )}
                                  {/* 🆕 Mostra "Carica Revisione" se scaduto o in scadenza */}
                                  {(isExpired || isExpiringSoon) ? (
                                    <button
                                      onClick={() => {
                                        setRevisionDocument(doc);
                                        setNewRevision({
                                          descrizione: '',
                                          dataRevisione: '',
                                          dataScadenza: '',
                                          file: null,
                                          autoReshare: doc.stato === 'Condiviso'
                                        });
                                        setShowRevisionModal(true);
                                      }}
                                      className="p-1.5 hover:bg-amber-50 rounded text-amber-600 transition-colors"
                                      title={isExpired ? 'Documento scaduto - Carica nuova revisione' : 'Documento in scadenza - Carica nuova revisione'}
                                    >
                                      <Upload className="w-4 h-4" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => doc.stato !== 'Condiviso' && handleEditDocument(doc)}
                                      className={`p-1.5 rounded transition-colors ${
                                        doc.stato === 'Condiviso'
                                          ? 'text-gray-300 cursor-not-allowed'
                                          : 'text-gray-700 hover:bg-gray-100'
                                      }`}
                                      title={doc.stato === 'Condiviso' 
                                        ? 'Non modificabile: documento già condiviso con tracciabilità legale'
                                        : 'Modifica'
                                      }
                                      disabled={doc.stato === 'Condiviso'}
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => doc.stato !== 'Condiviso' && !doc.isArchived && handleDeleteDocument(doc.id)}
                                    className={`p-1.5 rounded transition-colors ${
                                      doc.stato === 'Condiviso' || doc.isArchived
                                        ? 'text-amber-200 cursor-not-allowed'
                                        : 'text-amber-600 hover:bg-amber-50'
                                    }`}
                                    title={doc.stato === 'Condiviso' 
                                      ? 'Non archiviabile: documento già condiviso con tracciabilità legale'
                                      : doc.isArchived
                                        ? 'Documento già archiviato'
                                        : 'Archivia documento'
                                    }
                                    disabled={doc.stato === 'Condiviso' || doc.isArchived}
                                  >
                                    <Archive className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {getFilteredDocumentsAdmin().length === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl shadow-md">
                    <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">Nessun documento trovato</p>
                  </div>
                )}
              </>
            )}
          </div>
        );

      // 🆕 DOCUMENTI AZIENDALI (profilo AZIENDA)
      case 'documents':
        // 🆕 Controllo accesso AZIENDA
        if (currentUser.role === 'AZIENDA') {
          const accessStatus = getAziendaAccessStatus();
          if (!accessStatus.canAccess) {
            return <AccessBlockedComponent companyName={accessStatus.company?.ragioneSociale} role="AZIENDA" />;
          }
        }
        const docAziendaReadOnly = currentUser.role === 'AZIENDA' && getAziendaAccessStatus().isReadOnly;
        
        const myDocuments = documents.filter(d => String(d.companyId) === String(currentUser.companyId));
        
        // Filtri documenti
        const getFilteredDocuments = () => {
          let filtered = myDocuments;
          
          // 🆕 Filtro documenti archiviati (nascondi di default)
          if (!showArchivedDocuments) {
            filtered = filtered.filter(d => !d.isArchived);
          }
          
          // Filtro ricerca
          if (searchTerm) {
            filtered = filtered.filter(d =>
              d.descrizione.toLowerCase().includes(searchTerm.toLowerCase()) ||
              d.tipo.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
          
          // Filtro scadenza
          if (filterDocumentsExpired !== 'all') {
            filtered = filtered.filter(d => {
              if (!d.dataScadenza) return false;
              const daysToExpiry = Math.ceil((new Date(d.dataScadenza) - new Date()) / (1000 * 60 * 60 * 24));
              if (filterDocumentsExpired === 'expired') return daysToExpiry < 0;
              if (filterDocumentsExpired === 'expiring') return daysToExpiry >= 0 && daysToExpiry <= 30;
              return true;
            });
          }
          
          // Ordinamento: più recente prima
          return filtered.sort((a, b) => new Date(b.dataCaricamento) - new Date(a.dataCaricamento));
        };
        
        // 🆕 DETTAGLIO DOCUMENTO CONDIVISO
        if (selectedDocument) {
          const doc = documents.find(d => d.id === selectedDocument);
          if (!doc) {
            setSelectedDocument(null);
            return null;
          }

          const recipients = doc.recipients || [];
          const notRead = recipients.filter(r => !r.letto);
          const read = recipients.filter(r => r.letto);

          return (
            <div className="space-y-6">
              {/* 🆕 Banner contratto scaduto per AZIENDA in dettaglio documento */}
              {docAziendaReadOnly && (
                <ContractExpiredBanner scadenzaDate={getAziendaAccessStatus().company?.dataScadenzaContratto} role="AZIENDA" />
              )}
              
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <button 
                  onClick={() => setSelectedDocument(null)}
                  className="hover:text-blue-600 flex items-center gap-1 transition-colors"
                >
                  Documenti
                </button>
                <ChevronRight className="w-4 h-4" />
                <span className="font-semibold text-gray-900">{doc.descrizione}</span>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{doc.descrizione}</h1>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 ${
                        doc.tipo === 'DVR' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      } text-sm font-semibold rounded-full`}>
                        {doc.tipo}
                      </span>
                      <span className={`px-3 py-1 ${
                        doc.stato === 'Condiviso' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                      } text-sm font-semibold rounded-full`}>
                        {doc.stato}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => handleDownloadDocument(doc)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Scarica
                    </button>
                    <button 
                      onClick={() => {
                        setRevisionDocument(doc);
                        setNewRevision({
                          descrizione: '',
                          dataRevisione: '',
                          dataScadenza: '',
                          file: null,
                          autoReshare: doc.stato === 'Condiviso'
                        });
                        setShowRevisionModal(true);
                      }}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Carica Revisione
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-xl mb-6">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Data Caricamento</p>
                    <p className="font-semibold text-gray-900">{formatDate(doc.dataCaricamento)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Destinatari</p>
                    <p className="font-semibold text-gray-900">{calculateCommunicationStats(doc).destinatari}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Confermati</p>
                    <p className="font-semibold text-green-600">{calculateCommunicationStats(doc).letti}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Non Confermati</p>
                    <p className="font-semibold text-red-600">{calculateCommunicationStats(doc).nonLetti}</p>
                  </div>
                </div>

                {/* 🔐 FASE 6.1: CERTIFICATO TRACCIABILITÀ DOCUMENTO */}
                {doc?.hashSha256 && (
                  <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <h3 className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-emerald-600" />
                      Certificato di Tracciabilità
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Hash SHA-256</p>
                        <p className="text-xs font-mono text-emerald-900 bg-emerald-100 p-2 rounded break-all select-all">{doc.hashSha256}</p>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Timestamp Certificazione (UTC)</p>
                        <p className="text-sm font-semibold text-emerald-900">{formatDateUTC(doc.dataCaricamento)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-emerald-600 mt-2 italic">Questo hash certifica che il documento non e stato alterato dalla data di creazione. Timestamp e hash sono immutabili e non possono essere modificati.</p>
                  </div>
                )}

                <div className="flex gap-2 mb-6 border-b border-gray-200">
                  <button
                    onClick={() => setDocumentTab('recipients')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      documentTab === 'recipients'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Destinatari ({notRead.length})
                  </button>
                  <button
                    onClick={() => setDocumentTab('confirmations')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      documentTab === 'confirmations'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Registro Conferme ({read.length})
                  </button>
                </div>
                {/* 🆕 PULSANTE EXPORT REPORT CONDIVISIONI */}
                {doc.stato === 'Condiviso' && (
                  <div className="mb-6 flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-purple-900 mb-1">📊 Report Tracciabilità Condivisione</p>
                      <p className="text-xs text-purple-700">
                        Esporta un report completo con elenco destinatari, conferme di lettura, date e firme digitali per la tracciabilità legale
                      </p>
                    </div>
                    <button 
                      onClick={() => handleExportDocumentReport(doc)}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 whitespace-nowrap shadow-md hover:shadow-lg transition-all"
                      title="Genera report TXT con tutti i dati di condivisione e conferme"
                    >
                      <FileText className="w-4 h-4" />
                      Export Report Condivisioni
                    </button>
                  </div>
                )}

                {documentTab === 'recipients' && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-4">Destinatari che non hanno ancora confermato</h3>
                    <div className="space-y-2">
                      {notRead.map(recipient => (
                        <div key={recipient.id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                              <AlertCircle className="w-5 h-5 text-red-700" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{recipient.nome}</p>
                              <p className="text-sm text-gray-600">In attesa di conferma</p>
                            </div>
                          </div>

                            {recipient.solleciti && recipient.solleciti.length > 0 && (
                              <div 
                                className={`ml-3 px-2 py-1 ${recipient.solleciti.length >= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} rounded-full text-xs font-semibold flex items-center gap-1 cursor-help`}
                                title={`Solleciti inviati:\n${recipient.solleciti.map(s => `• ${formatDate(s.data)} da ${s.inviato_da}`).join('\n')}`}
                              >
                                <Bell className="w-3 h-3" />
                                {recipient.solleciti.length} sollecit{recipient.solleciti.length === 1 ? 'o' : 'i'}
                              </div>
                            )}                          <button onClick={() => handleSollecita(recipient, doc.descrizione, 'documento', doc.id)} className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
                            <Send className="w-4 h-4" />
                            Sollecita
                          </button>
                        </div>
                      ))}
                      {notRead.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                          <p>Tutti i destinatari hanno confermato la ricezione</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {documentTab === 'confirmations' && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-4">Registro delle conferme con tracciabilità legale</h3>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Data e Ora Conferma</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Firma Digitale</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Stato</th>
                          </tr>
                        </thead>
                        <tbody>
                          {read.map(recipient => (
                            <tr key={recipient.id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                  </div>
                                  <span className="font-semibold text-gray-900">{recipient.nome}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-gray-600">
                                {formatDate(recipient.dataLettura)}
                              </td>
                              <td className="px-6 py-4">
                                <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                  {recipient.firmaDigitale || '-'}
                                </code>
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                                  ✓ Confermato
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }
        
        return (
          <div className="space-y-6">
            {/* 🆕 Banner contratto scaduto per AZIENDA */}
            {docAziendaReadOnly && (
              <ContractExpiredBanner scadenzaDate={getAziendaAccessStatus().company?.dataScadenzaContratto} role="AZIENDA" />
            )}
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Documenti Aziendali</h1>
                <p className="text-gray-600">Gestisci DVR, DUVRI e altri documenti di sicurezza</p>
              </div>
              {/* Tutti i pulsanti nascosti se AZIENDA in stato scaduto */}
              {!docAziendaReadOnly && (
                <div className="flex gap-3">
                  <button
                    onClick={() => handleExportDocumentsList(getFilteredDocuments(), 'AZIENDA')}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-colors shadow-lg"
                    title="Esporta elenco documenti filtrati in formato TXT"
                  >
                    <Download className="w-5 h-5" />
                    Export Elenco
                  </button>
                  <button
                    onClick={() => openModal('newDocument')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-colors shadow-lg"
                  >
                    <Plus className="w-5 h-5" />
                    Carica Documento
                  </button>
                </div>
              )}
            </div>

            {/* Filtri */}
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Cerca documento..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={filterDocumentsExpired}
                  onChange={(e) => setFilterDocumentsExpired(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tutti i documenti</option>
                  <option value="expiring">In scadenza (≤30 gg)</option>
                  <option value="expired">Scaduti</option>
                </select>
                <label 
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors group"
                  title="Quando spuntata: mostra tutti i documenti (attivi + versioni archiviate). Quando non spuntata: mostra solo i documenti attivi (nasconde le versioni archiviate)."
                >
                  <input
                    type="checkbox"
                    checked={showArchivedDocuments}
                    onChange={(e) => setShowArchivedDocuments(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Mostra archiviate</span>
                  <Info className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                </label>
              </div>
            </div>

            {/* Tabella Documenti */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrizione</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Caricato</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revisione</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scadenza</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Conferme</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredDocuments().map(doc => {
                      const daysToExpiry = doc.dataScadenza ? Math.ceil((new Date(doc.dataScadenza) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                      const isExpiringSoon = daysToExpiry && daysToExpiry >= 0 && daysToExpiry <= 30;
                      const isExpired = daysToExpiry && daysToExpiry < 0;
                      const stats = calculateCommunicationStats(doc);
                      const completionRate = stats.destinatari > 0 ? ((stats.letti / stats.destinatari) * 100).toFixed(0) : 0;
                      
                      return (
                        <tr 
                          key={doc.id} 
                          className={`hover:bg-gray-50 ${
                            isExpired ? 'bg-red-50' : isExpiringSoon ? 'bg-yellow-50' : ''
                          } ${doc.stato === 'Condiviso' ? 'cursor-pointer' : ''}`}
                          onClick={() => doc.stato === 'Condiviso' && setSelectedDocument(doc.id)}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                doc.tipo === 'DVR' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {doc.tipo} {doc.version > 1 && `v${doc.version}`}
                              </span>
                              {(doc.version > 1 || doc.replacedDocumentId) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDocumentForHistory(doc);
                                  }}
                                  className="p-1.5 hover:bg-amber-50 rounded-lg text-amber-600 hover:text-amber-700 transition-colors"
                                  title="Vedi storico revisioni"
                                >
                                  <History className="w-4 h-4" />
                                </button>
                              )}
                              {doc.isArchived && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-medium rounded">
                                  ARCHIVIATO
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-semibold text-gray-900">{doc.descrizione}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDateOnly(doc.dataCaricamento)}</div>
                            <div className="text-xs text-gray-500">{new Date(doc.dataCaricamento).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {formatDateOnly(doc.dataRevisione)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {doc.dataScadenza ? (
                              <div className={`text-sm font-medium ${
                                isExpired ? 'text-red-600' : isExpiringSoon ? 'text-yellow-600' : 'text-gray-900'
                              }`}>
                                {formatDateOnly(doc.dataScadenza)}
                                {daysToExpiry !== null && (
                                  <div className="text-xs">
                                    {isExpired ? '⚠️ Scaduto' : `${daysToExpiry} giorni`}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              doc.stato === 'Condiviso' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                            }`}>
                              {doc.stato}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {doc.stato === 'Condiviso' ? (
                              <div>
                                <div className="text-sm font-bold text-gray-900">{completionRate}%</div>
                                <div className="flex gap-1 mt-1 text-xs justify-center">
                                  <span className="text-green-600">✓ {stats.letti}</span>
                                  <span className="text-red-600">✗ {stats.nonLetti}</span>
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            {/* Azioni nascoste se contratto scaduto */}
                            {docAziendaReadOnly ? (
                              <span className="text-xs text-orange-600 italic">Solo visualizzazione</span>
                            ) : (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => handleDownloadDocument(doc)}
                                  className="p-1.5 hover:bg-blue-50 rounded text-blue-600 transition-colors"
                                  title="Scarica"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                                {doc.stato === 'Condiviso' && (
                                  <button
                                    onClick={() => handleExportDocumentReport(doc)}
                                    className="p-1.5 hover:bg-green-50 rounded text-green-600 transition-colors"
                                    title="Export Report Condivisione"
                                  >
                                    <FileCheck className="w-4 h-4" />
                                  </button>
                                )}
                                {/* 🆕 Mostra "Carica Revisione" se scaduto o in scadenza */}
                                {(isExpired || isExpiringSoon) ? (
                                  <button
                                    onClick={() => {
                                      setRevisionDocument(doc);
                                      setNewRevision({
                                        descrizione: '',
                                        dataRevisione: '',
                                        dataScadenza: '',
                                        file: null,
                                        autoReshare: doc.stato === 'Condiviso'
                                      });
                                      setShowRevisionModal(true);
                                    }}
                                    className="p-1.5 hover:bg-amber-50 rounded text-amber-600 transition-colors"
                                    title={isExpired ? 'Documento scaduto - Carica nuova revisione' : 'Documento in scadenza - Carica nuova revisione'}
                                  >
                                    <Upload className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => doc.stato !== 'Condiviso' && handleEditDocument(doc)}
                                    className={`p-1.5 rounded transition-colors ${
                                      doc.stato === 'Condiviso'
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                    title={doc.stato === 'Condiviso' 
                                      ? 'Non modificabile: documento già condiviso con tracciabilità legale'
                                      : 'Modifica'
                                    }
                                    disabled={doc.stato === 'Condiviso'}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => doc.stato !== 'Condiviso' && !doc.isArchived && handleDeleteDocument(doc.id)}
                                  className={`p-1.5 rounded transition-colors ${
                                    doc.stato === 'Condiviso' || doc.isArchived
                                      ? 'text-amber-200 cursor-not-allowed'
                                      : 'text-amber-600 hover:bg-amber-50'
                                  }`}
                                  title={doc.stato === 'Condiviso' 
                                    ? 'Non archiviabile: documento già condiviso con tracciabilità legale'
                                    : doc.isArchived
                                      ? 'Documento già archiviato'
                                      : 'Archivia documento'
                                  }
                                  disabled={doc.stato === 'Condiviso' || doc.isArchived}
                                >
                                  <Archive className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {getFilteredDocuments().length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl shadow-md">
                <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Nessun documento trovato</p>
              </div>
            )}
          </div>
        );

      // ═══════════════════════════════════════════════════════════════════
      // 🆕 SEZIONI PROFILO LAVORATORE
      // ═══════════════════════════════════════════════════════════════════
      
      case 'worker-communications':
        // 🆕 Controllo accesso LAVORATORE
        const commLavAccessStatus = getLavoratoreAccessStatus();
        if (!commLavAccessStatus.canAccess) {
          return <AccessBlockedComponent companyName={commLavAccessStatus.company?.ragioneSociale} role="LAVORATORE" />;
        }
        const commLavReadOnly = commLavAccessStatus.isReadOnly;
        
        const workerComms = getWorkerCommunications();
        const filteredWorkerComms = filterWorkerComms(
          workerComms, 
          workerCommFilter, 
          workerCommDateFilter,
          workerCommDateFrom,
          workerCommDateTo,
          workerCommExpireFilter,
          workerCommTypeFilter
        );
        
        // Se c'è una comunicazione selezionata, mostra il dettaglio
        if (selectedWorkerComm) {
          const comm = selectedWorkerComm;
          const recipient = comm.recipients?.find(r => r.id === currentUser.workerId);
          const isRead = recipient?.letto;
          const senderCompany = companies.find(c => c.id === comm.aziendaId);
          
          return (
            <div className="space-y-6">
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <button 
                  onClick={() => setSelectedWorkerComm(null)}
                  className="hover:text-blue-600 flex items-center gap-1"
                >
                  ← Torna alle comunicazioni
                </button>
              </div>
              
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        comm.tipologia === 'Documento' ? 'bg-purple-100 text-purple-700' :
                        comm.tipologia === 'Azione' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {comm.tipologia}
                      </span>
                      {isRead ? (
                        <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">
                          ✓ Letto
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-sm bg-red-100 text-red-700">
                          Da leggere
                        </span>
                      )}
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">{comm.titolo}</h1>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">Mittente</p>
                    <p className="font-medium">{senderCompany?.ragioneSociale || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Data Invio</p>
                    <p className="font-medium">{formatDate(comm.dataInvio)}</p>
                  </div>
                  {comm.dataScadenza && (
                    <div>
                      <p className="text-sm text-gray-500">Scadenza</p>
                      <p className="font-medium text-orange-600">{formatDateOnly(comm.dataScadenza)}</p>
                    </div>
                  )}
                </div>
                
                <div className="prose max-w-none mb-6">
                  <h3 className="text-lg font-semibold mb-2">Contenuto</h3>
                  <div className="p-4 bg-gray-50 rounded-lg whitespace-pre-wrap">
                    {comm.contenuto}
                  </div>
                </div>
                
                {/* Timestamp lettura o pulsante conferma */}
                {isRead ? (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Comunicazione letta</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Data e ora lettura: {formatDate(recipient.dataLettura)}
                    </p>
                  </div>
                ) : commLavReadOnly ? (
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-2 text-orange-700">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">Azione non disponibile</span>
                    </div>
                    <p className="text-sm text-orange-600 mt-1">
                      Il contratto è scaduto - solo visualizzazione
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      confirmReadCommunication(comm.id);
                      setSelectedWorkerComm({
                        ...comm,
                        recipients: comm.recipients.map(r => 
                          r.id === currentUser.workerId
                            ? { ...r, letto: true, dataLettura: new Date().toISOString() }
                            : r
                        )
                      });
                    }}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Conferma Lettura
                  </button>
                )}
              </div>
            </div>
          );
        }
        
        // Lista comunicazioni
        return (
          <div className="space-y-6">
            {/* 🆕 Banner contratto scaduto per LAVORATORE */}
            {commLavReadOnly && (
              <ContractExpiredBanner scadenzaDate={commLavAccessStatus.company?.dataScadenzaContratto} role="LAVORATORE" />
            )}
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Le Mie Comunicazioni</h1>
              <p className="text-gray-600">Comunicazioni ricevute dalla tua azienda</p>
            </div>
            
            {/* Filtri */}
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Filtro stato */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Stato:</span>
                  <select
                    value={workerCommFilter}
                    onChange={(e) => setWorkerCommFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Tutte</option>
                    <option value="unread">Da leggere</option>
                    <option value="read">Lette</option>
                  </select>
                </div>
                
                {/* Filtro date */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Periodo:</span>
                  <select
                    value={workerCommDateFilter}
                    onChange={(e) => setWorkerCommDateFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Tutti</option>
                    <option value="3months">Ultimi 3 mesi</option>
                    <option value="6months">Ultimi 6 mesi</option>
                    <option value="1year">Ultimo anno</option>
                    <option value="custom">Personalizzato</option>
                  </select>
                </div>
                
                {workerCommDateFilter === 'custom' && (
                  <>
                    <input
                      type="date"
                      value={workerCommDateFrom}
                      onChange={(e) => setWorkerCommDateFrom(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Da"
                    />
                    <input
                      type="date"
                      value={workerCommDateTo}
                      onChange={(e) => setWorkerCommDateTo(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="A"
                    />
                  </>
                )}
                
                {/* Filtro scadenze */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Scadenza:</span>
                  <select
                    value={workerCommExpireFilter}
                    onChange={(e) => setWorkerCommExpireFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Tutte</option>
                    <option value="expiring">In scadenza (≤30 gg)</option>
                    <option value="expired">Scadute</option>
                    <option value="noexpiry">Senza scadenza</option>
                  </select>
                </div>
                
                {/* Filtro tipologia */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Tipologia:</span>
                  <select
                    value={workerCommTypeFilter}
                    onChange={(e) => setWorkerCommTypeFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Tutte</option>
                    <option value="Messaggio">Messaggio</option>
                    <option value="Azione">Azione</option>
                    <option value="Documento">Documento</option>
                  </select>
                </div>
                
                <div className="ml-auto text-sm text-gray-500">
                  {filteredWorkerComms.length} comunicazioni
                </div>
              </div>
            </div>
            
            {/* Lista */}
            {filteredWorkerComms.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-md">
                <Mail className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Nessuna comunicazione trovata</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Stato</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Titolo</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Tipologia</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Data Invio</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Scadenza</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredWorkerComms.map(comm => {
                      const recipient = comm.recipients?.find(r => r.id === currentUser.workerId);
                      const isRead = recipient?.letto;
                      return (
                        <tr 
                          key={comm.id}
                          className={`hover:bg-gray-50 cursor-pointer ${!isRead ? 'bg-blue-50' : ''}`}
                          onClick={() => setSelectedWorkerComm(comm)}
                        >
                          <td className="px-4 py-3">
                            {isRead ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <CheckCircle className="w-4 h-4" />
                                Letto
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                Da leggere
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={!isRead ? 'font-semibold' : ''}>{comm.titolo}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              comm.tipologia === 'Documento' ? 'bg-purple-100 text-purple-700' :
                              comm.tipologia === 'Azione' ? 'bg-orange-100 text-orange-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {comm.tipologia}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDateOnly(comm.dataInvio)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {comm.dataScadenza ? (
                              <span className="text-orange-600">{formatDateOnly(comm.dataScadenza)}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedWorkerComm(comm);
                              }}
                              className="p-2 hover:bg-gray-100 rounded text-blue-600"
                              title="Visualizza"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'worker-documents':
        // 🆕 Controllo accesso LAVORATORE
        const docLavAccessStatus = getLavoratoreAccessStatus();
        if (!docLavAccessStatus.canAccess) {
          return <AccessBlockedComponent companyName={docLavAccessStatus.company?.ragioneSociale} role="LAVORATORE" />;
        }
        const docLavReadOnly = docLavAccessStatus.isReadOnly;
        
        const workerDocs = getWorkerDocuments();
        const filteredWorkerDocs = filterWorkerDocs(workerDocs, workerDocFilter, workerDocSearch, workerDocTypeFilter);
        
        // Estrai tipi unici dai documenti del lavoratore
        const workerDocTypes = [...new Set(workerDocs.map(d => d.tipo))].sort();
        
        // Se c'è un documento selezionato, mostra il dettaglio
        if (selectedWorkerDoc) {
          const doc = selectedWorkerDoc;
          const recipient = doc.recipients?.find(r => r.id === currentUser.workerId);
          const isRead = recipient?.letto;
          const isApproved = recipient?.approvato;
          const docCompany = companies.find(c => c.id === doc.companyId);
          
          return (
            <div className="space-y-6">
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <button 
                  onClick={() => setSelectedWorkerDoc(null)}
                  className="hover:text-blue-600 flex items-center gap-1"
                >
                  ← Torna ai documenti
                </button>
              </div>
              
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                        {doc.tipo}
                      </span>
                      {isApproved && (
                        <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">
                          ✓ Approvato
                        </span>
                      )}
                      {isRead && (
                        <span className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700">
                          ✓ Visualizzato
                        </span>
                      )}
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">{doc.descrizione}</h1>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">Azienda</p>
                    <p className="font-medium">{docCompany?.ragioneSociale || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Data Caricamento</p>
                    <p className="font-medium">{formatDateOnly(doc.dataCaricamento)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Revisione</p>
                    <p className="font-medium">{formatDateOnly(doc.dataRevisione)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">File</p>
                    <p className="font-medium text-blue-600">{doc.file}</p>
                  </div>
                </div>
                
                {/* Registro Firme / Tracciabilità */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">Registro Firme</h3>
                  <div className="space-y-2">
                    {isApproved && (
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <ClipboardCheck className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-700">Documento Approvato</span>
                        </div>
                        <span className="text-sm text-green-600">{formatDate(recipient?.dataApprovazione)}</span>
                      </div>
                    )}
                    {isRead && (
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Eye className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-blue-700">Presa Visione</span>
                        </div>
                        <span className="text-sm text-blue-600">{formatDate(recipient?.dataLettura)}</span>
                      </div>
                    )}
                    {!isApproved && !isRead && (
                      <p className="text-gray-500 text-center py-2">Nessuna azione registrata</p>
                    )}
                  </div>
                </div>
                
                {/* Azioni */}
                {docLavReadOnly ? (
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-2 text-orange-700">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">Azioni non disponibili</span>
                    </div>
                    <p className="text-sm text-orange-600 mt-1">
                      Il contratto è scaduto - solo visualizzazione informazioni
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col sm:flex-row gap-3">
                      {/* Pulsante Visualizza PDF */}
                      <button
                        onClick={() => {
                          // Registra la visualizzazione
                          viewDocument(doc.id);
                          setSelectedWorkerDoc({
                            ...doc,
                            recipients: doc.recipients.map(r => 
                              r.id === currentUser.workerId
                                ? { ...r, letto: true, dataLettura: new Date().toISOString() }
                                : r
                            )
                          });
                          // Simula apertura PDF in nuova scheda
                          alert(`Visualizzazione PDF: ${doc.file}\n\nIn produzione il documento verrà aperto direttamente nel browser.`);
                          // In produzione: window.open(doc.fileUrl, '_blank');
                        }}
                        className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                      >
                        <Eye className="w-5 h-5" />
                        Visualizza PDF
                      </button>
                      
                      {/* Pulsante Scarica */}
                      <button
                        onClick={() => {
                          viewDocument(doc.id);
                          setSelectedWorkerDoc({
                            ...doc,
                            recipients: doc.recipients.map(r => 
                              r.id === currentUser.workerId
                                ? { ...r, letto: true, dataLettura: new Date().toISOString() }
                                : r
                            )
                          });
                          // Simula download
                          alert(`Download simulato: ${doc.file}\n\nIn produzione verrà scaricato il file reale.`);
                        }}
                        className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                      >
                        <Download className="w-5 h-5" />
                        Scarica Documento
                      </button>
                    </div>
                    
                    {/* Pulsante Approva - solo se già visualizzato */}
                    {!isApproved && (
                      <div className="mt-3">
                        {!isRead ? (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm text-amber-700 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" />
                              <span>Per approvare il documento devi prima visualizzarlo o scaricarlo</span>
                            </p>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              approveDocument(doc.id);
                              setSelectedWorkerDoc({
                                ...doc,
                                recipients: doc.recipients.map(r => 
                                  r.id === currentUser.workerId
                                    ? { ...r, approvato: true, dataApprovazione: new Date().toISOString() }
                                    : r
                                )
                              });
                            }}
                            className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                          >
                            <ClipboardCheck className="w-5 h-5" />
                            Approva Documento
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        }
        
        // Lista documenti
        return (
          <div className="space-y-6">
            {/* 🆕 Banner contratto scaduto per LAVORATORE */}
            {docLavReadOnly && (
              <ContractExpiredBanner scadenzaDate={docLavAccessStatus.company?.dataScadenzaContratto} role="LAVORATORE" />
            )}
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Documenti Ricevuti</h1>
              <p className="text-gray-600">Documenti aziendali condivisi con te</p>
            </div>
            
            {/* Filtri */}
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Campo ricerca */}
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <Search className="w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca documento..."
                    value={workerDocSearch}
                    onChange={(e) => setWorkerDocSearch(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                {/* Filtro stato */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Stato:</span>
                  <select
                    value={workerDocFilter}
                    onChange={(e) => setWorkerDocFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Tutti</option>
                    <option value="unread">Da visualizzare</option>
                    <option value="read">Visualizzati</option>
                    <option value="approved">Approvato</option>
                    <option value="notapproved">Non approvato</option>
                  </select>
                </div>
                
                {/* Filtro tipo documento */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Tipo:</span>
                  <select
                    value={workerDocTypeFilter}
                    onChange={(e) => setWorkerDocTypeFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Tutti</option>
                    {workerDocTypes.map(tipo => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </div>
                
                <div className="ml-auto text-sm text-gray-500">
                  {filteredWorkerDocs.length} document{filteredWorkerDocs.length === 1 ? 'o' : 'i'}
                </div>
              </div>
            </div>
            
            {/* Lista */}
            {filteredWorkerDocs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-md">
                <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Nessun documento trovato</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Stato</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Documento</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Tipo</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Data</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Approvato</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredWorkerDocs.map(doc => {
                      const recipient = doc.recipients?.find(r => r.id === currentUser.workerId);
                      const isRead = recipient?.letto;
                      const isApproved = recipient?.approvato;
                      return (
                        <tr 
                          key={doc.id}
                          className={`hover:bg-gray-50 cursor-pointer ${!isRead ? 'bg-purple-50' : ''}`}
                          onClick={() => setSelectedWorkerDoc(doc)}
                        >
                          <td className="px-4 py-3">
                            {isRead ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <CheckCircle className="w-4 h-4" />
                                Visualizzato
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-purple-600 font-medium">
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                Da visualizzare
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={!isRead ? 'font-semibold' : ''}>{doc.descrizione}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                              {doc.tipo}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDateOnly(doc.dataCaricamento)}
                          </td>
                          <td className="px-4 py-3">
                            {isApproved ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <CheckCircle className="w-4 h-4" />
                                Sì
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedWorkerDoc(doc);
                              }}
                              className="p-2 hover:bg-gray-100 rounded text-blue-600"
                              title="Visualizza"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'worker-profile':
        // 🆕 Controllo accesso LAVORATORE
        const profLavAccessStatus = getLavoratoreAccessStatus();
        if (!profLavAccessStatus.canAccess) {
          return <AccessBlockedComponent companyName={profLavAccessStatus.company?.ragioneSociale} role="LAVORATORE" />;
        }
        const profLavReadOnly = profLavAccessStatus.isReadOnly;
        
        const profileWorker = workers.find(w => w.id === currentUser.workerId);
        const profileCompany = companies.find(c => String(c.id) === String(currentUser.companyId));
        
        if (!profileWorker || !profileCompany) {
          return (
            <div className="text-center py-12 bg-white rounded-xl shadow-md">
              <UserCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Profilo non trovato</p>
            </div>
          );
        }
        
        // Verifica se è il datore di lavoro
        const isProfileDatore = profileWorker.email === profileCompany.datoreLavoro.email && 
                                profileCompany.datoreLavoro.cumuloRuoli;
        
        // Costruisci organigramma
        const getRsppName = () => {
          if (profileCompany.rspp?.nome && profileCompany.rspp?.cognome) {
            return `${profileCompany.rspp.nome} ${profileCompany.rspp.cognome}`;
          }
          return '-';
        };
        
        const getMedicoName = () => {
          if (profileCompany.medicoCompetente?.nome && profileCompany.medicoCompetente?.cognome) {
            return `${profileCompany.medicoCompetente.nome} ${profileCompany.medicoCompetente.cognome}`;
          }
          return '-';
        };
        
        const organigramma = {
          figureAziendali: [
            { 
              ruolo: 'Datore di Lavoro', 
              nome: `${profileCompany.datoreLavoro.nome} ${profileCompany.datoreLavoro.cognome}`, 
              email: profileCompany.datoreLavoro.email,
              cellulare: profileCompany.datoreLavoro.cellulare || ''
            },
            { 
              ruolo: 'RSPP', 
              nome: getRsppName(), 
              email: profileCompany.rspp?.email || '-',
              cellulare: profileCompany.rspp?.cellulare || ''
            },
            { 
              ruolo: 'Medico Competente', 
              nome: getMedicoName(), 
              email: profileCompany.medicoCompetente?.email || '-',
              cellulare: profileCompany.medicoCompetente?.cellulare || ''
            }
          ],
          ruoliSicurezza: workers
            .filter(w => w.companyId === currentUser.companyId && w.ruoli && w.ruoli.length > 0 && !w.dataFine)
            .map(w => ({
              nome: `${w.nome} ${w.cognome}`,
              ruoli: w.ruoli,
              email: w.email,
              cellulare: w.cellulare || ''
            }))
        };
        
        return (
          <div className="space-y-6">
            {/* 🆕 Banner contratto scaduto per LAVORATORE */}
            {profLavReadOnly && (
              <ContractExpiredBanner scadenzaDate={profLavAccessStatus.company?.dataScadenzaContratto} role="LAVORATORE" />
            )}
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Il Mio Profilo</h1>
              <p className="text-gray-600">I tuoi dati personali e l'organigramma aziendale</p>
            </div>
            
            {/* Dati Personali */}
            <div className="bg-blue-50 rounded-xl shadow-md p-6 border border-blue-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <UserCircle className="w-6 h-6 text-blue-600" />
                Dati Personali
                {isProfileDatore && (
                  <span className="ml-2 px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    DATORE
                  </span>
                )}
              </h2>
              
              {isProfileDatore && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <strong>Ruoli cumulati (Art. 34 D.Lgs. 81/2008)</strong> - Stai svolgendo anche il ruolo di Datore di Lavoro
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Nome e Cognome</p>
                    <p className="font-medium text-lg">{profileWorker.nome} {profileWorker.cognome}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Codice Fiscale</p>
                    <p className="font-medium font-mono">{profileWorker.codiceFiscale}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Data di Nascita</p>
                    <p className="font-medium">{formatDateOnly(profileWorker.dataNascita)} - {profileWorker.luogoNascita}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-blue-600">{profileWorker.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cellulare</p>
                    <p className="font-medium">{profileWorker.cellulare || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Data Assunzione</p>
                    <p className="font-medium">{formatDateOnly(profileWorker.dataAssunzione)}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Mansione</p>
                    <p className="font-medium">{profileWorker.mansione || 'Non assegnata'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Ruoli Sicurezza</p>
                    {profileWorker.ruoli && profileWorker.ruoli.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {profileWorker.ruoli.map((ruolo, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                            {ruolo}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="font-medium text-gray-500">Nessuno</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Azienda di Appartenenza */}
            <div className="bg-emerald-50 rounded-xl shadow-md p-6 border border-emerald-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-emerald-600" />
                Azienda di Appartenenza
              </h2>
              <div className="flex items-start gap-6">
                {/* Logo Aziendale */}
                {profileCompany.logo && (
                  <div className="flex-shrink-0">
                    <img 
                      src={profileCompany.logo} 
                      alt="Logo Aziendale" 
                      className="w-20 h-20 object-contain border rounded-lg bg-white p-2"
                    />
                  </div>
                )}
                {/* Dati Azienda */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">Ragione Sociale</p>
                    <p className="font-medium text-lg">{profileCompany.ragioneSociale}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Indirizzo</p>
                    <p className="font-medium">{profileCompany.indirizzo}, {profileCompany.citta}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">P.IVA</p>
                    <p className="font-medium font-mono">{profileCompany.partitaIva}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">PEC</p>
                    <p className="font-medium text-blue-600">{profileCompany.pec}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Organigramma Aziendale */}
            <div className="bg-violet-50 rounded-xl shadow-md p-6 border border-violet-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-6 h-6 text-violet-600" />
                Organigramma Aziendale
              </h2>
              
              {/* Figure Aziendali */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Figure Aziendali</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {organigramma.figureAziendali.map((figura, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-500 mb-1">{figura.ruolo}</p>
                      <p className="font-medium">{figura.nome}</p>
                      {/* Link disabilitati se contratto scaduto */}
                      {profLavReadOnly ? (
                        <span className="text-sm text-gray-500 block">{figura.email}</span>
                      ) : (
                        <a href={`mailto:${figura.email}`} className="text-sm text-blue-600 hover:underline block">{figura.email}</a>
                      )}
                      {figura.cellulare && (
                        profLavReadOnly ? (
                          <span className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                            <Phone className="w-3 h-3" />
                            {figura.cellulare}
                          </span>
                        ) : (
                          <a href={`tel:${figura.cellulare.replace(/\s/g, '')}`} className="text-sm text-gray-600 hover:text-blue-600 flex items-center gap-1 mt-1">
                            <Phone className="w-3 h-3" />
                            {figura.cellulare}
                          </a>
                        )
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Ruoli Sicurezza */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Ruoli Sicurezza Lavoratori</h3>
                {organigramma.ruoliSicurezza.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Nessun lavoratore con ruoli di sicurezza assegnati</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left px-4 py-2 text-sm font-semibold text-gray-700">Nome</th>
                          <th className="text-left px-4 py-2 text-sm font-semibold text-gray-700">Ruoli</th>
                          <th className="text-left px-4 py-2 text-sm font-semibold text-gray-700">Email</th>
                          <th className="text-left px-4 py-2 text-sm font-semibold text-gray-700">Cellulare</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {organigramma.ruoliSicurezza.map((persona, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium">{persona.nome}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {persona.ruoli.map((ruolo, rIdx) => (
                                  <span key={rIdx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                    {ruolo}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {/* Link disabilitati se contratto scaduto */}
                              {profLavReadOnly ? (
                                <span className="text-gray-500">{persona.email}</span>
                              ) : (
                                <a href={`mailto:${persona.email}`} className="text-blue-600 hover:underline">{persona.email}</a>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {persona.cellulare ? (
                                profLavReadOnly ? (
                                  <span className="flex items-center gap-1 text-gray-500">
                                    <Phone className="w-3 h-3" />
                                    {persona.cellulare}
                                  </span>
                                ) : (
                                  <a href={`tel:${persona.cellulare.replace(/\s/g, '')}`} className="hover:text-blue-600 flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {persona.cellulare}
                                  </a>
                                )
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'audit-log':
        // 🔐 FASE 6.2: Pannello Registro Tracciabilità (ADMIN e AZIENDA)
        const filteredLogs = auditLogs.filter(log => {
          if (auditFilter.action && log.action !== auditFilter.action) return false;
          if (auditFilter.entityType && log.entity_type !== auditFilter.entityType) return false;
          if (auditFilter.companyId) {
            const logCompanyId = String(log.new_values?.companyId || '');
            if (logCompanyId !== String(auditFilter.companyId)) return false;
          }
          if (auditFilter.search) {
            const searchLower = auditFilter.search.toLowerCase();
            const matchEmail = (log.user_email || '').toLowerCase().includes(searchLower);
            const matchEntity = (log.entity_id || '').toLowerCase().includes(searchLower);
            const matchValues = JSON.stringify(log.new_values || {}).toLowerCase().includes(searchLower);
            if (!matchEmail && !matchEntity && !matchValues) return false;
          }
          return true;
        });

        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Registro Tracciabilità</h1>
              <p className="text-gray-500">
                {currentUser.role === 'AZIENDA' 
                  ? 'Registro azioni della tua azienda - Tracciabilita legale D.Lgs. 81/2008'
                  : 'Registro immutabile di tutte le azioni - Tracciabilita legale D.Lgs. 81/2008'}
              </p>
            </div>

            {/* Statistiche */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="text-2xl font-bold text-blue-600">{auditLogs.length}</div>
                <div className="text-sm text-gray-500">Eventi totali</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="text-2xl font-bold text-green-600">{auditLogs.filter(l => l.action === 'CREATE').length}</div>
                <div className="text-sm text-gray-500">Creazioni</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="text-2xl font-bold text-purple-600">{auditLogs.filter(l => l.action === 'SHARE').length}</div>
                <div className="text-sm text-gray-500">Condivisioni</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="text-2xl font-bold text-yellow-600">{auditLogs.filter(l => l.action === 'CONFIRM').length}</div>
                <div className="text-sm text-gray-500">Conferme</div>
              </div>
            </div>

            {/* Filtri */}
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Filtri:</span>
                </div>
                <select 
                  value={auditFilter.action} 
                  onChange={(e) => setAuditFilter({...auditFilter, action: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-lg"
                >
                  <option value="">Tutte le azioni</option>
                  <option value="CREATE">Creazione</option>
                  <option value="UPDATE">Modifica</option>
                  <option value="DELETE">Eliminazione</option>
                  <option value="SHARE">Condivisione</option>
                  <option value="CONFIRM">Conferma</option>
                </select>
                <select 
                  value={auditFilter.entityType} 
                  onChange={(e) => setAuditFilter({...auditFilter, entityType: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-lg"
                >
                  <option value="">Tutte le entita</option>
                  <option value="company">Azienda</option>
                  <option value="worker">Lavoratore</option>
                  <option value="communication">Comunicazione</option>
                  <option value="document">Documento</option>
                  <option value="communication_read">Lettura Com.</option>
                  <option value="document_share">Condivisione Doc.</option>
                </select>
                {currentUser.role === 'ADMIN' && (
                  <select 
                    value={auditFilter.companyId} 
                    onChange={(e) => setAuditFilter({...auditFilter, companyId: e.target.value})}
                    className="px-3 py-1.5 text-sm border rounded-lg"
                  >
                    <option value="">Tutte le aziende</option>
                    {companies.filter(c => c.statoContratto !== 'Disattivato').map(c => (
                      <option key={c.id} value={c.id}>{c.ragioneSociale}</option>
                    ))}
                  </select>
                )}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca per email, ID o dettaglio..."
                    value={auditFilter.search}
                    onChange={(e) => setAuditFilter({...auditFilter, search: e.target.value})}
                    className="w-full pl-9 pr-3 py-1.5 text-sm border rounded-lg"
                  />
                </div>
                <button
                  onClick={loadAuditLogs}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Aggiorna
                </button>
              </div>
            </div>

            {/* Tabella Log */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {auditLoading ? (
                <div className="p-8 text-center text-gray-500">Caricamento audit log...</div>
              ) : filteredLogs.length === 0 ? (
                <div className="p-8 text-center text-gray-500">Nessun evento trovato</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Ora</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azione</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entita</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azienda</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dettagli</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredLogs.map((log, idx) => {
                        const actionInfo = formatAuditAction(log.action);
                        const details = log.new_values || {};
                        const detailStr = Object.entries(details)
                          .filter(([k]) => !['hashSha256', 'companyId', 'companyName'].includes(k))
                          .map(([k, v]) => k + ': ' + v)
                          .join(' | ');
                        return (
                          <tr key={log.id || idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                              {log.created_at ? formatDateUTC(log.created_at) : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={'px-2 py-1 text-xs font-medium rounded-full ' + actionInfo.color}>
                                {actionInfo.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatAuditEntity(log.entity_type)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              {details.companyName || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {log.user_email || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-xs cursor-pointer" title="Clicca per vedere i dettagli completi" onClick={() => { if (detailStr) alert(detailStr.replaceAll(' | ', '\n')); }}>
                              <div className="truncate hover:text-blue-600">{detailStr || '-'}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500">
                {filteredLogs.length} eventi{auditFilter.action || auditFilter.entityType || auditFilter.companyId || auditFilter.search ? ' (filtrati)' : ''} su {auditLogs.length} totali
              </div>
            </div>
          </div>
        );

      case 'settings':
        // 🆕 Controllo accesso AZIENDA
        if (currentUser.role === 'AZIENDA') {
          const settingsAccessStatus = getAziendaAccessStatus();
          // DISATTIVATA = blocco totale
          if (!settingsAccessStatus.canAccess) {
            return <AccessBlockedComponent companyName={settingsAccessStatus.company?.ragioneSociale} role="AZIENDA" />;
          }
        }
        const settingsAziendaReadOnly = currentUser.role === 'AZIENDA' && getAziendaAccessStatus().isReadOnly;
        
        return (
          <div className="space-y-6">
            {/* 🆕 Banner contratto scaduto per AZIENDA */}
            {settingsAziendaReadOnly && (
              <ContractExpiredBanner scadenzaDate={getAziendaAccessStatus().company?.dataScadenzaContratto} role="AZIENDA" />
            )}
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Impostazioni</h1>
              <p className="text-gray-600">Configura i template per mansioni, ruoli e documenti</p>
            </div>

            {/* 🆕 LOGO CONSULENTE - Solo per ADMIN */}
            {currentUser.role === 'ADMIN' && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <ImageIcon className="w-6 h-6 text-blue-600" />
                  Logo Studio / Consulente
                </h2>
                <div className="flex items-center gap-6">
                  <div className="flex-shrink-0">
                    {consultantLogo ? (
                      <div className="relative">
                        <img 
                          src={consultantLogo} 
                          alt="Logo Consulente" 
                          className="w-24 h-24 object-contain border rounded-lg bg-gray-50 p-2"
                        />
                        <button
                          onClick={() => setConsultantLogo(null)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome Studio</label>
                      <input
                        type="text"
                        value={consultantName}
                        onChange={(e) => setConsultantName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Es: Studio Sicurezza & Consulenza"
                      />
                    </div>
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      <Upload className="w-4 h-4" />
                      {consultantLogo ? 'Cambia Logo' : 'Carica Logo'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setConsultantLogo(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-2">Formati: PNG, JPG, SVG. Max 2MB.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Mansioni Template */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Mansioni Lavorative</h2>
                  <button 
                    onClick={() => openModal('newMansione')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Aggiungi
                  </button>
                </div>
                <div className="space-y-2">
                  {mansioniTemplate.map((mansione, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-3">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-900">{mansione}</span>
                      </div>
                      <button 
                        onClick={() => handleDeleteMansione(mansione)}
                        className="p-1 hover:bg-blue-100 rounded text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ruoli Template */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Ruoli Aziendali</h2>
                  <button 
                    onClick={() => openModal('newRuolo')}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Aggiungi
                  </button>
                </div>
                <div className="space-y-2">
                  {ruoliTemplate.map((ruolo, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-purple-600" />
                        <span className="font-medium text-gray-900">{ruolo}</span>
                      </div>
                      <button 
                        onClick={() => handleDeleteRuolo(ruolo)}
                        className="p-1 hover:bg-purple-100 rounded text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 🆕 TEMPLATE DOCUMENTI (ADMIN + AZIENDA) */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Template Documenti</h2>
                  <button 
                    onClick={() => openModal('newDocumento')}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Aggiungi
                  </button>
                </div>
                <div className="space-y-2">
                  {documentiTemplate.map((documento, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <FileCheck className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-gray-900">{documento}</span>
                      </div>
                      <button 
                        onClick={() => handleDeleteDocumento(documento)}
                        className="p-1 hover:bg-green-100 rounded text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

              {/* 📋 INFORMAZIONI SOFTWARE */}
              <div className="bg-white rounded-xl shadow-md p-6 mt-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Info className="w-6 h-6 text-blue-600" />
                  Informazioni Software
                </h2>
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-lg font-bold text-blue-900">SafeTracker v1.0.0</p>
                  <p className="text-sm text-blue-700 mt-1">Gestione Sicurezza sul Lavoro — D.Lgs. 81/2008</p>
                  <p className="text-sm text-gray-600 mt-1">Progetto realizzato da MDM Consulting Engineering S.r.l.</p>
                  <p className="text-xs text-gray-500 mt-2">https://www.safetracker.it</p>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Registro Versioni</h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-green-800">v1.0.0</span>
                        <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Attuale</span>
                        <span className="text-xs text-gray-500">09/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Prima release ufficiale. Favicon e titolo personalizzati, guida operativa, RLS policies ristrette, .env rimosso da Git.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.9.4</span>
                        <span className="text-xs text-gray-500">09/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Fix footer email (leggibilità migliorata). Fix bug Bozza→Pubblicato (hash SHA-256 + invio email).</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.9.3</span>
                        <span className="text-xs text-gray-500">09/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Fix destinatari organizzativi UUID. Verifica dominio Resend. Testo no-reply nelle email.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.9.2</span>
                        <span className="text-xs text-gray-500">08/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Ricerca lavoratore per cognome e ordinamento lista nel Cambia Profilo.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.9.1</span>
                        <span className="text-xs text-gray-500">08/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Conferma password per eliminazione aziende. Cambia Profilo rinominato con isRealAdmin.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.9.0</span>
                        <span className="text-xs text-gray-500">08/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Login Supabase Auth (email + password). Sostituisce gate password temporaneo.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.8.4</span>
                        <span className="text-xs text-gray-500">08/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Fix storage_path documenti. 5 bug fix (sidebar, email bozza, elimina bozza, selezione testo, dettagli audit).</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.8.3</span>
                        <span className="text-xs text-gray-500">07/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Deploy online su safetracker.it. Git, GitHub, Vercel, dominio, Supabase configurato.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.8.2</span>
                        <span className="text-xs text-gray-500">07/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Audit log migliorato con companyId/companyName. Timestamp UTC. Registro Tracciabilità rinominato.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.8.1</span>
                        <span className="text-xs text-gray-500">06/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Fase 6.2 Audit Log Immutabile.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.8.0</span>
                        <span className="text-xs text-gray-500">06/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Fase 6.1 Hash SHA-256 per comunicazioni e documenti.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.7.x</span>
                        <span className="text-xs text-gray-500">04-06/02/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Export PDF, Import XLSX, Email Resend, Upload Storage, CRUD completo, Controllo accessi contratti.</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-800">v0.1-0.6</span>
                        <span className="text-xs text-gray-500">01/2026</span>
                      </div>
                      <p className="text-sm text-gray-700">Struttura base, profili ADMIN/AZIENDA/LAVORATORE, import massivo, layout compatto, dismissioni, storico eventi.</p>
                    </div>
                  </div>
                </div>
              </div>

          </div>
        );

      default:
        return null;
    }
  };

  // Modals
  const renderModal = () => {
    if (!showModal) return null;

    if (modalType === 'newCompany' || modalType === 'editCompany') {
      const isEdit = modalType === 'editCompany';
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-blue-600 text-white p-6 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <h2 className="text-2xl font-bold">{isEdit ? 'Modifica' : 'Nuova'} Azienda</h2>
              <button onClick={closeModal} className="p-2 hover:bg-blue-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Anagrafica Aziendale */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Anagrafica Aziendale</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ragione Sociale *</label>
                    <input
                      type="text"
                      value={newCompany.ragioneSociale}
                      onChange={(e) => setNewCompany({...newCompany, ragioneSociale: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Es. Manifattura Italiana SRL"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Indirizzo Sede Operativa *</label>
                    <input
                      type="text"
                      value={newCompany.indirizzo}
                      onChange={(e) => setNewCompany({...newCompany, indirizzo: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Via Roma, 123"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Città *</label>
                    <input
                      type="text"
                      value={newCompany.citta}
                      onChange={(e) => setNewCompany({...newCompany, citta: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Milano"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PEC *</label>
                    <input
                      type="email"
                      value={newCompany.pec}
                      onChange={(e) => setNewCompany({...newCompany, pec: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="azienda@pec.it"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Partita IVA *</label>
                    <input
                      type="text"
                      value={newCompany.partitaIva}
                      onChange={(e) => setNewCompany({...newCompany, partitaIva: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="12345678901"
                    />
                  </div>
                </div>
              </div>

              {/* 🆕 Gestione Contratto */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-blue-600" />
                  Gestione Contratto
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Tipo Contratto - SOLO ADMIN */}
                  {currentUser.role === 'ADMIN' && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tipo Contratto *</label>
                      <select
                        value={newCompany.tipoContratto}
                        onChange={(e) => setNewCompany({...newCompany, tipoContratto: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="gestione_completa">Gestione Completa (Servizio Sicurezza)</option>
                        <option value="solo_piattaforma">Solo Piattaforma (Licenza SafeTracker)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {newCompany.tipoContratto === 'gestione_completa' 
                          ? '✓ Comunicazioni, documenti e gestione lavoratori abilitati'
                          : '⚡ Solo accesso piattaforma - gestione autonoma da parte del cliente'}
                      </p>
                    </div>
                  )}
                  {/* Stato Attiva/Disattivata - SOLO ADMIN */}
                  {currentUser.role === 'ADMIN' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stato</label>
                      <div className="flex items-center gap-3 h-[42px]">
                        <button
                          type="button"
                          onClick={() => setNewCompany({...newCompany, attiva: !newCompany.attiva})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            newCompany.attiva ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            newCompany.attiva ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                        <span className={`text-sm font-medium ${newCompany.attiva ? 'text-green-600' : 'text-gray-500'}`}>
                          {newCompany.attiva ? 'Attiva' : 'Disattivata'}
                        </span>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Importo Annuo (€)</label>
                    <input
                      type="number"
                      value={newCompany.importoContratto}
                      onChange={(e) => setNewCompany({...newCompany, importoContratto: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Data Inizio Contratto</label>
                    <input
                      type="date"
                      value={newCompany.dataInizioContratto}
                      onChange={(e) => setNewCompany({...newCompany, dataInizioContratto: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Data Scadenza Contratto *</label>
                    <input
                      type="date"
                      value={newCompany.dataScadenzaContratto}
                      onChange={(e) => setNewCompany({...newCompany, dataScadenzaContratto: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Note Contratto</label>
                    <input
                      type="text"
                      value={newCompany.noteContratto}
                      onChange={(e) => setNewCompany({...newCompany, noteContratto: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Es. Rinnovo tacito, sconto applicato, ecc."
                    />
                  </div>
                </div>
              </div>

              {/* Datore di Lavoro */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Datore di Lavoro</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome *</label>
                    <input
                      type="text"
                      value={newCompany.datoreLavoro.nome}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        datoreLavoro: {...newCompany.datoreLavoro, nome: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cognome *</label>
                    <input
                      type="text"
                      value={newCompany.datoreLavoro.cognome}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        datoreLavoro: {...newCompany.datoreLavoro, cognome: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={newCompany.datoreLavoro.email}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        datoreLavoro: {...newCompany.datoreLavoro, email: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cellulare</label>
                    <input
                      type="tel"
                      value={newCompany.datoreLavoro.cellulare || ''}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        datoreLavoro: {...newCompany.datoreLavoro, cellulare: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+39 333 1234567"
                    />
                  </div>
                </div>
                
                {/* 🆕 CUMULO RUOLI OPERATIVI (Art. 34 D.Lgs. 81/2008) */}
                <div className="mt-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newCompany.datoreLavoro.cumulaRuoli || false}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        datoreLavoro: {
                          ...newCompany.datoreLavoro,
                          cumulaRuoli: e.target.checked,
                          ruoliCumulati: e.target.checked ? (newCompany.datoreLavoro.ruoliCumulati || []) : []
                        }
                      })}
                      className="mt-1 w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-amber-600" />
                        <p className="font-bold text-gray-900">
                          Il Datore di Lavoro cumula ruoli operativi
                        </p>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Art. 34 D.Lgs. 81/2008 - Consentito nelle aziende fino a 5 lavoratori o nelle aziende artigiane e industriali fino a 30 lavoratori
                      </p>
                    </div>
                  </label>

                  {newCompany.datoreLavoro.cumulaRuoli && (
                    <div className="mt-4 ml-7 p-4 bg-white border border-amber-200 rounded-lg space-y-3">
                      <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-amber-600" />
                        Seleziona ruoli cumulati dal Datore:
                      </p>
                      
                      <label className="flex items-center gap-2 p-2 hover:bg-amber-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newCompany.datoreLavoro.ruoliCumulati?.includes('Addetto Antincendio') || false}
                          onChange={(e) => {
                            const ruoli = newCompany.datoreLavoro.ruoliCumulati || [];
                            const newRuoli = e.target.checked
                              ? [...ruoli, 'Addetto Antincendio']
                              : ruoli.filter(r => r !== 'Addetto Antincendio');
                            setNewCompany({
                              ...newCompany,
                              datoreLavoro: {
                                ...newCompany.datoreLavoro,
                                ruoliCumulati: newRuoli
                              }
                            });
                          }}
                          className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium text-gray-900">🔥 Addetto Antincendio</span>
                      </label>

                      <label className="flex items-center gap-2 p-2 hover:bg-amber-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newCompany.datoreLavoro.ruoliCumulati?.includes('Addetto Primo Soccorso') || false}
                          onChange={(e) => {
                            const ruoli = newCompany.datoreLavoro.ruoliCumulati || [];
                            const newRuoli = e.target.checked
                              ? [...ruoli, 'Addetto Primo Soccorso']
                              : ruoli.filter(r => r !== 'Addetto Primo Soccorso');
                            setNewCompany({
                              ...newCompany,
                              datoreLavoro: {
                                ...newCompany.datoreLavoro,
                                ruoliCumulati: newRuoli
                              }
                            });
                          }}
                          className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium text-gray-900">🩹 Addetto Primo Soccorso</span>
                      </label>

                      {newCompany.datoreLavoro.ruoliCumulati && newCompany.datoreLavoro.ruoliCumulati.length > 0 && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                          <p className="text-xs text-blue-800">
                            ℹ️ <strong>Il Datore apparirà automaticamente nell'elenco lavoratori</strong> con mansione "Datore di Lavoro" e i ruoli selezionati. Potrà ricevere comunicazioni mirate per questi ruoli.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Medico Competente */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Medico Competente</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                    <input
                      type="text"
                      value={newCompany.medicoCompetente.nome}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        medicoCompetente: {...newCompany.medicoCompetente, nome: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cognome</label>
                    <input
                      type="text"
                      value={newCompany.medicoCompetente.cognome}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        medicoCompetente: {...newCompany.medicoCompetente, cognome: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={newCompany.medicoCompetente.email}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        medicoCompetente: {...newCompany.medicoCompetente, email: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PEC</label>
                    <input
                      type="email"
                      value={newCompany.medicoCompetente.pec}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        medicoCompetente: {...newCompany.medicoCompetente, pec: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cellulare</label>
                    <input
                      type="tel"
                      value={newCompany.medicoCompetente.cellulare || ''}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        medicoCompetente: {...newCompany.medicoCompetente, cellulare: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+39 340 1234567"
                    />
                  </div>
                </div>
              </div>

              {/* RSPP */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">RSPP (Responsabile Servizio Prevenzione e Protezione)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                    <input
                      type="text"
                      value={newCompany.rspp.nome}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        rspp: {...newCompany.rspp, nome: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cognome</label>
                    <input
                      type="text"
                      value={newCompany.rspp.cognome}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        rspp: {...newCompany.rspp, cognome: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={newCompany.rspp.email}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        rspp: {...newCompany.rspp, email: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PEC</label>
                    <input
                      type="email"
                      value={newCompany.rspp.pec}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        rspp: {...newCompany.rspp, pec: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cellulare</label>
                    <input
                      type="tel"
                      value={newCompany.rspp.cellulare || ''}
                      onChange={(e) => setNewCompany({
                        ...newCompany,
                        rspp: {...newCompany.rspp, cellulare: e.target.value}
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+39 348 1234567"
                    />
                  </div>
                </div>
              </div>

              {/* Codici ATECO */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Codici ATECO</h3>
                  <button 
                    onClick={addAtecoCode}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Aggiungi Codice
                  </button>
                </div>
                <div className="space-y-3">
                  {newCompany.codiciAteco.map((ateco, idx) => (
                    <div key={idx} className="flex gap-3 items-start">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={ateco.codice}
                          onChange={(e) => {
                            const newCodici = [...newCompany.codiciAteco];
                            newCodici[idx].codice = e.target.value;
                            setNewCompany({...newCompany, codiciAteco: newCodici});
                          }}
                          placeholder="Es. 25.11.00"
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          value={ateco.descrizione}
                          onChange={(e) => {
                            const newCodici = [...newCompany.codiciAteco];
                            newCodici[idx].descrizione = e.target.value;
                            setNewCompany({...newCompany, codiciAteco: newCodici});
                          }}
                          placeholder="Descrizione attività"
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      {newCompany.codiciAteco.length > 1 && (
                        <button 
                          onClick={() => removeAtecoCode(idx)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions - Fixed at bottom */}
            <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button 
                onClick={closeModal}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors"
              >
                Annulla
              </button>
              <button 
                onClick={handleSaveCompany}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {isEdit ? 'Salva Modifiche' : 'Salva Azienda'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (modalType === 'newWorker' || modalType === 'editWorker') {
      const isEdit = modalType === 'editWorker';
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="bg-indigo-600 text-white p-6 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-2xl font-bold">{isEdit ? 'Modifica' : 'Nuovo'} Lavoratore</h2>
              <button onClick={closeModal} className="p-2 hover:bg-indigo-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome *</label>
                  <input
                    type="text"
                    value={newWorker.nome}
                    onChange={(e) => setNewWorker({...newWorker, nome: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cognome *</label>
                  <input
                    type="text"
                    value={newWorker.cognome}
                    onChange={(e) => setNewWorker({...newWorker, cognome: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Luogo di Nascita</label>
                  <input
                    type="text"
                    value={newWorker.luogoNascita}
                    onChange={(e) => setNewWorker({...newWorker, luogoNascita: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data di Nascita</label>
                  <input
                    type="date"
                    value={newWorker.dataNascita}
                    onChange={(e) => setNewWorker({...newWorker, dataNascita: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Codice Fiscale *</label>
                  <input
                    type="text"
                    value={newWorker.codiceFiscale}
                    onChange={(e) => setNewWorker({...newWorker, codiceFiscale: e.target.value.toUpperCase()})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent uppercase"
                    maxLength={16}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={newWorker.email}
                    onChange={(e) => setNewWorker({...newWorker, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Numero di Cellulare</label>
                  <input
                    type="tel"
                    value={newWorker.cellulare}
                    onChange={(e) => setNewWorker({...newWorker, cellulare: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="+39 333 1234567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mansione *</label>
                  <select
                    value={newWorker.mansione}
                    onChange={(e) => setNewWorker({...newWorker, mansione: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Seleziona mansione</option>
                    {mansioniTemplate.map((m, idx) => (
                      <option key={idx} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Assunzione *</label>
                  <input
                    type="date"
                    value={newWorker.dataAssunzione}
                    onChange={(e) => setNewWorker({...newWorker, dataAssunzione: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Fine</label>
                  <input
                    type="date"
                    value={newWorker.dataFine}
                    onChange={(e) => setNewWorker({...newWorker, dataFine: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ruoli Aziendali (Multi-selezione)</label>
                <select
                  multiple
                  value={newWorker.ruoli}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setNewWorker({...newWorker, ruoli: selected});
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  size={4}
                >
                  {ruoliTemplate.map((ruolo, idx) => (
                    <option key={idx} value={ruolo}>{ruolo}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Tieni premuto Ctrl (Windows) o Cmd (Mac) per selezionare più ruoli</p>
                
                {newWorker.ruoli.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {newWorker.ruoli.map((ruolo, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full flex items-center gap-2">
                        {ruolo}
                        <button
                          onClick={() => setNewWorker({...newWorker, ruoli: newWorker.ruoli.filter(r => r !== ruolo)})}
                          className="hover:bg-purple-200 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl sticky bottom-0">
              <button 
                onClick={closeModal}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors"
              >
                Annulla
              </button>
              <button 
                onClick={handleSaveWorker}
                className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {isEdit ? 'Salva Modifiche' : 'Salva Lavoratore'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (modalType === 'newCommunication' || modalType === 'editCommunication') {
      const isEdit = modalType === 'editCommunication';
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8" onMouseDown={(e) => e.stopPropagation()}>
            <div className="bg-purple-600 text-white p-6 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-2xl font-bold">{isEdit ? 'Modifica' : 'Nuova'} Comunicazione</h2>
              <button onClick={closeModal} className="p-2 hover:bg-purple-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Azienda *</label>
                {currentUser.role === 'AZIENDA' ? (
                  <input
                    type="text"
                    value={companies.find(c => String(c.id) === String(currentUser.companyId))?.ragioneSociale || ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                ) : (
                  <select
                    value={newCommunication.aziendaId || ''}
                    onChange={(e) => { const val = e.target.value; setNewCommunication({...newCommunication, aziendaId: isNaN(val) ? val : Number(val)}); }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Seleziona azienda</option>
                    {companies
                      .filter(c => c.tipoContratto === 'gestione_completa' && c.attiva)
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.ragioneSociale}</option>
                      ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titolo *</label>
                <input
                  type="text"
                  value={newCommunication.titolo}
                  onChange={(e) => setNewCommunication({...newCommunication, titolo: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Es. Informativa sui Rischi"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contenuto *</label>
                <textarea
                  value={newCommunication.contenuto}
                  onChange={(e) => setNewCommunication({...newCommunication, contenuto: e.target.value})}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Scrivi il contenuto della comunicazione..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipologia *</label>
                  <select
                    value={newCommunication.tipologia}
                    onChange={(e) => setNewCommunication({...newCommunication, tipologia: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="Messaggio">Messaggio</option>
                    <option value="Azione">Azione</option>
                    <option value="Documento">Documento</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stato *</label>
                  <select
                    value={newCommunication.stato}
                    onChange={(e) => setNewCommunication({...newCommunication, stato: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="Bozza">Bozza</option>
                    <option value="Pubblicato">Pubblicato</option>
                  </select>
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800 flex items-start gap-2">
                      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Attenzione:</strong> Una volta pubblicata, la comunicazione verrà inviata ai destinatari e <strong>non potrà più essere modificata</strong>. 
                        Salva come "Bozza" se vuoi apportare modifiche in seguito.
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {newCommunication.tipologia === 'Azione' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Scadenza</label>
                  <input
                    type="date"
                    value={newCommunication.dataScadenza}
                    onChange={(e) => setNewCommunication({...newCommunication, dataScadenza: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              )}

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Destinatari</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Per Mansioni</label>
                    <select
                      multiple
                      value={newCommunication.destinatari.mansioni}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setNewCommunication({
                          ...newCommunication,
                          destinatari: {...newCommunication.destinatari, mansioni: selected}
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      size={3}
                    >
                      {mansioniTemplate.map((m, idx) => (
                        <option key={idx} value={m}>{m}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Tieni premuto Ctrl/Cmd per selezionare più opzioni</p>
                    {newCommunication.destinatari.mansioni.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {newCommunication.destinatari.mansioni.map((m, idx) => (
                          <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full flex items-center gap-2">
                            {m}
                            <button
                              onClick={() => {
                                setNewCommunication({
                                  ...newCommunication,
                                  destinatari: {
                                    ...newCommunication.destinatari,
                                    mansioni: newCommunication.destinatari.mansioni.filter(mansione => mansione !== m)
                                  }
                                });
                              }}
                              className="hover:bg-blue-200 rounded-full p-0.5"
                              type="button"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Per Ruoli Lavoratori</label>
                    <select
                      multiple
                      value={newCommunication.destinatari.ruoli}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setNewCommunication({
                          ...newCommunication,
                          destinatari: {...newCommunication.destinatari, ruoli: selected}
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      size={3}
                    >
                      {ruoliTemplate.map((r, idx) => (
                        <option key={idx} value={r}>{r}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Ruoli assegnati ai dipendenti (es. Preposto, RLS)</p>
                    {newCommunication.destinatari.ruoli.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {newCommunication.destinatari.ruoli.map((r, idx) => (
                          <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full flex items-center gap-2">
                            {r}
                            <button
                              onClick={() => {
                                setNewCommunication({
                                  ...newCommunication,
                                  destinatari: {
                                    ...newCommunication.destinatari,
                                    ruoli: newCommunication.destinatari.ruoli.filter(ruolo => ruolo !== r)
                                  }
                                });
                              }}
                              className="hover:bg-purple-200 rounded-full p-0.5"
                              type="button"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 🆕 RUOLI ORGANIZZATIVI (Figure Aziendali) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Per Figure Aziendali</label>
                    <select
                      multiple
                      value={newCommunication.destinatari.ruoliOrganizzativi}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setNewCommunication({
                          ...newCommunication,
                          destinatari: {...newCommunication.destinatari, ruoliOrganizzativi: selected}
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      size={3}
                    >
                      {ruoliOrganizzativiTemplate.map((r, idx) => (
                        <option key={idx} value={r}>{r}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Figure nell'anagrafica aziendale (Datore Lavoro, RSPP, Medico)</p>
                    {newCommunication.destinatari.ruoliOrganizzativi.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {newCommunication.destinatari.ruoliOrganizzativi.map((r, idx) => (
                          <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-full flex items-center gap-2">
                            {r}
                            <button
                              onClick={() => {
                                setNewCommunication({
                                  ...newCommunication,
                                  destinatari: {
                                    ...newCommunication.destinatari,
                                    ruoliOrganizzativi: newCommunication.destinatari.ruoliOrganizzativi.filter(ruolo => ruolo !== r)
                                  }
                                });
                              }}
                              className="hover:bg-orange-200 rounded-full p-0.5"
                              type="button"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg cursor-pointer border border-blue-200">
                      <input
                        type="checkbox"
                        checked={newCommunication.destinatari.tuttiLavoratori}
                        onChange={(e) => setNewCommunication({
                          ...newCommunication,
                          destinatari: {...newCommunication.destinatari, tuttiLavoratori: e.target.checked}
                        })}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="font-medium text-gray-900">Invia a tutti i lavoratori</span>
                    </label>
                  </div>

                  {/* 🆕 PERSONE SPECIFICHE */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Seleziona Persone Specifiche</label>
                    <select
                      multiple
                      value={newCommunication.destinatari.personeSpecifiche.map(id => String(id))}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => { const v = option.value; return isNaN(v) ? v : Number(v); });
                        setNewCommunication({
                          ...newCommunication,
                          destinatari: {...newCommunication.destinatari, personeSpecifiche: selected}
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      size={4}
                    >
                      {(() => {
                        // Filtra lavoratori per azienda selezionata
                        const commCompanyId = newCommunication.aziendaId || 
                          (currentUser.role === 'AZIENDA' ? currentUser.companyId : null);
                        
                        if (!commCompanyId) return [];
                        
                        const companyWorkers = workers.filter(w => String(w.companyId) === String(commCompanyId));
                        
                        return companyWorkers.map(worker => (
                          <option key={worker.id} value={worker.id}>
                            {worker.nome} {worker.cognome} - {worker.mansione}
                          </option>
                        ));
                      })()}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Tieni premuto Ctrl/Cmd per selezionare più persone</p>
                    {newCommunication.destinatari.personeSpecifiche.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {newCommunication.destinatari.personeSpecifiche.map((workerId, idx) => {
                          const worker = workers.find(w => w.id === workerId);
                          if (!worker) return null;
                          return (
                            <span key={idx} className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full flex items-center gap-2">
                              {worker.nome} {worker.cognome}
                              <button
                                onClick={() => {
                                  setNewCommunication({
                                    ...newCommunication,
                                    destinatari: {
                                      ...newCommunication.destinatari,
                                      personeSpecifiche: newCommunication.destinatari.personeSpecifiche.filter(id => id !== workerId)
                                    }
                                  });
                                }}
                                className="hover:bg-green-200 rounded-full p-0.5"
                                type="button"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Allegati</label>
                <input 
                  type="file" 
                  id="file-upload-comm"
                  className="hidden" 
                  accept=".pdf,.doc,.docx"
                  multiple
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      const fileNames = Array.from(e.target.files).map(f => f.name);
                      setNewCommunication({
                        ...newCommunication,
                        allegati: [...(newCommunication.allegati || []), ...fileNames]
                      });
                      alert(`${fileNames.length} file caricato/i con successo!`);
                      e.target.value = ''; // Reset input per permettere ricaricamento stesso file
                    }
                  }}
                />
                <label 
                  htmlFor="file-upload-comm"
                  className="block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors cursor-pointer"
                >
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Clicca per caricare file o trascina qui</p>
                  <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX fino a 10MB</p>
                </label>
                
                {newCommunication.allegati && newCommunication.allegati.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm font-medium text-gray-700">File allegati:</p>
                    {newCommunication.allegati.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2">
                          <FileCheck className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-gray-900">{file}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setNewCommunication({
                              ...newCommunication,
                              allegati: newCommunication.allegati.filter((_, i) => i !== idx)
                            });
                          }}
                          className="p-1 hover:bg-red-50 rounded text-red-600 transition-colors"
                          title="Rimuovi allegato"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl sticky bottom-0">
              <button 
                onClick={closeModal}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors"
              >
                Annulla
              </button>
              <button 
                onClick={handleSaveCommunication}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                Salva Comunicazione
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (modalType === 'newMansione') {
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-blue-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
              <h2 className="text-xl font-bold">Nuova Mansione</h2>
              <button onClick={closeModal} className="p-2 hover:bg-blue-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome Mansione *</label>
                <input
                  type="text"
                  value={newMansione}
                  onChange={(e) => setNewMansione(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es. Operaio Produzione"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddMansione()}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button 
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
                <button 
                  onClick={handleAddMansione}
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                >
                  Salva
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (modalType === 'newRuolo') {
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-purple-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
              <h2 className="text-xl font-bold">Nuovo Ruolo</h2>
              <button onClick={closeModal} className="p-2 hover:bg-purple-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome Ruolo *</label>
                <input
                  type="text"
                  value={newRuolo}
                  onChange={(e) => setNewRuolo(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Es. Addetto Antincendio"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddRuolo()}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button 
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
                <button 
                  onClick={handleAddRuolo}
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
                >
                  Salva
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 🆕 MODAL NUOVO DOCUMENTO
    if (modalType === 'newDocument' || modalType === 'editDocument') {
      const isEdit = modalType === 'editDocument';
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* HEADER FISSO */}
            <div className="bg-blue-600 text-white p-6 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold">{isEdit ? 'Modifica' : 'Carica'} Documento Aziendale</h2>
              <button onClick={closeModal} className="p-2 hover:bg-blue-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* CONTENUTO SCROLLABILE */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo Documento *</label>
                <select
                  value={newDocument.tipo}
                  onChange={(e) => setNewDocument({...newDocument, tipo: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {documentiTemplate.map((template, idx) => (
                    <option key={idx} value={template.split(' - ')[0]}>
                      {template}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrizione *</label>
                <input
                  type="text"
                  value={newDocument.descrizione}
                  onChange={(e) => setNewDocument({...newDocument, descrizione: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Es. Documento Valutazione Rischi 2025"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Revisione *</label>
                  <input
                    type="date"
                    value={newDocument.dataRevisione}
                    onChange={(e) => setNewDocument({...newDocument, dataRevisione: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Scadenza</label>
                  <input
                    type="date"
                    value={newDocument.dataScadenza}
                    onChange={(e) => setNewDocument({...newDocument, dataScadenza: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Allega File</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors">
                  <label className="flex flex-col items-center justify-center p-6 cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click per selezionare file</p>
                    <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX - max 50MB</p>
                    <input
                      type="file"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          setDocumentFileObject(file);
                          setNewDocument({...newDocument, file: file.name});
                        }
                      }}
                      className="hidden"
                      accept=".pdf,.doc,.docx"
                    />
                    {newDocument.file && (
                      <p className="text-xs text-green-600 mt-2 font-medium">
                        📎 {newDocument.file}
                      </p>
                    )}
                  </label>
                </div>
              </div>

              {/* 🆕 STATO E CONDIVISIONE */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Condivisione Documento</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stato *</label>
                  <select
                    value={newDocument.stato}
                    onChange={(e) => setNewDocument({...newDocument, stato: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Privato">Privato (solo visibile a te)</option>
                    <option value="Condiviso">Condiviso con lavoratori</option>
                  </select>
                </div>

                {newDocument.stato === 'Condiviso' && (
                  <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Condividi per Mansioni</label>
                      <select
                        multiple
                        value={newDocument.destinatari.mansioni}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setNewDocument({
                            ...newDocument,
                            destinatari: {...newDocument.destinatari, mansioni: selected}
                          });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        size={3}
                      >
                        {mansioniTemplate.map((m, idx) => (
                          <option key={idx} value={m}>{m}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Tieni premuto Ctrl/Cmd per selezionare più opzioni</p>
                      {newDocument.destinatari.mansioni.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {newDocument.destinatari.mansioni.map((m, idx) => (
                            <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full flex items-center gap-2">
                              {m}
                              <button
                                onClick={() => {
                                  setNewDocument({
                                    ...newDocument,
                                    destinatari: {
                                      ...newDocument.destinatari,
                                      mansioni: newDocument.destinatari.mansioni.filter(mansione => mansione !== m)
                                    }
                                  });
                                }}
                                className="hover:bg-blue-200 rounded-full p-0.5"
                                type="button"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Condividi per Ruoli Lavoratori</label>
                      <select
                        multiple
                        value={newDocument.destinatari.ruoli}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setNewDocument({
                            ...newDocument,
                            destinatari: {...newDocument.destinatari, ruoli: selected}
                          });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        size={3}
                      >
                        {ruoliTemplate.map((r, idx) => (
                          <option key={idx} value={r}>{r}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Ruoli assegnati ai dipendenti (es. Preposto, RLS)</p>
                      {newDocument.destinatari.ruoli.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {newDocument.destinatari.ruoli.map((r, idx) => (
                            <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full flex items-center gap-2">
                              {r}
                              <button
                                onClick={() => {
                                  setNewDocument({
                                    ...newDocument,
                                    destinatari: {
                                      ...newDocument.destinatari,
                                      ruoli: newDocument.destinatari.ruoli.filter(ruolo => ruolo !== r)
                                    }
                                  });
                                }}
                                className="hover:bg-purple-200 rounded-full p-0.5"
                                type="button"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 🆕 RUOLI ORGANIZZATIVI (Figure Aziendali) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Condividi per Figure Aziendali</label>
                      <select
                        multiple
                        value={newDocument.destinatari.ruoliOrganizzativi}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setNewDocument({
                            ...newDocument,
                            destinatari: {...newDocument.destinatari, ruoliOrganizzativi: selected}
                          });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        size={3}
                      >
                        {ruoliOrganizzativiTemplate.map((r, idx) => (
                          <option key={idx} value={r}>{r}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Figure nell'anagrafica aziendale (Datore Lavoro, RSPP, Medico)</p>
                      {newDocument.destinatari.ruoliOrganizzativi.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {newDocument.destinatari.ruoliOrganizzativi.map((r, idx) => (
                            <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-full flex items-center gap-2">
                              {r}
                              <button
                                onClick={() => {
                                  setNewDocument({
                                    ...newDocument,
                                    destinatari: {
                                      ...newDocument.destinatari,
                                      ruoliOrganizzativi: newDocument.destinatari.ruoliOrganizzativi.filter(ruolo => ruolo !== r)
                                    }
                                  });
                                }}
                                className="hover:bg-orange-200 rounded-full p-0.5"
                                type="button"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="flex items-center gap-2 p-3 bg-white rounded-lg cursor-pointer border border-gray-300">
                        <input
                          type="checkbox"
                          checked={newDocument.destinatari.tuttiLavoratori}
                          onChange={(e) => setNewDocument({
                            ...newDocument,
                            destinatari: {...newDocument.destinatari, tuttiLavoratori: e.target.checked}
                          })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="font-medium text-gray-900">Condividi con tutti i lavoratori</span>
                      </label>
                    </div>

                    {/* 🆕 PERSONE SPECIFICHE */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Seleziona Persone Specifiche</label>
                      <select
                        multiple
                        value={newDocument.destinatari.personeSpecifiche.map(id => String(id))}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => { const v = option.value; return isNaN(v) ? v : Number(v); });
                          setNewDocument({
                            ...newDocument,
                            destinatari: {...newDocument.destinatari, personeSpecifiche: selected}
                          });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        size={4}
                      >
                        {(() => {
                          // Filtra lavoratori per azienda corretta
                          const docCompanyId = currentUser.role === 'AZIENDA' 
                            ? currentUser.companyId 
                            : (editingDocument ? editingDocument.companyId : (selectedCompanyForDocuments || selectedCompany));
                          
                          const companyWorkers = workers.filter(w => w.companyId === docCompanyId);
                          
                          return companyWorkers.map(worker => (
                            <option key={worker.id} value={worker.id}>
                              {worker.nome} {worker.cognome} - {worker.mansione}
                            </option>
                          ));
                        })()}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Tieni premuto Ctrl/Cmd per selezionare più persone</p>
                      {newDocument.destinatari.personeSpecifiche.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {newDocument.destinatari.personeSpecifiche.map((workerId, idx) => {
                            const worker = workers.find(w => w.id === workerId);
                            if (!worker) return null;
                            return (
                              <span key={idx} className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full flex items-center gap-2">
                                {worker.nome} {worker.cognome}
                                <button
                                  onClick={() => {
                                    setNewDocument({
                                      ...newDocument,
                                      destinatari: {
                                        ...newDocument.destinatari,
                                        personeSpecifiche: newDocument.destinatari.personeSpecifiche.filter(id => id !== workerId)
                                      }
                                    });
                                  }}
                                  className="hover:bg-green-200 rounded-full p-0.5"
                                  type="button"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER FISSO CON PULSANTI */}
            <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button 
                onClick={closeModal}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100"
              >
                Annulla
              </button>
              <button 
                onClick={handleSaveDocument}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                Salva
              </button>
            </div>
          </div>
        </div>
      );
    }

    // 🆕 MODAL NUOVO TEMPLATE DOCUMENTO
    if (modalType === 'newDocumento') {
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-green-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
              <h2 className="text-xl font-bold">Nuovo Template Documento</h2>
              <button onClick={closeModal} className="p-2 hover:bg-green-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome Template *</label>
                <input
                  type="text"
                  value={newDocumento}
                  onChange={(e) => setNewDocumento(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Es. PSC - Piano di Sicurezza e Coordinamento"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddDocumento()}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button 
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button 
                  onClick={handleAddDocumento}
                  className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium"
                >
                  Salva
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  // 🔐 SUPABASE AUTH - Login/Logout
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword
      });
      
      if (error) {
        if (error.message.includes('Invalid login')) {
          setLoginError('Email o password non validi');
        } else {
          setLoginError('Errore di accesso: ' + error.message);
        }
        setLoginLoading(false);
        return;
      }
      
      // Login riuscito - carica profilo utente dalla tabella users
      const { data: userProfile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('auth_id', data.user.id)
        .single();
      
      if (profileError || !userProfile) {
        setLoginError('Profilo utente non trovato. Contatta l\'amministratore.');
        await supabase.auth.signOut();
        setLoginLoading(false);
        return;
      }
      
      if (!userProfile.attivo) {
        setLoginError('Account disattivato. Contatta l\'amministratore.');
        await supabase.auth.signOut();
        setLoginLoading(false);
        return;
      }
      
      // Imposta currentUser in base al ruolo
      setCurrentUser({
        role: userProfile.ruolo,
        companyId: userProfile.company_id || null,
        workerId: userProfile.worker_id || null,
        nome: userProfile.nome,
        cognome: userProfile.cognome,
        email: userProfile.email
      });
      
      setAuthUser(data.user);
      setIsRealAdmin(userProfile.ruolo === 'ADMIN');
      setLoginLoading(false);
      
    } catch (err) {
      setLoginError('Errore di connessione. Riprova.');
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setAuthUser(null);
    setIsRealAdmin(false);
    setCurrentUser({
      role: 'ADMIN',
      companyId: null,
      workerId: null,
      nome: '',
      cognome: '',
      email: ''
    });
    setLoginEmail('');
    setLoginPassword('');
    setLoginError('');
    setActiveSection('dashboard');
  };

  // 🔐 CHECK SESSIONE ALL'AVVIO - Controlla se l'utente è già loggato
  React.useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // Sessione attiva - carica profilo
          const { data: userProfile } = await supabase
            .from('users')
            .select('*')
            .eq('auth_id', session.user.id)
            .single();
          
          if (userProfile && userProfile.attivo) {
            setCurrentUser({
              role: userProfile.ruolo,
              companyId: userProfile.company_id || null,
              workerId: userProfile.worker_id || null,
              nome: userProfile.nome,
              cognome: userProfile.cognome,
              email: userProfile.email
            });
            setAuthUser(session.user);
            setIsRealAdmin(userProfile.ruolo === 'ADMIN');
          } else {
            // Profilo non trovato o disattivato
            await supabase.auth.signOut();
          }
        }
      } catch (err) {
        console.error('Errore check sessione:', err);
      }
      setAuthLoading(false);
    };
    
    checkSession();
  }, []);

  // 🔐 SCHERMATA LOADING INIZIALE (check sessione)
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">SafeTracker</h1>
          <p className="text-gray-500 mt-2">Verifica sessione in corso...</p>
        </div>
      </div>
    );
  }

  // 🔐 SCHERMATA LOGIN (se non autenticato)
  if (!authUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">SafeTracker</h1>
            <p className="text-gray-500 mt-1">Accedi al tuo account</p>
          </div>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => { setLoginEmail(e.target.value); setLoginError(''); }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Inserisci la tua email"
                autoFocus
                disabled={loginLoading}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => { setLoginPassword(e.target.value); setLoginError(''); }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Inserisci la password"
                disabled={loginLoading}
              />
            </div>
            {loginError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {loginError}
              </div>
            )}
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {loginLoading ? 'Accesso in corso...' : 'Accedi'}
            </button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-6">SafeTracker v1.0.0<br/>Progetto realizzato da MDM Consulting Engineering S.r.l.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className={`${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300 p-4 md:p-8`}>
        <div className="max-w-7xl mx-auto">
          {renderContent()}
        </div>
      </div>

      {renderModal()}
      
      {/* 🔐 MODAL CONFERMA ELIMINAZIONE AZIENDA */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertCircle className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Conferma Eliminazione</h3>
              <p className="text-sm text-gray-500 mt-2">
                Stai per eliminare <strong>{companies.find(c => c.id === pendingDeleteCompanyId)?.ragioneSociale || 'questa azienda'}</strong> e tutti i dati associati (lavoratori, comunicazioni, documenti).
              </p>
              <p className="text-sm text-red-600 font-semibold mt-2">Questa operazione è irreversibile.</p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Inserisci la tua password per confermare
              </label>
              <input
                type="password"
                value={deleteConfirmPassword}
                onChange={(e) => { setDeleteConfirmPassword(e.target.value); setDeleteConfirmError(''); }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                placeholder="La tua password di accesso"
                autoFocus
                disabled={deleteConfirmLoading}
                onKeyDown={(e) => { if (e.key === 'Enter') confirmDeleteCompany(); }}
              />
            </div>
            
            {deleteConfirmError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {deleteConfirmError}
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteConfirmModal(false); setDeleteConfirmPassword(''); setPendingDeleteCompanyId(null); }}
                disabled={deleteConfirmLoading}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={confirmDeleteCompany}
                disabled={deleteConfirmLoading}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
              >
                {deleteConfirmLoading ? 'Verifica...' : 'Elimina Azienda'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 MODAL STORICO EVENTI */}
      <StoricoEventiModal 
        show={showStoricoEventoModal}
        onClose={() => setShowStoricoEventoModal(false)}
        worker={selectedWorkerForStorico}
        currentUser={currentUser}
        workers={workers}
        setWorkers={setWorkers}
        onUpdateWorker={setSelectedWorkerForStorico}
        calcolaGiorniTotaliLavorati={calcolaGiorniTotaliLavorati}
        contaRiassunzioni={contaRiassunzioni}
        isLavoratoreAttivo={isLavoratoreAttivo}
        formatDateOnly={formatDateOnly}
        formatDate={formatDate}
        formatTipoEvento={formatTipoEvento}
      />
      
      {/* 🆕 MODAL CONFERMA DOCUMENTO */}
      <DocumentConfirmModal />
      
      {/* 🆕 MODAL EMAIL */}
      <EmailConfirmModal />
      
      {/* 🆕 MODAL IMPORT EXCEL LAVORATORI */}
      <ImportExcelModal />
      <ImportPreviewModal />
      
      {/* 🆕 MODAL CARICA REVISIONE */}
      {showRevisionModal && revisionDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowRevisionModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-amber-600 text-white p-6 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-2xl font-bold">📝 Carica Nuova Revisione</h2>
                <p className="text-sm text-amber-100 mt-1">Aggiorna documento con nuova versione</p>
              </div>
              <button onClick={() => setShowRevisionModal(false)} className="p-2 hover:bg-amber-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Informazioni versione corrente */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <h3 className="font-bold text-blue-900 mb-2">📄 Stai aggiornando:</h3>
                <p className="text-sm text-blue-800"><strong>{revisionDocument.descrizione}</strong></p>
                <div className="mt-2 flex items-center gap-4 text-xs text-blue-700">
                  <span>📌 Versione: v{revisionDocument.version || 1}</span>
                  <span>📅 Revisione: {formatDateOnly(revisionDocument.dataRevisione)}</span>
                  {revisionDocument.dataScadenza && (
                    <span className="text-orange-700">⏰ Scadenza: {formatDateOnly(revisionDocument.dataScadenza)}</span>
                  )}
                </div>
              </div>

              {/* Form nuova revisione */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrizione Nuova Revisione *</label>
                <input
                  type="text"
                  value={newRevision.descrizione}
                  onChange={(e) => setNewRevision({...newRevision, descrizione: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder={`Es. ${revisionDocument.tipo} ${new Date().getFullYear() + 1}`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Nuova Revisione *</label>
                  <input
                    type="date"
                    value={newRevision.dataRevisione}
                    onChange={(e) => setNewRevision({...newRevision, dataRevisione: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nuova Scadenza</label>
                  <input
                    type="date"
                    value={newRevision.dataScadenza}
                    onChange={(e) => setNewRevision({...newRevision, dataScadenza: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Carica Nuovo File</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg hover:border-amber-500 transition-colors">
                  <label className="flex flex-col items-center justify-center p-6 cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Clicca per selezionare file</p>
                    <p className="text-xs text-gray-500 mt-1">PDF, max 10MB</p>
                    <input
                      type="file"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          setRevisionFileObject(file);
                          setNewRevision({...newRevision, file: file.name});
                        }
                      }}
                      className="hidden"
                      accept=".pdf"
                    />
                    {newRevision.file && (
                      <p className="text-xs text-green-600 mt-2 font-medium">📎 {newRevision.file}</p>
                    )}
                  </label>
                </div>
              </div>

              {/* Ricondivisione */}
              {revisionDocument.stato === 'Condiviso' && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newRevision.autoReshare}
                      onChange={(e) => setNewRevision({...newRevision, autoReshare: e.target.checked})}
                      className="w-5 h-5 text-green-600 rounded mt-0.5"
                    />
                    <div>
                      <p className="font-semibold text-green-900">🔄 Ricondividi automaticamente</p>
                      <p className="text-sm text-green-700 mt-1">
                        La nuova versione verrà condivisa con gli stessi destinatari della v{revisionDocument.version || 1}
                      </p>
                      {revisionDocument.recipients && revisionDocument.recipients.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">
                          📧 {revisionDocument.recipients.length} destinatari riceveranno email di notifica
                        </p>
                      )}
                    </div>
                  </label>
                  
                  {/* Info quando deselezionato */}
                  {!newRevision.autoReshare && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-900 flex items-start gap-2">
                        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>Nuovi destinatari:</strong> Dopo aver caricato la revisione, si aprirà automaticamente 
                          il pannello di condivisione per selezionare i nuovi destinatari.
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700">
                <p className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span>La versione v{revisionDocument.version || 1} verrà archiviata e non sarà più visibile nella tabella principale. Sarà consultabile dallo storico revisioni.</span>
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button 
                onClick={() => setShowRevisionModal(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
              >
                Annulla
              </button>
              <button 
                onClick={handleUploadRevision}
                className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700 transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Carica Revisione v{(revisionDocument.version || 1) + 1}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📚 MODAL STORICO REVISIONI */}
      {selectedDocumentForHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedDocumentForHistory(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-amber-600 text-white p-6 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <History className="w-8 h-8" />
                <div>
                  <h2 className="text-2xl font-bold">Storico Revisioni</h2>
                  <p className="text-sm text-amber-100 mt-1">{selectedDocumentForHistory.descrizione}</p>
                </div>
              </div>
              <button onClick={() => setSelectedDocumentForHistory(null)} className="p-2 hover:bg-amber-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Versione corrente */}
              {!selectedDocumentForHistory.isArchived && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                        <FileCheck className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-gray-900">Versione {selectedDocumentForHistory.version || 1}</h3>
                          <span className="px-2 py-1 bg-green-600 text-white text-xs font-bold rounded-full">CORRENTE</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{selectedDocumentForHistory.descrizione}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Data Caricamento</p>
                      <p className="font-semibold text-gray-900">{formatDateOnly(selectedDocumentForHistory.dataCaricamento)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Data Revisione</p>
                      <p className="font-semibold text-gray-900">{formatDateOnly(selectedDocumentForHistory.dataRevisione)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Scadenza</p>
                      <p className="font-semibold text-gray-900">
                        {selectedDocumentForHistory.dataScadenza ? formatDateOnly(selectedDocumentForHistory.dataScadenza) : '-'}
                      </p>
                    </div>
                  </div>

                  {selectedDocumentForHistory.stato === 'Condiviso' && selectedDocumentForHistory.recipients && (
                    <div className="bg-white/80 rounded-lg p-4 border border-green-200">
                      <h4 className="text-sm font-bold text-gray-900 mb-2">Conferme di Lettura</h4>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">
                            {selectedDocumentForHistory.recipients.filter(r => r.confermato).length}/{selectedDocumentForHistory.recipients.length}
                          </p>
                          <p className="text-xs text-gray-600">confermate</p>
                        </div>
                        <div className="flex-1">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full transition-all"
                              style={{ 
                                width: `${selectedDocumentForHistory.recipients.length > 0 
                                  ? (selectedDocumentForHistory.recipients.filter(r => r.confermato).length / selectedDocumentForHistory.recipients.length * 100) 
                                  : 0}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-4">
                    <button 
                      onClick={() => handleDownloadDocument(selectedDocumentForHistory)}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Scarica
                    </button>
                    {selectedDocumentForHistory.stato === 'Condiviso' && (
                      <button 
                        onClick={() => {
                          handleExportDocumentReport(selectedDocumentForHistory);
                          setSelectedDocumentForHistory(null);
                        }}
                        className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                      >
                        <FileText className="w-4 h-4" />
                        Report
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Versioni precedenti */}
              {(() => {
                const previousVersions = [];
                let currentDocId = selectedDocumentForHistory.replacedDocumentId;
                
                while (currentDocId) {
                  const prevDoc = documents.find(d => d.id === currentDocId);
                  if (prevDoc) {
                    previousVersions.push(prevDoc);
                    currentDocId = prevDoc.replacedDocumentId;
                  } else {
                    break;
                  }
                }

                if (previousVersions.length === 0) {
                  return (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600">Nessuna revisione precedente</p>
                    </div>
                  );
                }

                return (
                  <>
                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Revisioni Precedenti ({previousVersions.length})
                      </h3>
                    </div>

                    <div className="space-y-3">
                      {previousVersions.map((prevDoc, idx) => {
                        const isExpired = prevDoc.dataScadenza && new Date(prevDoc.dataScadenza) < new Date();
                        
                        return (
                          <div key={prevDoc.id} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-400 rounded-lg flex items-center justify-center">
                                  <FileCheck className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-bold text-gray-900">Versione {prevDoc.version || 1}</h4>
                                    <span className="px-2 py-1 bg-gray-400 text-white text-xs font-bold rounded-full">
                                      ARCHIVIATA
                                    </span>
                                    {isExpired && (
                                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                                        SCADUTA
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">{prevDoc.descrizione}</p>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Data Revisione</p>
                                <p className="text-sm font-semibold text-gray-900">{formatDateOnly(prevDoc.dataRevisione)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Scadenza</p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {prevDoc.dataScadenza ? formatDateOnly(prevDoc.dataScadenza) : '-'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Archiviata il</p>
                                <p className="text-sm font-semibold text-gray-900">{formatDateOnly(prevDoc.dataCaricamento)}</p>
                              </div>
                            </div>

                            {prevDoc.stato === 'Condiviso' && prevDoc.recipients && (
                              <div className="bg-white rounded-lg p-3 border border-gray-200 mb-3">
                                <h5 className="text-xs font-bold text-gray-700 mb-2">Conferme di Lettura</h5>
                                <div className="flex items-center gap-3">
                                  <div className="text-center">
                                    <p className="text-lg font-bold text-gray-900">
                                      {prevDoc.recipients.filter(r => r.confermato).length}/{prevDoc.recipients.length}
                                    </p>
                                    <p className="text-xs text-gray-600">confermate</p>
                                  </div>
                                  <div className="flex-1">
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="bg-gray-600 h-2 rounded-full transition-all"
                                        style={{ 
                                          width: `${prevDoc.recipients.length > 0 
                                            ? (prevDoc.recipients.filter(r => r.confermato).length / prevDoc.recipients.length * 100) 
                                            : 0}%` 
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="flex gap-2">
                              <button 
                                onClick={() => handleDownloadDocument(prevDoc)}
                                className="flex-1 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                              >
                                <Download className="w-4 h-4" />
                                Scarica
                              </button>
                              {prevDoc.stato === 'Condiviso' && (
                                <button 
                                  onClick={() => {
                                    handleExportDocumentReport(prevDoc);
                                  }}
                                  className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                                >
                                  <FileText className="w-4 h-4" />
                                  Report
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button 
                onClick={() => setSelectedDocumentForHistory(null)}
                className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-xl font-bold hover:bg-gray-700 transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SafeTrackerAdmin;